import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import React from 'react';
import { createPortal } from 'react-dom';
import { isWeekend, getNextBusinessDay } from "../utils/dateValidation";
import {
  FiEdit2,
  FiAlertCircle,
  FiRefreshCw,
  FiEye,
  FiEyeOff,
  FiPlus,
  FiSearch,
  FiUserPlus,
  FiCalendar,
  FiCheckCircle,
  FiCheckSquare,
  FiClock,
  FiLock,
  FiUnlock,
  FiDownload,
  FiX,
  FiLogOut,
  FiUsers,
  FiBookOpen,
  FiUser,
  FiTrendingUp,
  FiFolder,
  FiFileText,
  FiPaperclip,
  FiShield,
  FiMenu,
  FiBell
} from "react-icons/fi";
import { MdOutlineArchive } from "react-icons/md";
import { useLanguage } from "../contexts/LanguageContext";

import { pdfjs } from 'react-pdf';
import pdfjsWorker from 'pdfjs-dist/build/pdf.worker.min.js';
import { createPDF, createAppointmentSlipPDF } from '../utils/pdfExport';
import jsPDF from 'jspdf';
import * as XLSX from 'xlsx';
import { 
  signOut as signOutSecondary, 
  EmailAuthProvider, 
  reauthenticateWithCredential 
} from "firebase/auth";
import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  limit,
  onSnapshot,
  query,
  serverTimestamp,
  where,
  setDoc,
  updateDoc,
  orderBy
} from "firebase/firestore";
import { db, auth, functionsService } from "../firebase";
import { httpsCallable } from "firebase/functions";
import { sessionService } from "../services/sessionService";
import { sendOtpEmail } from "../services/otpService";
import { generateStaffId, notificationService } from "../services/firestoreService";
import { appointmentService } from "../services/appointmentService";
import { generateFollowUpDates } from "../utils/followUpSchedule";
import { ADMIN_ACCOUNT_ID } from "../services/adminService";
import {
  generateDynamicSlots,
  getServiceRule,
  formatDateToPH,
  getPHNow,
  getSlotValidation,
  SERVICE_FILTER_OPTIONS,
  matchesServiceFilter,
  getServiceFilterKey
} from "../utils/appointmentUtils";
import {
  getAppointmentDateTimeValue,
  getAppointmentSortIndicator,
  getAppointmentSortTitle,
  getAppointmentTextValue,
  getNextAppointmentSortConfig,
  sortAppointments
} from "../utils/appointmentSorting";
import "./AdminPage.css";
import "./staffpages/staffcss/medicalprofessional.css";
import "./staffpages/staffcss/logoutmodal.css";
import "./staffpages/staffcss/contentlibrary.css";
import "../components/otpmodals/otpmodalscss/TwoFactorOtpModal.css";
import AdminDashboard from "./staffpages/AdminDashboard";
import Portal from "../components/Portal";
import AdminMedicalEscalatedCases from "./staffpages/AdminMedicalEscalatedCases";
import AdminPeerResources from "./staffpages/AdminPeerResources";
import AdminProfile from "./staffpages/AdminProfile";
import AdminContent from "./staffpages/AdminContent";
import AdminRecords from "./staffpages/AdminRecords";
import ProNotifications from "./staffpages/ProNotifications";
import CancelAppointmentModal from "../components/usercomponents/CancelAppointmentModal";
import logoName from "../images/auravit_logo.png";
import auravitIcon from "../images/auraviticon.png";
import cfsiLogo from "../images/CFSI-mobile-logo-sr.png";

// Configure PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = pdfjsWorker;

// Helper: returns calendar cells for a given month (null for empty leading cells)
const getCalendarDays = (date) => {
  const year = date.getFullYear();
  const month = date.getMonth();
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const startOffset = firstDay.getDay() === 0 ? 6 : firstDay.getDay() - 1; // Monday-first
  const totalDays = lastDay.getDate();
  const cells = [];
  for (let i = 0; i < startOffset; i++) cells.push(null);
  for (let day = 1; day <= totalDays; day++) cells.push(day);
  return cells;
};

const getAttachmentExpiry = (appointment) => {
  if (!appointment) return null;

  if (appointment.editDeadline) {
    const deadline = new Date(appointment.editDeadline);
    if (!Number.isNaN(deadline.getTime())) return deadline;
  }

  const completedAt = appointment.completedAt?.toDate
    ? appointment.completedAt.toDate()
    : appointment.completedAt
      ? new Date(appointment.completedAt)
      : null;
  if (!completedAt || Number.isNaN(completedAt.getTime())) return null;

  // Legacy completed appointments use the same seven-day fallback as the edit guard.
  return new Date(completedAt.getTime() + (7 * 24 * 60 * 60 * 1000));
};

const formatPhilippineContactNumber = (value) => {
  const rawNumber = String(value || "").trim();
  if (!rawNumber) return "Not Provided";

  const digits = rawNumber.replace(/\D/g, "");
  if (!digits) return "Not Provided";
  if (digits.startsWith("0063")) return `+63${digits.slice(4)}`;
  if (digits.startsWith("63")) return `+${digits}`;
  if (digits.startsWith("0") && digits.length === 11) return `+63${digits.slice(1)}`;
  if (digits.startsWith("9") && digits.length === 10) return `+63${digits}`;
  return rawNumber;
};

// Modal Components
function ConfirmPasswordModal({ isOpen, onClose, onConfirm, title }) {
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.trim()) {
      setIsLoading(true);
      setError("");

      try {
        await onConfirm(password);
        // Only clear password and loading if successful
        setPassword("");
        setIsLoading(false);
      } catch (err) {
        // Don't clear password on error, keep it for user to retry
        setError("Incorrect password");
        setIsLoading(false);
      }
    }
  };

  const handleClose = () => {
    setPassword("");
    setError("");
    setShowPassword(false);
    setIsLoading(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <Portal>
      <div className="modal-overlay">
      <div className="modal-content">
        <div className="modal-header">
          <h3 className="modal-title-white">{title}</h3>
          <button className="modal-close" onClick={handleClose}>
            <FiX />
          </button>
        </div>
        
        <p className="modal-subtitle">Enter admin password to create account</p>
        
        <form onSubmit={handleSubmit} className="modal-form">
          <div className="password-field">
            <FiLock className="password-icon" />
            <input
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                if (error) setError("");
              }}
              placeholder="Enter admin password"
              className={`password-input ${error ? "error" : ""}`}
              autoFocus
            />
            <button
              type="button"
              className="password-toggle"
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? <FiEye /> : <FiEyeOff />}
            </button>
          </div>
          
          {error && (
            <div className="password-error">
              <span className="error-icon">⚠</span>
              {error}
            </div>
          )}
          
          <div className="modal-actions">
            <button 
              type="button" 
              className="modal-btn modal-btn-cancel" 
              onClick={handleClose}
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className={`modal-btn modal-btn-confirm ${isLoading ? "loading" : ""}`}
              disabled={isLoading}
            >
              {isLoading ? "Confirming..." : "Confirm"}
            </button>
          </div>
        </form>
      </div>
    </div>
    </Portal>
  );
}

// Inline password form used inside the email-change password modal
// (lives outside AccountManagement so it can have its own local state)
function EmailPasswordConfirmForm({ onConfirm, onCancel }) {
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!password.trim()) return;
    setIsLoading(true);
    setError("");
    try {
      await onConfirm(password);
      setPassword("");
    } catch {
      setError("Incorrect password. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="modal-form" style={{ paddingTop: "8px" }}>
      <div className="password-field">
        <FiLock className="password-icon" />
        <input
          type={showPassword ? "text" : "password"}
          value={password}
          onChange={e => { setPassword(e.target.value); if (error) setError(""); }}
          placeholder="Enter admin password"
          className={`password-input ${error ? "error" : ""}`}
          autoFocus
        />
        <button
          type="button"
          className="password-toggle"
          onClick={() => setShowPassword(v => !v)}
        >
          {showPassword ? <FiEye /> : <FiEyeOff />}
        </button>
      </div>
      {error && (
        <div className="password-error">
          <span className="error-icon">⚠</span>
          {error}
        </div>
      )}
      <div className="modal-actions">
        <button
          type="button"
          className="modal-btn modal-btn-cancel"
          onClick={onCancel}
          disabled={isLoading}
        >
          Cancel
        </button>
        <button
          type="submit"
          className={`modal-btn modal-btn-confirm ${isLoading ? "loading" : ""}`}
          disabled={isLoading || !password.trim()}
        >
          {isLoading ? "Confirming..." : "Confirm"}
        </button>
      </div>
    </form>
  );
}

function NotificationModal({ message, type, onClose }) {
  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => {
        onClose();
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [message, onClose]);

  if (!message) return null;

  return (
    <Portal>
      <div
        className={`feedback-modal ${type}`}
      style={{
        position: "fixed",
        top: "20px",
        right: "20px",
        padding: "12px 20px",
        borderRadius: "8px",
        color: "#fff",
        backgroundColor: type === "success" ? "#36C667" : "#e74c3c",
        boxShadow: "0 4px 6px rgba(0,0,0,0.2)",
        zIndex: 10001,
        transition: "opacity 0.3s",
      }}
    >
      {message}
    </div>
    </Portal>
  );
}

function StaffCreatedSuccessModal({ isOpen, onViewAccounts }) {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <Portal>
      <div
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(15, 23, 42, 0.55)",
          backdropFilter: "blur(4px)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 9999,
        }}
      >
        <div
          style={{
            background: "#ffffff",
            borderRadius: "24px",
            padding: "48px 40px 40px",
            maxWidth: "420px",
            width: "92%",
            boxShadow: "0 25px 50px -12px rgba(0,0,0,0.25)",
            textAlign: "center",
            position: "relative",
          }}
        >
          {/* Success icon */}
          <div
            style={{
              width: "72px",
              height: "72px",
              borderRadius: "50%",
              background: "linear-gradient(135deg, #22c55e 0%, #16a34a 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 24px",
              boxShadow: "0 8px 24px rgba(34,197,94,0.35)",
            }}
          >
            <FiCheckCircle style={{ color: "#ffffff", fontSize: "34px" }} />
          </div>

          <h2
            style={{
              fontSize: "22px",
              fontWeight: 700,
              color: "#0f172a",
              margin: "0 0 10px 0",
              letterSpacing: "-0.3px",
            }}
          >
            Staff Account Created
          </h2>
          <p
            style={{
              fontSize: "14px",
              color: "#64748b",
              margin: "0 0 32px 0",
              lineHeight: 1.6,
            }}
          >
            The new staff account has been successfully created. You can view and manage it in the Accounts table.
          </p>

          <div style={{ display: "flex", justifyContent: "center" }}>
            <button
              onClick={onViewAccounts}
              style={{
                padding: "12px 28px",
                borderRadius: "14px",
                border: "none",
                background: "linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)",
                color: "#ffffff",
                fontSize: "14px",
                fontWeight: 700,
                cursor: "pointer",
                transition: "opacity 0.2s ease",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.opacity = "0.88")}
              onMouseLeave={(e) => (e.currentTarget.style.opacity = "1")}
            >
              <FiUsers style={{ fontSize: "15px" }} />
              View Accounts
            </button>
          </div>
        </div>
      </div>
    </Portal>
  );
}

// ---------------- Export Modal ----------------
function ExportModal({ isOpen, onClose, onExport, isExporting, activeTab }) {
  const [selectedFormat, setSelectedFormat] = useState(null);

  useEffect(() => {
    const scrollContainer = document.querySelector('.mp-workspace');
    if (!isOpen) return undefined;

    const previousBodyOverflow = document.body.style.overflow;
    const previousContainerOverflow = scrollContainer?.style.overflow ?? '';

    document.body.style.overflow = 'hidden';
    if (scrollContainer) {
      scrollContainer.style.overflow = 'hidden';
    }

    return () => {
      document.body.style.overflow = previousBodyOverflow;
      if (scrollContainer) {
        scrollContainer.style.overflow = previousContainerOverflow;
      }
    };
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      setSelectedFormat(null);
    }
  }, [isOpen]);

  const handleExport = (format) => {
    setSelectedFormat(format);
    onExport(format);
  };

  if (!isOpen) return null;

  return (
    <Portal>
      <div className="modal-overlay export-modal-overlay">
        <div className="modal-content export-modal-content">
          <div className="modal-header">
            <h3 className="modal-title-white">Export Report</h3>
            <button className="modal-close" onClick={onClose}>
              <FiX />
            </button>
          </div>

          <div className="export-modal-body">
            <p className="export-modal-subtitle">Select export format for {activeTab.charAt(0).toUpperCase() + activeTab.slice(1)} tab</p>

            <div className="export-format-list">
              <button
                className={`export-format-btn ${selectedFormat === 'pdf' ? 'selected' : ''}`}
                onClick={() => handleExport('pdf')}
                disabled={isExporting}
              >
                <div className="export-format-icon-wrap pdf">
                  <FiFileText className="export-format-icon" />
                </div>
                <div className="export-format-info">
                  <span className="export-format-title">PDF Report</span>
                  <span className="export-format-desc">Presentation-ready report</span>
                </div>
              </button>

              <button
                className={`export-format-btn ${selectedFormat === 'excel' ? 'selected' : ''}`}
                onClick={() => handleExport('excel')}
                disabled={isExporting}
              >
                <div className="export-format-icon-wrap excel">
                  <FiFileText className="export-format-icon" />
                </div>
                <div className="export-format-info">
                  <span className="export-format-title">Excel .xlsx</span>
                  <span className="export-format-desc">Structured analytics workbook</span>
                </div>
              </button>

              <button
                className={`export-format-btn ${selectedFormat === 'csv' ? 'selected' : ''}`}
                onClick={() => handleExport('csv')}
                disabled={isExporting}
              >
                <div className="export-format-icon-wrap csv">
                  <FiFileText className="export-format-icon" />
                </div>
                <div className="export-format-info">
                  <span className="export-format-title">CSV .csv</span>
                  <span className="export-format-desc">Raw structured data</span>
                </div>
              </button>
            </div>
          </div>
        </div>
      </div>
    </Portal>
  );
}

function AdminRescheduleModal({
  isOpen,
  appointment,
  onClose,
  onReschedule,
  isSubmitting,
  bookedSlots = [],
  timeSlots = { morning: [], afternoon: [] },
  selectedDate,
  setSelectedDate,
  selectedTime,
  setSelectedTime,
  reason,
  setReason,
  reasonOther,
  setReasonOther,
  reasonError
}) {
  if (!isOpen || !appointment) return null;
  return createPortal(
    <div className="ma-modal-backdrop">
      <div className="ma-modal-card walkin-modal" style={{ maxWidth: '850px', width: '95%', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
        <div className="modal-header" style={{ padding: '24px 32px', borderBottom: '1px solid #f3f4f6', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
          <h3 style={{ margin: 0, fontSize: '20px', fontWeight: 700, color: '#111827' }}>Reschedule Appointment</h3>
          <button className="modal-close" onClick={onClose}><FiX /></button>
        </div>

        <div className="modal-body-scroll" style={{ overflowY: 'auto', padding: '32px', flex: 1, backgroundColor: '#fff', minHeight: 0 }}>
          <p style={{ marginBottom: '24px', color: '#4b5563' }}>Select a new date and time for <strong>{appointment.patientName}</strong>'s appointment.</p>

          <div className="form-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '40px' }}>
            <div className="walkin-left-col" style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
              <div className="form-group">
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 600, color: '#4b5563' }}>Current Date</label>
                <input type="text" value={appointment.date} readOnly style={{ width: '100%', padding: '12px 16px', borderRadius: '8px', border: '1px solid #e5e7eb', backgroundColor: '#f9fafb', color: '#6b7280', fontSize: '15px', cursor: 'not-allowed' }} />
              </div>
              <div className="form-group">
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 600, color: '#4b5563' }}>Current Time</label>
                <input type="text" value={appointment.time} readOnly style={{ width: '100%', padding: '12px 16px', borderRadius: '8px', border: '1px solid #e5e7eb', backgroundColor: '#f9fafb', color: '#6b7280', fontSize: '15px', cursor: 'not-allowed' }} />
              </div>
              <div className="form-group">
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 600, color: '#4b5563' }}>Service</label>
                <input type="text" value={appointment.serviceName} readOnly style={{ width: '100%', padding: '12px 16px', borderRadius: '8px', border: '1px solid #e5e7eb', backgroundColor: '#f9fafb', color: '#6b7280', fontSize: '15px', cursor: 'not-allowed' }} />
                <div style={{ marginTop: '12px', display: 'flex', gap: '20px', alignItems: 'center' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{ width: '8px', height: '8px', backgroundColor: '#19458c', borderRadius: '50%' }} />
                    <span style={{ fontSize: '12px', color: '#6b7280' }}>Scheduled</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{ width: '8px', height: '8px', backgroundColor: '#fdebb2', borderRadius: '50%' }} />
                    <span style={{ fontSize: '12px', color: '#6b7280' }}>Walk-in</span>
                  </div>
                </div>
              </div>

              <div className="form-group" style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #e5e7eb' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 600, color: '#4b5563' }}>Reschedule Reason *</label>
                <select
                  value={reason}
                  onChange={(e) => {
                    setReason(e.target.value);
                  }}
                  style={{ width: '100%', padding: '12px 16px', borderRadius: '8px', border: '1px solid #e5e7eb', backgroundColor: '#fff', color: '#374151', fontSize: '15px', cursor: 'pointer' }}
                >
                  <option value="schedule_conflict">Schedule conflict with another confirmed appointment</option>
                  <option value="staff_unavailable">Medical staff temporarily unavailable</option>
                  <option value="schedule_passed">Selected appointment schedule has already passed</option>
                  <option value="clinic_adjustment">Clinic operational adjustment</option>
                  <option value="other">Other</option>
                </select>

                {reason === 'other' && (
                  <textarea
                    value={reasonOther}
                    onChange={(e) => {
                      setReasonOther(e.target.value);
                    }}
                    placeholder="Please specify the reason (10-500 characters)"
                    style={{
                      width: '100%',
                      marginTop: '12px',
                      padding: '12px 16px',
                      borderRadius: '8px',
                      border: reasonError ? '1px solid #ef4444' : '1px solid #e5e7eb',
                      backgroundColor: '#fff',
                      color: '#1f2937',
                      fontSize: '14px',
                      minHeight: '100px',
                      fontFamily: 'inherit',
                      resize: 'vertical'
                    }}
                  />
                )}

                {reasonError && (
                  <p style={{ margin: '8px 0 0 0', fontSize: '13px', color: '#ef4444' }}>{reasonError}</p>
                )}
              </div>
            </div>

            <div className="walkin-right-col">
              <div className="datetime-picker-card" style={{ background: '#f8fafc', padding: '24px', borderRadius: '12px', border: '1px solid #f1f5f9' }}>
                <div className="calendar-widget">
                  <div className="calendar-nav" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                    <button type="button" onClick={() => {
                      const d = new Date(selectedDate);
                      d.setMonth(d.getMonth() - 1);
                      setSelectedDate(d);
                    }} style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '6px', width: '32px', height: '32px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>‹</button>
                    <h4 style={{ margin: 0, fontSize: '14px', fontWeight: 700, color: '#1e293b', letterSpacing: '0.025em' }}>{selectedDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }).toUpperCase()}</h4>
                    <button type="button" onClick={() => {
                      const d = new Date(selectedDate);
                      d.setMonth(d.getMonth() + 1);
                      setSelectedDate(d);
                    }} style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '6px', width: '32px', height: '32px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>›</button>
                  </div>

                  <div className="calendar-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '4px', textAlign: 'center' }}>
                    {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(day => (
                      <div key={day} style={{ fontSize: '11px', fontWeight: 600, color: '#94a3b8', padding: '8px 0' }}>{day}</div>
                    ))}
                    {getCalendarDays(selectedDate).map((day, i) => {
                      if (!day) return <div key={`empty-${i}`} />;
                      const d = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), day);
                      const isSelected = formatDateToPH(d) === formatDateToPH(selectedDate);
                      const isToday = formatDateToPH(d) === formatDateToPH(new Date());
                      const isPast = formatDateToPH(d) < formatDateToPH(new Date());
                      const isWeekendDate = isWeekend(d);
                      const isDisabled = isPast || isWeekendDate;

                      return (
                        <button
                          key={day}
                          type="button"
                          onClick={() => {
                            if (isWeekendDate) {
                              return;
                            }
                            if (!isPast) setSelectedDate(d);
                          }}
                          disabled={isDisabled}
                          title={isWeekendDate ? 'Clinic closed on weekends' : ''}
                          style={{
                            padding: '8px 0',
                            borderRadius: '6px',
                            border: 'none',
                            background: isSelected ? '#3b82f6' : 'transparent',
                            color: isSelected ? '#fff' : (isDisabled ? '#cbd5e1' : '#475569'),
                            cursor: isDisabled ? 'not-allowed' : 'pointer',
                            fontSize: '13px',
                            fontWeight: isSelected || isToday ? 600 : 400,
                            position: 'relative'
                          }}
                        >
                          {day}
                          {isToday && !isSelected && <span style={{ position: 'absolute', bottom: '4px', left: '50%', transform: 'translateX(-50%)', width: '4px', height: '4px', background: '#3b82f6', borderRadius: '50%' }} />}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="time-picker-widget" style={{ marginTop: '24px', borderTop: '1px solid #e2e8f0', paddingTop: '24px' }}>
                  <h5 style={{ margin: '0 0 16px 0', fontSize: '13px', fontWeight: 600, color: '#64748b' }}>Select Time Slot</h5>

                  <div className="time-slots-container" style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                    <div>
                      <span style={{ fontSize: '11px', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Morning</span>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px', marginTop: '8px' }}>
                        {timeSlots.morning.map((time) => {
                          const { valid } = isWeekend(selectedDate) ? { valid: false } : { valid: true };
                          const matchingSlots = bookedSlots.filter(slot => {
                            const sameService = slot.serviceId === appointment.serviceId || slot.serviceName === appointment.serviceName;
                            const sameTime = slot.time === time;
                            return sameService && sameTime;
                          });
                          const uniqueBookingTypes = [...new Set(matchingSlots.map(slot => slot.bookingType))];

                          return (
                            <button
                              key={time}
                              type="button"
                              disabled={!valid}
                              onClick={() => setSelectedTime(time)}
                              style={{
                                padding: '8px 2px 16px 2px',
                                borderRadius: '6px',
                                border: '1px solid',
                                borderColor: selectedTime === time ? '#29499d' : (valid ? '#e2e8f0' : '#f1f5f9'),
                                background: selectedTime === time ? '#29499d' : (valid ? '#fff' : '#f8fafc'),
                                color: selectedTime === time ? '#fff' : (valid ? '#475569' : '#9ca3af'),
                                fontSize: '11px',
                                fontWeight: selectedTime === time ? 700 : 500,
                                cursor: valid ? 'pointer' : 'not-allowed',
                                position: 'relative'
                              }}
                            >
                              {time}
                              {uniqueBookingTypes.length > 0 && (
                                <div style={{
                                  position: 'absolute',
                                  bottom: '4px',
                                  left: '50%',
                                  transform: 'translateX(-50%)',
                                  display: 'flex',
                                  gap: '4px',
                                  alignItems: 'center'
                                }}>
                                  {uniqueBookingTypes.map((bookingType, idx) => (
                                    <div
                                      key={idx}
                                      style={{
                                        width: '8px',
                                        height: '8px',
                                        backgroundColor: bookingType === 'WALK_IN' ? '#fdebb2' : '#19458c',
                                        borderRadius: '50%'
                                      }}
                                    />
                                  ))}
                                </div>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    <div>
                      <span style={{ fontSize: '11px', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Afternoon</span>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px', marginTop: '8px' }}>
                        {timeSlots.afternoon.map((time) => {
                          const { valid } = isWeekend(selectedDate) ? { valid: false } : { valid: true };
                          const matchingSlots = bookedSlots.filter(slot => {
                            const sameService = slot.serviceId === appointment.serviceId || slot.serviceName === appointment.serviceName;
                            const sameTime = slot.time === time;
                            return sameService && sameTime;
                          });
                          const uniqueBookingTypes = [...new Set(matchingSlots.map(slot => slot.bookingType))];

                          return (
                            <button
                              key={time}
                              type="button"
                              disabled={!valid}
                              onClick={() => setSelectedTime(time)}
                              style={{
                                padding: '8px 2px 16px 2px',
                                borderRadius: '6px',
                                border: '1px solid',
                                borderColor: selectedTime === time ? '#29499d' : (valid ? '#e2e8f0' : '#f1f5f9'),
                                background: selectedTime === time ? '#29499d' : (valid ? '#fff' : '#f8fafc'),
                                color: selectedTime === time ? '#fff' : (valid ? '#475569' : '#9ca3af'),
                                fontSize: '11px',
                                fontWeight: selectedTime === time ? 700 : 500,
                                cursor: valid ? 'pointer' : 'not-allowed',
                                position: 'relative'
                              }}
                            >
                              {time}
                              {uniqueBookingTypes.length > 0 && (
                                <div style={{
                                  position: 'absolute',
                                  bottom: '4px',
                                  left: '50%',
                                  transform: 'translateX(-50%)',
                                  display: 'flex',
                                  gap: '4px',
                                  alignItems: 'center'
                                }}>
                                  {uniqueBookingTypes.map((bookingType, idx) => (
                                    <div
                                      key={idx}
                                      style={{
                                        width: '8px',
                                        height: '8px',
                                        backgroundColor: bookingType === 'WALK_IN' ? '#fdebb2' : '#19458c',
                                        borderRadius: '50%'
                                      }}
                                    />
                                  ))}
                                </div>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="modal-footer" style={{ padding: '20px 32px', borderTop: '1px solid #f3f4f6', display: 'flex', justifyContent: 'flex-end', gap: '12px', flexShrink: 0 }}>
          <button className="ma-outline-btn" onClick={onClose}>Cancel</button>
          <button
            className="ma-primary-btn"
            onClick={onReschedule}
            disabled={isSubmitting || !selectedTime}
            style={{ minWidth: '140px' }}
          >
            {isSubmitting ? 'Rescheduling...' : 'Reschedule'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

// ---------------- Logout Modal ----------------
function LogoutModal({ isOpen, onClose }) {
  const navigate = useNavigate();

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const handleConfirm = async () => {
    sessionStorage.setItem('logoutInProgress', 'true');
    try {
      // Delete the current session document directly before signing out.
      const currentUser = auth.currentUser;
      if (currentUser) {
        await sessionService.deleteCurrentSession(currentUser.uid);
      }
      sessionService.clearCurrentSession();

      await signOutSecondary(auth);
      localStorage.clear();
      sessionStorage.removeItem('freshLoginInProgress');
      navigate("/login", { replace: true });
    } catch (e) {
      console.log("Sign out error:", e);
      sessionService.clearCurrentSession();
      localStorage.clear();
      sessionStorage.removeItem('freshLoginInProgress');
      navigate("/login", { replace: true });
    } finally {
      setTimeout(() => sessionStorage.removeItem('logoutInProgress'), 1000);
    }
  };

  return (
    <Portal>
      <div className="modal-overlay" onClick={onClose}>
      <div className="logout-modal-card" onClick={(e) => e.stopPropagation()}>
        <button className="logout-modal-close" onClick={onClose}>
          <FiX />
        </button>

        <div className="logout-modal-icon-wrapper">
          <div className="logout-modal-icon-bg">
            <FiLogOut />
          </div>
        </div>

        <div className="logout-modal-content">
          <h2 className="logout-modal-title">Ready to leave?</h2>
          <p className="logout-modal-subtext">
            Your session will end and you'll be redirected to the login page. You can always come back later!
          </p>
        </div>

        <div className="logout-modal-actions">
          <button className="logout-modal-btn cancel" onClick={onClose}>
            <span className="btn-text">Stay logged in</span>
          </button>
          <button className="logout-modal-btn confirm" onClick={handleConfirm}>
            <span className="btn-text">Log out</span>
            <FiLogOut className="btn-icon" />
          </button>
        </div>

        <div className="logout-modal-footer">
          <div className="logout-modal-security-info">
            <FiShield className="security-icon" />
            <span>Your data is safely saved</span>
          </div>
        </div>
      </div>
    </div>
    </Portal>
  );
}

export default function AdminPage() {
  const [searchParams] = useSearchParams();
  const tabParam = searchParams.get("tab");
  const [activeMenu, setActiveMenu] = useState(tabParam || "dashboard");
  const [highlightState, setHighlightState] = useState(null); // { highlightPatientUid, highlightAppointmentId }
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [isMobileDrawerOpen, setIsMobileDrawerOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadNotificationsCount, setUnreadNotificationsCount] = useState(0);

  useEffect(() => {
    if (tabParam) {
      setActiveMenu(tabParam);
    }
  }, [tabParam]);

  useEffect(() => {
    const handleSwitchTab = (e) => {
      if (e.detail?.tab) setActiveMenu(e.detail.tab);
    };
    window.addEventListener("admin-switch-tab", handleSwitchTab);
    return () => window.removeEventListener("admin-switch-tab", handleSwitchTab);
  }, []);

  useEffect(() => {
    const user = auth.currentUser;
    if (!user) return undefined;

    const unsubscribe = notificationService.getUserNotificationsListener(user.uid, (notifDocs) => {
      const notifs = notifDocs.map((n) => {
        const accessStatus = n.accessStatus || n.access_status || n.eventStatus || null;
        const statusLabel = accessStatus || (n.type === 'data_access_termination' ? 'EXPIRED' : '');
        const readStatus = n.status || 'unread';
        const createdAt = n.createdAt || new Date();
        const dateValue = createdAt instanceof Date ? createdAt : new Date(createdAt);

        return {
          id: n.id,
          type: n.type || 'data_access_request',
          status: statusLabel,
          readStatus,
          title: n.notificationsTitle || (statusLabel === 'ACTIVE' ? 'Access Approved' : (statusLabel === 'DENIED' ? 'Access Denied' : (statusLabel === 'EXPIRED' ? 'Access Expired' : 'Access Update'))),
          desc: n.message || '',
          timestamp: dateValue,
          time: dateValue.toLocaleString(),
          patientUid: n.patientUid,
          appointmentId: n.appointmentId
        };
      });

      notifs.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
      setNotifications(notifs);
      setUnreadNotificationsCount(notifs.filter((n) => n.readStatus === 'unread').length);
    });

    return () => {
      if (typeof unsubscribe === 'function') unsubscribe();
    };
  }, []);

  // Close drawer when clicking outside on mobile
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setIsMobileDrawerOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <div className="admin-aura-layout">
      {/* Mobile drawer backdrop */}
      <div 
        className={`admin-drawer-backdrop ${isMobileDrawerOpen ? 'visible' : ''}`}
        onClick={() => setIsMobileDrawerOpen(false)}
      />

      <aside className={`admin-aura-sidebar ${sidebarCollapsed ? 'collapsed' : ''} ${isMobileDrawerOpen ? 'drawer-open' : ''}`}>
        <div className="admin-aura-profile">
          <div
            className="admin-branding-container"
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            style={{ cursor: 'pointer' }}
          >
            <img
              src={sidebarCollapsed ? auravitIcon : logoName}
              alt="AURAVIT"
              className="admin-sidebar-logo"
            />
            {!sidebarCollapsed && (
              <>
                <span className="admin-logo-separator">|</span>
                <img
                  src={cfsiLogo}
                  alt="CFSI"
                  className="admin-cfsi-logo"
                />
              </>
            )}
          </div>
          <button
            className="admin-drawer-close"
            onClick={() => setIsMobileDrawerOpen(false)}
            aria-label="Close menu"
          >
            <FiX />
          </button>
        </div>

        <nav className="admin-aura-menu">
          <button
            className={activeMenu === "dashboard" ? "active" : ""}
            onClick={() => {
              setActiveMenu("dashboard");
              setIsMobileDrawerOpen(false);
            }}
            data-tooltip="Dashboard"
          >
            <FiTrendingUp /> <span>Dashboard</span>
          </button>

          <button
            className={activeMenu === "accounts" ? "active" : ""}
            onClick={() => {
              setActiveMenu("accounts");
              setIsMobileDrawerOpen(false);
            }}
            data-tooltip="Accounts"
          >
            <FiUsers /> <span>Accounts</span>
          </button>

          <button
            className={activeMenu === "appointments" ? "active" : ""}
            onClick={() => {
              setActiveMenu("appointments");
              setIsMobileDrawerOpen(false);
            }}
            data-tooltip="Appointments"
          >
            <FiCalendar /> <span>Appointments</span>
          </button>

          <button
            className={activeMenu === "records" ? "active" : ""}
            onClick={() => {
              setActiveMenu("records");
              setIsMobileDrawerOpen(false);
            }}
            data-tooltip="Records"
          >
            <FiFileText /> <span>Records</span>
          </button>

          <button
            className={activeMenu === "content" ? "active" : ""}
            onClick={() => {
              setActiveMenu("content");
              setIsMobileDrawerOpen(false);
            }}
            data-tooltip="Contents"
          >
            <FiBookOpen /> <span>Contents</span>
          </button>

          <button
            className={activeMenu === "peer-resources" ? "active" : ""}
            onClick={() => {
              setActiveMenu("peer-resources");
              setIsMobileDrawerOpen(false);
            }}
            data-tooltip="Peer Resources"
          >
            <FiFolder /> <span>Peer Resources</span>
          </button>

          <button
            className={activeMenu === "escalated-cases" ? "active" : ""}
            onClick={() => {
              setActiveMenu("escalated-cases");
              setIsMobileDrawerOpen(false);
            }}
            data-tooltip="Escalated Cases"
          >
            <FiShield /> <span>Escalated Cases</span>
          </button>

          <button
            className={activeMenu === "notifications" ? "active" : ""}
            onClick={() => {
              setActiveMenu("notifications");
              setIsMobileDrawerOpen(false);
            }}
            data-tooltip="Notifications"
            style={{ position: 'relative' }}
          >
            <FiBell /> <span>Notifications</span>
            {unreadNotificationsCount > 0 && (
              <span style={{
                position: 'absolute',
                top: '10px',
                right: sidebarCollapsed ? '8px' : '16px',
                minWidth: '22px',
                height: '22px',
                padding: '0 6px',
                borderRadius: '999px',
                background: 'linear-gradient(135deg, #f59e0b 0%, #ef4444 100%)',
                color: '#fff',
                fontSize: '11px',
                fontWeight: 800,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                lineHeight: 1,
                border: '2px solid #fff',
                boxShadow: '0 4px 8px rgba(239, 68, 68, 0.25)'
              }}>
                {unreadNotificationsCount > 99 ? '99+' : unreadNotificationsCount}
              </span>
            )}
          </button>

          <button
            className={activeMenu === "profile" ? "active" : ""}
            onClick={() => {
              setActiveMenu("profile");
              setIsMobileDrawerOpen(false);
            }}
            data-tooltip="Edit Profile"
          >
            <FiUser /> <span>Profile</span>
          </button>

          <button
            className={activeMenu === "logout" ? "active admin-logout" : "admin-logout"}
            onClick={() => setShowLogoutModal(true)}
            data-tooltip="Logout"
          >
            <FiLogOut /> <span>Logout</span>
          </button>
        </nav>
      </aside>

      {/* Mobile header */}
      <header className="admin-mobile-header">
        <div className="admin-mobile-header-logos">
          <img src={logoName} alt="AuraVit" />
          <span className="admin-logo-separator">|</span>
          <img src={cfsiLogo} alt="CFSI" />
        </div>
        <button
          className="admin-mobile-hamburger"
          onClick={() => setIsMobileDrawerOpen(true)}
          aria-label="Open menu"
        >
          <FiMenu />
        </button>
      </header>

      <section className="admin-aura-content">
        {activeMenu === "dashboard" && <AdminDashboard />}
        {activeMenu === "accounts" && <AccountManagement />}
        {activeMenu === "appointments" && <AppointmentManagement />}
        {activeMenu === "records" && <AdminRecords highlightState={highlightState} />}
        {activeMenu === "content" && <AdminContent />}
        {activeMenu === "peer-resources" && <AdminPeerResources />}
        {activeMenu === "escalated-cases" && <AdminMedicalEscalatedCases />}
        {activeMenu === "notifications" && (
          <ProNotifications
            role="admin"
            notifications={notifications}
            onUnreadCountChange={setUnreadNotificationsCount}
            onNotificationClick={(patientUid, appointmentId) => {
              setHighlightState({
                highlightPatientUid: patientUid,
                highlightAppointmentId: appointmentId
              });
              setActiveMenu('records');
            }}
          />
        )}
        {activeMenu === "profile" && <AdminProfile />}
      </section>

      <LogoutModal isOpen={showLogoutModal} onClose={() => setShowLogoutModal(false)} />
    </div>
  );
}



// ---------------- Assign Services Modal ----------------
function AssignServicesModal({ isOpen, onClose, onAssign }) {
  const [selectedServices, setSelectedServices] = useState([]);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [staffList, setStaffList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentStep, setCurrentStep] = useState(0);

  const services = [
    "Reproductive Health Consultation",
    "Family Planning / Contraceptive Services",
    "STI Testing / Screening",
    "Other"
  ];
  const STEPS = ["Select Services", "Select Staff"];

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
      setCurrentStep(0);
      setSelectedServices([]);
      setSelectedStaff(null);
      setSearchTerm("");
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      fetchStaff();
    }
  }, [isOpen]);

  const fetchStaff = async () => {
    setLoading(true);
    try {
      const q = query(collection(db, "users"), where("accountType", "==", "Staff"));
      const snap = await getDocs(q);
      const data = snap.docs.map(doc => ({ uid: doc.id, ...doc.data() }));
      setStaffList(data);
    } catch (err) {
      console.error("Error fetching staff:", err);
    } finally {
      setLoading(false);
    }
  };

  const toggleService = (service) => {
    setSelectedServices(prev => 
      prev.includes(service) 
        ? prev.filter(s => s !== service) 
        : [...prev, service]
    );
  };

  const handleNextStep = () => {
    if (currentStep === 0 && selectedServices.length > 0) {
      setCurrentStep(1);
    }
  };

  const handlePrevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(0);
    }
  };

  const handleAssign = () => {
    if (selectedServices.length > 0 && selectedStaff) {
      onAssign(selectedServices, selectedStaff);
      setSelectedServices([]);
      setSelectedStaff(null);
      setCurrentStep(0);
    }
  };

  const filteredStaff = staffList.filter(s => 
    s.fullName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    s.staffId?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (!isOpen) return null;

  return (
    <React.Fragment>
      <div className="modal-backdrop" onClick={onClose}>
        <div 
          style={{ 
            maxWidth: '680px', 
            width: '94%', 
            maxHeight: '85vh', 
            borderRadius: '24px', 
            overflow: 'hidden', 
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.2)', 
            position: 'relative',
            background: '#ffffff'
          }}
          onClick={e => e.stopPropagation()}
        >
          <button 
            type="button" 
            onClick={onClose} 
            style={{ 
              position: 'absolute',
              top: '14px',
              right: '14px',
              zIndex: 20,
              width: '38px', 
              height: '38px',
              borderRadius: '50%',
              background: 'rgba(255,255,255,0.18)',
              border: 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              color: 'white',
              fontSize: '22px',
              lineHeight: 1,
              transition: 'all 0.2s ease',
              backdropFilter: 'blur(6px)'
            }}
            onMouseEnter={(e) => e.target.style.background = 'rgba(255,255,255,0.32)'}
            onMouseLeave={(e) => e.target.style.background = 'rgba(255,255,255,0.18)'}
          >
            <FiX />
          </button>

          <div style={{ 
            background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 50%, #1e40af 100%)',
            padding: '24px 24px 18px 24px',
            color: 'white',
            width: '100%',
            boxSizing: 'border-box'
          }}>
            <div style={{ marginBottom: '16px' }}>
              <h2 style={{ margin: 0, fontSize: '21px', fontWeight: 700, letterSpacing: '-0.3px' }}>Assign Services to Staff</h2>
              <p style={{ margin: '6px 0 0 0', fontSize: '14px', opacity: 0.92, fontWeight: 400 }}>Assign services to a staff member</p>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div style={{ 
                background: 'rgba(255,255,255,0.2)', 
                padding: '6px 14px', 
                borderRadius: '9999px',
                fontSize: '12.5px',
                fontWeight: 600,
                letterSpacing: '0.02em'
              }}>
                Step {currentStep + 1} of {STEPS.length}
              </div>
              <div style={{ fontSize: '14px', fontWeight: 600, opacity: 0.98 }}>
                {STEPS[currentStep]}
              </div>
            </div>
          </div>

          <div style={{ padding: '16px 24px 0 24px' }}>
            <div style={{ display: 'flex', gap: '5px', height: '5px' }}>
              {STEPS.map((step, idx) => (
                <div 
                  key={idx} 
                  style={{
                    flex: 1,
                    height: '100%',
                    borderRadius: '9999px',
                    backgroundColor: idx <= currentStep ? '#3b82f6' : '#e2e8f0',
                    transition: 'background-color 0.3s ease'
                  }}
                />
              ))}
            </div>
          </div>

          <div style={{ 
            padding: '18px 24px 20px 24px', 
            overflowY: 'auto', 
            maxHeight: '52vh'
          }}>
            {/* Step 1: Select Services */}
            {currentStep === 0 && (
              <div>
                <h3 style={{ fontSize: '18px', fontWeight: 700, color: '#0f172a', margin: '0 0 18px 0' }}>Select Services</h3>
                
                <div style={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  gap: '10px', 
                  width: '100%',
                  background: '#f8fafc',
                  padding: '16px',
                  borderRadius: '14px',
                  border: '2px solid #e2e8f0',
                  boxSizing: 'border-box'
                }}>
                  {services.map(s => (
                    <label 
                      key={s} 
                      style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '10px', 
                        cursor: 'pointer', 
                        fontSize: '14px',
                        color: "#334155",
                        fontWeight: "500",
                        padding: '8px 4px',
                        borderRadius: '8px',
                        transition: 'all 0.15s ease'
                      }}
                      onMouseEnter={(e) => {
                        if (!selectedServices.includes(s)) {
                          e.currentTarget.style.background = '#eff6ff';
                        }
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'transparent';
                      }}
                    >
                      <input 
                        type="checkbox" 
                        checked={selectedServices.includes(s)}
                        onChange={() => toggleService(s)}
                        style={{ 
                          width: '18px', 
                          height: '18px',
                          accentColor: "#3b82f6"
                        }}
                      />
                      {s}
                    </label>
                  ))}
                </div>
              </div>
            )}

            {/* Step 2: Select Staff */}
            {currentStep === 1 && (
              <div>
                <h3 style={{ fontSize: '18px', fontWeight: 700, color: '#0f172a', margin: '0 0 18px 0' }}>Select Staff Member</h3>
                
                {/* Search Input */}
                <div style={{ position: 'relative', width: '100%', marginBottom: "12px", boxSizing: 'border-box' }}>
                  <FiSearch style={{ 
                    position: 'absolute', 
                    left: '16px', 
                    top: '50%', 
                    transform: 'translateY(-50%)', 
                    color: '#94a3b8',
                    fontSize: "16px"
                  }} />
                  <input 
                    type="text" 
                    placeholder="Search staff by name or ID..." 
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    style={{ 
                      width: '100%', 
                      padding: '14px 16px 14px 46px', 
                      background: '#ffffff',
                      border: "2px solid #e2e8f0",
                      borderRadius: "14px",
                      fontSize: "14px",
                      color: "#1f2937",
                      outline: "none",
                      transition: "all 0.2s ease",
                      boxSizing: 'border-box'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = "#3b82f6";
                      e.target.style.boxShadow = '0 0 0 4px rgba(59, 130, 246, 0.1)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = "#e2e8f0";
                      e.target.style.boxShadow = 'none';
                    }}
                  />
                </div>
                
                {/* Staff List */}
                <div style={{ 
                  width: '100%', 
                  maxHeight: '280px', 
                  overflowY: 'auto', 
                  border: '2px solid #e2e8f0', 
                  borderRadius: '14px',
                  background: "#ffffff",
                  boxSizing: 'border-box'
                }}>
                  {loading ? (
                    <p style={{ padding: '20px', fontSize: '14px', color: "#6b7280", textAlign: "center", margin: 0, fontWeight: '500' }}>
                      <FiRefreshCw className="spinning" style={{ marginRight: '6px' }} /> Loading staff...
                    </p>
                  ) : filteredStaff.length === 0 ? (
                    <p style={{ padding: '20px', fontSize: '14px', color: "#6b7280", textAlign: "center", margin: 0, fontWeight: '500' }}>
                      No staff members found.
                    </p>
                  ) : (
                    filteredStaff.map(staff => (
                      <div 
                        key={staff.uid}
                        onClick={() => setSelectedStaff(staff)}
                        style={{
                          padding: '12px 16px',
                          cursor: 'pointer',
                          borderBottom: '1px solid #f1f5f9',
                          background: selectedStaff?.uid === staff.uid ? 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)' : 'transparent',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '4px',
                          transition: "all 0.2s ease"
                        }}
                        onMouseEnter={(e) => {
                          if (selectedStaff?.uid !== staff.uid) {
                            e.currentTarget.style.background = "#f8fafc";
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (selectedStaff?.uid !== staff.uid) {
                            e.currentTarget.style.background = "transparent";
                          }
                        }}
                      >
                        <div style={{ fontWeight: '800', fontSize: '14px', color: '#1e293b' }}>
                          {staff.fullName}
                        </div>
                        <div style={{ fontSize: '12px', color: '#64748b', fontWeight: "600" }}>
                          {staff.role} • ID: {staff.staffId || 'N/A'}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>

          <div style={{ padding: '16px 24px 24px 24px', display: 'flex', gap: '12px', justifyContent: currentStep === 0 ? 'flex-end' : 'space-between' }}>
            {currentStep > 0 && (
              <button 
                onClick={handlePrevStep}
                style={{
                  padding: '14px 32px',
                  borderRadius: '16px',
                  border: 'none',
                  background: '#f1f5f9',
                  color: '#475569',
                  fontSize: '15px',
                  fontWeight: '700',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = '#e2e8f0';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = '#f1f5f9';
                }}
              >
                Back
              </button>
            )}
            
            <div style={{ display: 'flex', gap: '12px', marginLeft: currentStep === 0 ? 'auto' : '0' }}>
              <button 
                onClick={onClose}
                style={{
                  padding: '14px 32px',
                  borderRadius: '16px',
                  border: 'none',
                  background: '#f1f5f9',
                  color: '#475569',
                  fontSize: '15px',
                  fontWeight: '700',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = '#e2e8f0';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = '#f1f5f9';
                }}
              >
                Cancel
              </button>
              
              {currentStep < 1 ? (
                <button 
                  onClick={handleNextStep}
                  disabled={selectedServices.length === 0}
                  style={{ 
                    padding: '14px 32px',
                    borderRadius: '16px',
                    border: 'none',
                    background: selectedServices.length === 0 
                      ? '#94a3b8' 
                      : 'linear-gradient(135deg, #3b82f6 0%, #1e40af 100%)',
                    color: '#ffffff',
                    fontSize: '15px',
                    fontWeight: '700',
                    cursor: selectedServices.length === 0 ? 'not-allowed' : 'pointer',
                    boxShadow: selectedServices.length === 0 ? 'none' : '0 4px 16px rgba(59, 130, 246, 0.35)',
                    transition: 'all 0.3s cubic-bezier(0.4,0,0.2,1)'
                  }}
                  onMouseEnter={(e) => {
                    if (selectedServices.length > 0) {
                      e.target.style.transform = 'translateY(-2px)';
                      e.target.style.boxShadow = '0 8px 24px rgba(59, 130, 246, 0.45)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = selectedServices.length === 0 ? 'none' : '0 4px 16px rgba(59, 130, 246, 0.35)';
                  }}
                >
                  Next
                </button>
              ) : (
                <button 
                  disabled={!selectedStaff}
                  onClick={handleAssign}
                  style={{ 
                    padding: '14px 32px',
                    borderRadius: '16px',
                    border: 'none',
                    background: !selectedStaff 
                      ? '#94a3b8' 
                      : 'linear-gradient(135deg, #3b82f6 0%, #1e40af 100%)',
                    color: '#ffffff',
                    fontSize: '15px',
                    fontWeight: '700',
                    cursor: !selectedStaff ? 'not-allowed' : 'pointer',
                    boxShadow: !selectedStaff ? 'none' : '0 4px 16px rgba(59, 130, 246, 0.35)',
                    transition: 'all 0.3s cubic-bezier(0.4,0,0.2,1)'
                  }}
                  onMouseEnter={(e) => {
                    if (selectedStaff) {
                      e.target.style.transform = 'translateY(-2px)';
                      e.target.style.boxShadow = '0 8px 24px rgba(59, 130, 246, 0.45)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = !selectedStaff ? 'none' : '0 4px 16px rgba(59, 130, 246, 0.35)';
                  }}
                >
                  Assign Services
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </React.Fragment>
  );
}

const generateAppointmentReference = (appointmentType) => {
  const now = new Date();
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'Asia/Manila',
    year: '2-digit',
    month: '2-digit',
    day: '2-digit'
  }).formatToParts(now).reduce((acc, part) => {
    if (part.type === 'year') acc.year = part.value;
    if (part.type === 'month') acc.month = part.value;
    if (part.type === 'day') acc.day = part.value;
    return acc;
  }, { year: '', month: '', day: '' });

  const randomDigits = () => String(Math.floor(Math.random() * 10000)).padStart(4, '0');
  const prefix = appointmentType === 'walk-in' ? 'AW' : 'AS';
  return `${prefix}-${parts.year}${parts.month}${parts.day}-${randomDigits()}`;
};

const APPOINTMENT_SERVICES = [
  { id: "rh_consult", name: "Reproductive Health Consultation" },
  { id: "family_planning", name: "Family Planning / Contraceptive Services" },
  { id: "sti_testing", name: "STI Testing / Screening" }
];

export function CreateAppointmentModal({
  isOpen,
  onClose,
  setNotification,
  onCreated,
  followUpAppointment = null
}) {
  const isFollowUpMode = Boolean(followUpAppointment);
  const [formData, setFormData] = useState({
    patientName: "",
    clinicId: "park-ave",
    clinicName: "Park Avenue Clinic Branch",
    serviceId: "rh_consult",
    serviceName: "Reproductive Health Consultation",
    appointmentType: ""
  });
  const [selectedDate, setSelectedDate] = useState(getNextBusinessDay(new Date()));
  const [selectedTime, setSelectedTime] = useState("");
  const [bookedSlots, setBookedSlots] = useState([]);
  const [timeSlots, setTimeSlots] = useState({ morning: [], afternoon: [] });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [followUpRepeat, setFollowUpRepeat] = useState("none");
  const [repeatInterval, setRepeatInterval] = useState("1");
  const [occurrenceCount, setOccurrenceCount] = useState("");
  const [followUpPreview, setFollowUpPreview] = useState(null);
  const [isPreviewingFollowUp, setIsPreviewingFollowUp] = useState(false);

  const services = APPOINTMENT_SERVICES;

  const appointmentTypeOptions = [
    { value: "scheduled", label: "Appointment Scheduled (Online)" },
    { value: "walk-in", label: "Appointment Walk-in" }
  ];

  useEffect(() => {
    if (!isOpen) return;
    const sourceService = followUpAppointment && services.find(service =>
      service.id === followUpAppointment.serviceId ||
      service.name === followUpAppointment.serviceName
    );
    setFormData({
      patientName: followUpAppointment?.patientName || "",
      clinicId: followUpAppointment?.clinicId || "park-ave",
      clinicName: followUpAppointment?.clinicName || "Park Avenue Clinic Branch",
      serviceId: sourceService?.id || "rh_consult",
      serviceName: sourceService?.name || "Reproductive Health Consultation",
      appointmentType: followUpAppointment ? "scheduled" : ""
    });
    setSelectedDate(getNextBusinessDay(new Date()));
    setSelectedTime("");
    setBookedSlots([]);
    setTimeSlots({ morning: [], afternoon: [] });
    setFollowUpRepeat("none");
    setRepeatInterval("1");
    setOccurrenceCount("");
    setFollowUpPreview(null);
  }, [isOpen, followUpAppointment]);

  useEffect(() => {
    if (!isOpen) return;
    // Prevent time slot generation on weekends
    if (isWeekend(selectedDate)) {
      setTimeSlots({ morning: [], afternoon: [] });
      setSelectedTime("");
      return;
    }
    const slots = generateDynamicSlots(formData.serviceId);
    setTimeSlots(slots);
    setSelectedTime("");
  }, [formData.serviceId, formData.appointmentType, isOpen, selectedDate]);

  useEffect(() => {
    if (!isOpen || !selectedDate) return;
    const fetchBookedSlots = async () => {
      try {
        const dateStr = formatDateToPH(selectedDate);
        const slots = await appointmentService.getBookedSlots(dateStr);
        setBookedSlots(slots);
      } catch (err) {
        console.error("Error fetching booked slots:", err);
      }
    };
    fetchBookedSlots();
  }, [selectedDate, isOpen]);

  // Ensure selectedDate is never a weekend (safety net)
  useEffect(() => {
    if (isOpen && isWeekend(selectedDate)) {
      const nextBusinessDay = getNextBusinessDay(selectedDate);
      setSelectedDate(nextBusinessDay);
      setNotification({ message: "The clinic is closed on weekends. Automatically selected the next available business day.", type: "error" });
    }
  }, [selectedDate, isOpen, setNotification]);

  const validateName = (name) => {
    if (!name) return "Patient Name is required.";
    if (name.length > 50) return "Max 50 characters allowed.";
    const nameRegex = /^[a-zA-Z\s\-']+$/;
    if (!nameRegex.test(name)) return "Numbers and special characters (except - and ') are not allowed.";
    return null;
  };

  const isTimeSlotValid = (time) => {
    const rule = getServiceRule(formData.serviceId);
    const bookingType = formData.appointmentType === "walk-in" ? "WALK_IN" : "ONLINE";

    const conflict = bookedSlots.some(slot => {
      if (slot.time !== time) return false;
      const sameService = slot.serviceId === formData.serviceId || slot.serviceName === formData.serviceName;
      if (!sameService) return false;
      if (rule && rule.separateBookingType === false) {
        return true;
      }
      return slot.bookingType === bookingType;
    });

    if (conflict) return { valid: false, reason: "booked" };

    const slotValidation = getSlotValidation(time, formData.serviceId);
    if (!slotValidation.valid) {
      return { valid: false, reason: slotValidation.reason === "lunch" ? "lunch" : "service" };
    }

    const phNow = getPHNow();
    const [timeStr, period] = time.split(' ');
    let [hours, minutes] = timeStr.split(':').map(Number);
    if (period === 'PM' && hours !== 12) hours += 12;
    if (period === 'AM' && hours === 12) hours = 0;

    const selectedDateStr = formatDateToPH(selectedDate);
    const todayPHStr = formatDateToPH(new Date());
    const isToday = selectedDateStr === todayPHStr;
    const isPastDate = selectedDateStr < todayPHStr;

    if (isPastDate) return { valid: false, reason: "past" };
    if (isToday) {
      if (hours < phNow.hour) return { valid: false, reason: "past" };
      if (hours === phNow.hour && minutes <= phNow.minute) return { valid: false, reason: "past" };
    }
    return { valid: true };
  };

  const handleSelectTime = (time) => {
    const { valid, reason } = isTimeSlotValid(time);
    if (!valid) {
      if (reason === "past") {
        setNotification({ message: "This time slot has already passed. Please choose a future time.", type: "error" });
      } else if (reason === "booked") {
        setNotification({ message: "This time slot is no longer available. Please select another.", type: "error" });
      }
      return;
    }
    setSelectedTime(time);
    if (isFollowUpMode) setFollowUpPreview(null);
  };

  const handlePreviewFollowUp = async () => {
    if (!selectedDate || !selectedTime) {
      setNotification({ message: "Select a follow-up date and time before previewing the schedule.", type: "error" });
      return;
    }

    const interval = Number(repeatInterval);
    const count = Number(occurrenceCount);
    if (!Number.isInteger(interval) || interval < 1) {
      setNotification({ message: "Repeat interval must be a positive whole number.", type: "error" });
      return;
    }
    if (!Number.isInteger(count) || count < 2 || count > 500) {
      setNotification({ message: "Enter between 2 and 500 follow-up occurrences.", type: "error" });
      return;
    }

    const dates = generateFollowUpDates(formatDateToPH(selectedDate), followUpRepeat, interval, count);
    const isWalkIn = formData.appointmentType === "walk-in";
    setIsPreviewingFollowUp(true);
    setFollowUpPreview(null);
    try {
      const preview = await appointmentService.validateFollowUpSchedule({
        sourceAppointmentId: followUpAppointment.id,
        serviceId: formData.serviceId,
        serviceName: formData.serviceName,
        bookingType: isWalkIn ? "WALK_IN" : "ONLINE",
        occurrences: dates.map(date => ({ date, time: selectedTime }))
      });
      setFollowUpPreview(preview);
    } catch (err) {
      setNotification({ message: "Could not validate the follow-up schedule: " + err.message, type: "error" });
    } finally {
      setIsPreviewingFollowUp(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const nameError = isFollowUpMode
      ? (!formData.patientName.trim() ? "Patient Name is required." : null)
      : validateName(formData.patientName);
    if (nameError) {
      setNotification({ message: nameError, type: "error" });
      return;
    }
    if (!formData.appointmentType) {
      setNotification({ message: "Please choose an appointment type.", type: "error" });
      return;
    }
    if (!selectedTime) {
      setNotification({ message: "Please select a time slot.", type: "error" });
      return;
    }
    if (!selectedDate) {
      setNotification({ message: "Please select a date.", type: "error" });
      return;
    }

    if (isWeekend(selectedDate)) {
      setNotification({ message: "The clinic is closed on weekends.\nPlease select an available weekday.", type: "error" });
      return;
    }

    const interval = Number(repeatInterval);
    const count = followUpRepeat === "none" ? 1 : Number(occurrenceCount);
    if (isFollowUpMode && followUpRepeat !== "none") {
      if (!Number.isInteger(interval) || interval < 1) {
        setNotification({ message: "Repeat interval must be a positive whole number.", type: "error" });
        return;
      }
      if (!Number.isInteger(count) || count < 2 || count > 500) {
        setNotification({ message: "Enter between 2 and 500 follow-up occurrences.", type: "error" });
        return;
      }
      if (!followUpPreview?.valid) {
        setNotification({ message: "Preview and resolve every follow-up occurrence before creating the series.", type: "error" });
        return;
      }
    }

    setIsSubmitting(true);
    try {
      const isWalkIn = formData.appointmentType === "walk-in";
      const bookingType = isWalkIn ? "WALK_IN" : "ONLINE";
      if (isFollowUpMode) {
        const dates = generateFollowUpDates(
          formatDateToPH(selectedDate),
          followUpRepeat,
          interval,
          count
        );
        const createdAppointments = await appointmentService.createFollowUpAppointments({
          sourceAppointmentId: followUpAppointment.id,
          serviceId: formData.serviceId,
          serviceName: formData.serviceName,
          appointmentType: formData.appointmentType,
          bookingType,
          occurrences: dates.map(date => ({ date, time: selectedTime }))
        });
        onCreated?.(createdAppointments);
        setNotification({
          message: `${createdAppointments.length} follow-up appointment${createdAppointments.length === 1 ? "" : "s"} created.`,
          type: "success"
        });
        onClose();
        return;
      }

      const apptData = {
        clinicId: formData.clinicId,
        clinicName: formData.clinicName,
        date: formatDateToPH(selectedDate),
        patientName: formData.patientName,
        patientDob: "walk-in",
        proId: auth.currentUser?.uid || "admin",
        proName: auth.currentUser?.displayName || "Administrator",
        reference: generateAppointmentReference(formData.appointmentType),
        serviceId: formData.serviceId,
        serviceName: formData.serviceName,
        status: "CONFIRMED",
        time: selectedTime,
        type: isWalkIn ? "walk-in" : "scheduled",
        bookingType,
        appointmentType: formData.appointmentType,
        uid: "walk-in"
      };
      const result = await appointmentService.createAppointment(apptData);
      onCreated?.(result);
      onClose();
    } catch (err) {
      setNotification({ message: "Failed to create appointment: " + err.message, type: "error" });
    } finally {
      setIsSubmitting(false);
    }
  };

  const todayPHStr = formatDateToPH(new Date());
  const daysOfWeek = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

  const getCalendarDays = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startOffset = firstDay.getDay() === 0 ? 6 : firstDay.getDay() - 1;
    const totalDays = lastDay.getDate();
    const cells = [];
    for (let i = 0; i < startOffset; i++) cells.push(null);
    for (let day = 1; day <= totalDays; day++) cells.push(day);
    return cells;
  };

  const calendarDays = getCalendarDays(selectedDate);
  const submitButtonLabel = () => {
    if (isSubmitting) return "Creating...";
    if (!isFollowUpMode) return "Create Appointment";
    return followUpRepeat === "none" ? "Create Follow-Up Appointment" : "Create Follow-Up Schedule";
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="modal-backdrop" style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 10000
    }}>
      <div className="modal-card walkin-modal" style={{
        maxWidth: '850px',
        width: '95%',
        maxHeight: '90vh',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: '16px',
        backgroundColor: '#fff',
        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
        overflow: 'hidden'
      }}>
        {/* Header - Fixed */}
        <div style={{ 
          background: 'linear-gradient(135deg, #2563eb 0%, #1e40af 100%)',
          width: '100%',
          padding: '20px 28px', 
          borderBottom: '1px solid rgba(37, 99, 235, 0.2)', 
          display: 'flex', 
          justifyContent: 'flex-start', 
          alignItems: 'center',
          flexShrink: 0,
          boxSizing: 'border-box',
          gap: '12px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          minHeight: 'auto',
          overflow: 'visible'
        }}>
          <div style={{ 
            background: 'rgba(255,255,255,0.2)', 
            borderRadius: '10px', 
            padding: '8px', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center'
          }}>
            <span style={{ fontSize: '18px' }}>📅</span>
          </div>
          <h3 style={{ 
            margin: 0, 
            fontSize: '18px', 
            fontWeight: 700, 
            color: '#ffffff', 
            background: 'none', 
            backgroundImage: 'none', 
            opacity: 1, 
            visibility: 'visible', 
            display: 'block', 
            transform: 'none',
            letterSpacing: '-0.01em'
          }}>{isFollowUpMode ? "Create Follow-Up Appointment" : "Create Appointment"}</h3>
        </div>

        {/* Body - Scrollable */}
        <div 
          style={{ 
            overflowY: 'auto', 
            padding: '28px', 
            flex: 1,
            background: '#fafbfc',
            minHeight: 0,
            width: '100%',
            boxSizing: 'border-box'
          }}
        >
          <form onSubmit={handleSubmit}>
            <div className="form-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '40px' }}>
                {/* Left Column: Patient & Service Info */}
              <div className="walkin-left-col" style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                <div className="form-group">
                  <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 600, color: '#4b5563' }}>Patient Name</label>
                  <input
                    type="text"
                    placeholder={isFollowUpMode ? "" : "e.g. Juan Dela Cruz"}
                    required
                    readOnly={isFollowUpMode}
                    style={{ 
                      width: '100%', 
                      padding: '12px 16px', 
                      borderRadius: '8px', 
                      border: `1px solid ${isFollowUpMode ? '#e5e7eb' : '#d1d5db'}`,
                      backgroundColor: isFollowUpMode ? '#f9fafb' : '#fff',
                      fontSize: '15px',
                      cursor: isFollowUpMode ? 'not-allowed' : 'text',
                      outline: 'none',
                      transition: 'border-color 0.2s'
                    }}
                    value={formData.patientName}
                    onChange={(e) => setFormData({ ...formData, patientName: e.target.value })}
                  />
                </div>
                {isFollowUpMode && (
                  <div className="form-group">
                    <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 600, color: '#4b5563' }}>Original Appointment</label>
                    <input
                      type="text"
                      value={`${followUpAppointment.reference || followUpAppointment.id} — ${followUpAppointment.date || ""} ${followUpAppointment.time || ""}`.trim()}
                      readOnly
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        borderRadius: '8px',
                        border: '1px solid #e5e7eb',
                        backgroundColor: '#f9fafb',
                        color: '#6b7280',
                        fontSize: '15px',
                        cursor: 'not-allowed'
                      }}
                    />
                  </div>
                )}
                <div className="form-group">
                  <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 600, color: '#4b5563' }}>Appointment Type</label>
                  <select
                    required
                    style={{ 
                      width: '100%', 
                      padding: '12px 16px', 
                      borderRadius: '8px', 
                      border: '1px solid #d1d5db',
                      fontSize: '15px',
                      backgroundColor: '#fff',
                      cursor: 'pointer',
                      outline: 'none'
                    }}
                    value={formData.appointmentType}
                    onChange={(e) => {
                      setFormData({ ...formData, appointmentType: e.target.value });
                      setSelectedTime("");
                      setFollowUpPreview(null);
                    }}
                  >
                    <option value="">Select appointment type</option>
                    {appointmentTypeOptions.map(option => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 600, color: '#4b5563' }}>Clinic</label>
                  <input 
                    type="text" 
                    value={formData.clinicName}
                    readOnly 
                    style={{ 
                      width: '100%', 
                      padding: '12px 16px', 
                      borderRadius: '8px', 
                      border: '1px solid #e5e7eb',
                      backgroundColor: '#f9fafb',
                      color: '#6b7280',
                      fontSize: '15px',
                      cursor: 'not-allowed'
                    }} 
                  />
                </div>
                <div className="form-group">
                  <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 600, color: '#4b5563' }}>Service</label>
                  <select 
                    style={{ 
                      width: '100%', 
                      padding: '12px 16px', 
                      borderRadius: '8px', 
                      border: '1px solid #d1d5db',
                      fontSize: '15px',
                      backgroundColor: '#fff',
                      cursor: 'pointer',
                      outline: 'none'
                    }}
                    value={formData.serviceId} 
                    onChange={(e) => {
                      const selectedService = services.find(service => service.id === e.target.value);
                      setFormData({
                        ...formData,
                        serviceId: e.target.value,
                        serviceName: selectedService ? selectedService.name : formData.serviceName
                      });
                      setFollowUpPreview(null);
                    }}
                  >
                    {services.map(service => (
                      <option key={service.id} value={service.id}>{service.name}</option>
                    ))}
                  </select>
                  <div style={{ marginTop: '12px', display: 'flex', gap: '20px', alignItems: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <div style={{ width: '8px', height: '8px', backgroundColor: '#19458c', borderRadius: '50%' }} />
                      <span style={{ fontSize: '12px', color: '#6b7280' }}>Scheduled</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <div style={{ width: '8px', height: '8px', backgroundColor: '#fdebb2', borderRadius: '50%' }} />
                      <span style={{ fontSize: '12px', color: '#6b7280' }}>Walk-in</span>
                    </div>
                  </div>
                  {isFollowUpMode && (
                    <div className="form-group" style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      <label style={{ fontSize: '14px', fontWeight: 600, color: '#4b5563' }} htmlFor="follow-up-repeat">Repeat</label>
                      <select
                        id="follow-up-repeat"
                        value={followUpRepeat}
                        onChange={(event) => {
                          setFollowUpRepeat(event.target.value);
                          setFollowUpPreview(null);
                        }}
                        style={{ width: '100%', padding: '12px 16px', borderRadius: '8px', border: '1px solid #d1d5db', fontSize: '15px', backgroundColor: '#fff' }}
                      >
                        <option value="none">None</option>
                        <option value="weekly">Weekly</option>
                        <option value="monthly">Monthly</option>
                      </select>
                      {followUpRepeat !== "none" && (
                        <>
                          <label style={{ fontSize: '14px', fontWeight: 600, color: '#4b5563' }} htmlFor="follow-up-interval">
                            Repeat Every ({followUpRepeat === "weekly" ? "weeks" : "months"})
                          </label>
                          <input
                            id="follow-up-interval"
                            type="number"
                            min="1"
                            max="500"
                            required
                            value={repeatInterval}
                            onChange={(event) => {
                              setRepeatInterval(event.target.value);
                              setFollowUpPreview(null);
                            }}
                            style={{ width: '100%', padding: '12px 16px', borderRadius: '8px', border: '1px solid #d1d5db', fontSize: '15px' }}
                          />
                          <label style={{ fontSize: '14px', fontWeight: 600, color: '#4b5563' }} htmlFor="follow-up-count">Number of Follow-Up Occurrences</label>
                          <input
                            id="follow-up-count"
                            type="number"
                            min="2"
                            max="500"
                            required
                            value={occurrenceCount}
                            onChange={(event) => {
                              setOccurrenceCount(event.target.value);
                              setFollowUpPreview(null);
                            }}
                            style={{ width: '100%', padding: '12px 16px', borderRadius: '8px', border: '1px solid #d1d5db', fontSize: '15px' }}
                          />
                          <button
                            type="button"
                            onClick={handlePreviewFollowUp}
                            disabled={isPreviewingFollowUp || isSubmitting}
                            style={{ padding: '11px 16px', borderRadius: '10px', border: '1px solid #2563eb', background: '#eff6ff', color: '#1d4ed8', fontWeight: 700, cursor: isPreviewingFollowUp || isSubmitting ? 'wait' : 'pointer' }}
                          >
                            {isPreviewingFollowUp ? "Validating Schedule..." : "Preview Follow-Up Schedule"}
                          </button>
                          {followUpPreview && (
                            <div style={{ border: '1px solid #e2e8f0', borderRadius: '10px', padding: '12px', background: '#fff' }}>
                              <strong style={{ display: 'block', marginBottom: '8px', color: '#1e293b' }}>Follow-Up Schedule</strong>
                              <ul style={{ margin: 0, paddingLeft: '20px', display: 'grid', gap: '6px' }}>
                                {followUpPreview.occurrences.map((occurrence, index) => (
                                  <li key={`${occurrence.date}-${occurrence.time}-${index}`} style={{ color: occurrence.available ? '#15803d' : '#b91c1c', fontSize: '13px' }}>
                                    {occurrence.date} at {occurrence.time} — {occurrence.available ? "Available" : `Conflict: ${occurrence.reason}`}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Date & Time selection (right column) */}
              <div style={{ width: '100%' }}>
                <div style={{ background: '#fff', padding: '24px', borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: '0 2px 8px rgba(0,0,0,0.05)', width: '100%', boxSizing: 'border-box' }}>
                  <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                      <button type="button" onClick={() => {
                        const d = new Date(selectedDate);
                        d.setMonth(d.getMonth() - 1);
                        setSelectedDate(d);
                        setFollowUpPreview(null);
                      }} style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '6px', width: '32px', height: '32px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>‹</button>
                      <h4 style={{ margin: 0, fontSize: '14px', fontWeight: 700, color: '#1e293b', letterSpacing: '0.025em' }}>{selectedDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }).toUpperCase()}</h4>
                      <button type="button" onClick={() => {
                        const d = new Date(selectedDate);
                        d.setMonth(d.getMonth() + 1);
                        setSelectedDate(d);
                        setFollowUpPreview(null);
                      }} style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '6px', width: '32px', height: '32px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>›</button>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '4px', textAlign: 'center' }}>
                      {daysOfWeek.map(d => <div key={d} style={{ fontSize: '11px', fontWeight: 700, color: '#94a3b8', paddingBottom: '8px', textTransform: 'uppercase' }}>{d}</div>)}
                      {calendarDays.map((day, idx) => {
                        if (!day) return <div key={idx}></div>;
                        const curDate = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), day);
                        const curDateStr = formatDateToPH(curDate);
                        const isPast = curDateStr < todayPHStr;
                        const isWeekendDate = isWeekend(curDate);
                        const isDisabled = isPast || isWeekendDate;
                        const isSelected =
                          selectedDate.getDate() === curDate.getDate() &&
                          selectedDate.getMonth() === curDate.getMonth() &&
                          selectedDate.getFullYear() === curDate.getFullYear();
                        return (
                          <button
                            key={idx}
                            type="button"
                            disabled={isDisabled}
                            title={isWeekendDate ? 'Clinic closed on weekends.' : ''}
                            style={{
                              padding: '8px 0',
                              borderRadius: '10px',
                              border: 'none',
                              background: isSelected ? '#29499d' : 'transparent',
                              color: isSelected ? '#fff' : (isDisabled ? '#cbd5e1' : '#475569'),
                              cursor: isDisabled ? 'not-allowed' : 'pointer',
                              fontSize: '13px',
                              fontWeight: isSelected ? 700 : 500,
                              transition: 'all 0.2s',
                              boxShadow: isSelected ? '0 2px 8px rgba(59, 130, 246, 0.35)' : 'none'
                            }}
                            onClick={() => {
                              if (isWeekendDate) {
                                setNotification({ message: "The clinic is closed on weekends.\nPlease select an available weekday.", type: "error" });
                                return;
                              }
                              if (isPast) return;
                              setSelectedDate(curDate);
                              setFollowUpPreview(null);
                            }}
                          >{day}</button>
                        );
                      })}
                    </div>
                  </div>

                  <div style={{ marginTop: '24px', borderTop: '1px solid #f1f5f9', paddingTop: '20px' }}>
                    <h5 style={{ margin: '0 0 12px 0', fontSize: '14px', fontWeight: 600, color: '#1e293b' }}>Select Time</h5>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px' }}>
                      {[...timeSlots.morning, ...timeSlots.afternoon].map(time => {
                        const { valid, reason } = isTimeSlotValid(time);
                        const isSel = selectedTime === time;
                        // Find ALL booked slots for this time and service
                        const matchingSlots = bookedSlots.filter(slot => {
                          const sameService = slot.serviceId === formData.serviceId || slot.serviceName === formData.serviceName;
                          const sameTime = slot.time === time;
                          return sameService && sameTime;
                        });
                        // Get unique booking types to avoid duplicate dots
                        const uniqueBookingTypes = [...new Set(matchingSlots.map(slot => slot.bookingType))];
                        
                        // Check if the slot is fully booked or past
                        const isFullyBooked = !valid && reason === "booked";
                        const isPastHour = !valid && reason === "past";
                        
                        let hoverText = "";
                        if (isFullyBooked) hoverText = "Date fully booked";
                        if (isPastHour) hoverText = "Hour has already passed";
                        
                        return (
                          <button
                            key={time}
                            type="button"
                            style={{
                              padding: '8px 2px 16px 2px',
                              fontSize: '11px',
                              borderRadius: '10px',
                              border: '1px solid',
                              borderColor: isSel ? '#3f62b5' : (valid ? '#e2e8f0' : '#f1f5f9'),
                              background: isSel ? 'linear-gradient(135deg, #3f62b5 0%, #2563eb 100%)' : (valid ? '#fff' : '#f8fafc'),
                              color: isSel ? '#fff' : (valid ? '#475569' : '#9ca3af'),
                              cursor: valid ? 'pointer' : 'default',
                              fontWeight: isSel ? 700 : 500,
                              transition: 'all 0.2s',
                              position: 'relative',
                              boxShadow: isSel ? '0 2px 8px rgba(59, 130, 246, 0.35)' : 'none'
                            }}
                            onMouseEnter={(e) => { if (valid && !isSel) { e.target.style.background = '#eff6ff'; e.target.style.borderColor = '#3f62b5'; } }}
                            onMouseLeave={(e) => { if (valid && !isSel) { e.target.style.background = '#fff'; e.target.style.borderColor = '#e2e8f0'; } }}
                            onClick={() => handleSelectTime(time)}
                            title={hoverText}
                          >
                            {time}
                            {uniqueBookingTypes.length > 0 && (
                              <div style={{ 
                                position: 'absolute', 
                                bottom: '4px', 
                                left: '50%', 
                                transform: 'translateX(-50%)', 
                                display: 'flex', 
                                gap: '4px',
                                alignItems: 'center'
                              }}>
                                {uniqueBookingTypes.map((bookingType, idx) => (
                                  <div 
                                    key={idx}
                                    style={{ 
                                      width: '8px', 
                                      height: '8px', 
                                      backgroundColor: bookingType === "WALK_IN" ? '#fdebb2' : '#3f62b5', 
                                      borderRadius: '50%' 
                                    }} 
                                  />
                                ))}
                              </div>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </form>
        </div>

        {/* Footer - Fixed */}
        <div style={{ 
          padding: '20px 28px', 
          borderTop: '1px solid #f1f5f9', 
          display: 'flex', 
          justifyContent: 'flex-end', 
          gap: '14px', 
          background: '#fff',
          flexShrink: 0
        }}>
          <button 
            type="button" 
            onClick={onClose} 
            style={{ 
              padding: '11px 22px', 
              borderRadius: '12px', 
              border: '1px solid #e2e8f0', 
              background: '#fff', 
              color: '#4b5563', 
              fontWeight: 700, 
              cursor: 'pointer', 
              transition: 'all 0.2s',
              fontSize: '14px'
            }}
            onMouseEnter={(e) => { e.target.style.background = '#f8fafc'; e.target.style.borderColor = '#cbd5e1'; }}
            onMouseLeave={(e) => { e.target.style.background = '#fff'; e.target.style.borderColor = '#e2e8f0'; }}
          >
            Cancel
          </button>
          <button 
            type="button" 
            disabled={isSubmitting} 
            onClick={handleSubmit}
            style={{ 
              padding: '11px 22px', 
              borderRadius: '12px', 
              border: 'none', 
              background: 'linear-gradient(135deg, #2563eb 0%, #1e40af 100%)', 
              color: '#fff', 
              fontWeight: 700, 
              cursor: isSubmitting ? 'not-allowed' : 'pointer',
              opacity: isSubmitting ? 0.7 : 1,
              transition: 'all 0.2s',
              boxShadow: isSubmitting ? 'none' : '0 4px 14px rgba(37, 99, 235, 0.4)',
              fontSize: '14px'
            }}
            onMouseEnter={(e) => { if (!isSubmitting) { e.target.style.transform = 'translateY(-2px)'; e.target.style.boxShadow = '0 6px 20px rgba(37, 99, 235, 0.5)'; } }}
            onMouseLeave={(e) => { if (!isSubmitting) { e.target.style.transform = 'translateY(0)'; e.target.style.boxShadow = '0 4px 14px rgba(37, 99, 235, 0.4)'; } }}
          >
            {submitButtonLabel()}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

function AppointmentManagement({ initialTab = "appointments" }) {
  const [appointments, setAppointments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState(initialTab);
  const [showExportModal, setShowExportModal] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [patientAssistanceData, setPatientAssistanceData] = useState({});
  
  // Task 1: Search, Filter, and Pagination states
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [serviceFilter, setServiceFilter] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState(null);
  const itemsPerPage = 10;
  // eslint-disable-next-line no-unused-vars
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  // Task 2, 3, 4, 5: Assign Services states
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showCreateAppointmentModal, setShowCreateAppointmentModal] = useState(false);
  const [followUpAppointmentTarget, setFollowUpAppointmentTarget] = useState(null);
  const [assignedStaff, setAssignedStaff] = useState([]);
  const [isStaffLoading, setIsStaffLoading] = useState(true);
  const [notification, setNotification] = useState({ message: "", type: "" });

  // Service Assignment Search and Filter states
  const [staffSearch, setStaffSearch] = useState("");
  const [staffServiceFilter, setStaffServiceFilter] = useState("");
  const [staffCurrentPage, setStaffCurrentPage] = useState(1);
  const staffItemsPerPage = 10;

  // Admin action states (Approve / Reschedule / Cancel)
  const [adminRescheduleTarget, setAdminRescheduleTarget] = useState(null);
  const [adminCancelTarget, setAdminCancelTarget] = useState(null);
  const [adminIsCancelling, setAdminIsCancelling] = useState(false);

  // Blockchain modal state
  const [showBlockchainModal, setShowBlockchainModal] = useState(false);
  const [blockchainViewing, setBlockchainViewing] = useState(null);

  // Tamper alerts state
  const [tamperAlerts, setTamperAlerts] = useState([]);
  const [showResolveModal, setShowResolveModal] = useState(false);
  const [resolveTarget, setResolveTarget] = useState(null);
  const [resolvePassword, setResolvePassword] = useState("");
  const [resolvePasswordError, setResolvePasswordError] = useState("");
  const [isResolving, setIsResolving] = useState(false);

  // Resolve modal — multi-step (review → restore → resolve)

  // Helper function to get badge color based on assistance reason
  const getAssistanceBadgeColor = (reason) => {
    const colors = {
      minor: '#dbeafe',
      senior: '#fef3c7',
      pwd: '#d1fae5',
      nrnw: '#fce7f3',
      other: '#e5e7eb'
    };
    return colors[reason?.toLowerCase()] || colors.other;
  };

  const getAssistanceBadgeTextColor = (reason) => {
    const colors = {
      minor: '#1e40af',
      senior: '#92400e',
      pwd: '#065f46',
      nrnw: '#9d174d',
      other: '#374151'
    };
    return colors[reason?.toLowerCase()] || colors.other;
  };
  // step: "review" | "restoring" | "restored" | "resolving"
  const [resolveStep, setResolveStep] = useState("review");
  const [resolveAuditLogs, setResolveAuditLogs] = useState([]);
  const [resolveAuditLoading, setResolveAuditLoading] = useState(false);
  const [resolveRestoreResult, setResolveRestoreResult] = useState(null);
  const [resolveShowPassword, setResolveShowPassword] = useState(false);

  // Minimal reschedule prompt state
  const [adminIsSubmittingReschedule, setAdminIsSubmittingReschedule] = useState(false);
  // Reschedule modal states
  const [selectedAdminRescheduleDate, setSelectedAdminRescheduleDate] = useState(getNextBusinessDay(new Date()));
  const [selectedAdminRescheduleTime, setSelectedAdminRescheduleTime] = useState("");
  const [adminRescheduleReason, setAdminRescheduleReason] = useState("schedule_conflict");
  const [adminRescheduleReasonOther, setAdminRescheduleReasonOther] = useState("");
  const [adminRescheduleReasonError, setAdminRescheduleReasonError] = useState("");
  const [adminBookedSlots, setAdminBookedSlots] = useState([]);
  const [adminTimeSlots, setAdminTimeSlots] = useState({ morning: [], afternoon: [] });
  const adminBookedSlotsCache = useRef({});

  // Tooltip state
  const [tooltip, setTooltip] = useState({ visible: false, text: '', x: 0, y: 0, type: 'default' });

  // Action button tooltip handlers
  const handleActionMouseEnter = (e, text, type = 'default') => {
    setTooltip({
      visible: true,
      text: text,
      x: e.clientX - 150,
      y: e.clientY,
      type: type
    });
  };

  const handleActionMouseMove = (e) => {
    if (tooltip.visible) {
      setTooltip(prev => ({
        ...prev,
        x: e.clientX - 150,
        y: e.clientY
      }));
    }
  };

  const handleActionMouseLeave = () => {
    setTooltip({ visible: false, text: '', x: 0, y: 0, type: 'default' });
  };

  const getStatusLabel = (status) => {
    const mapping = {
      'PENDING': 'Pending Approval',
      'CONFIRMED': 'Confirmed',
      'ACCEPTED': 'Accepted',
      'CANCELLING': 'Cancelling...',
      'COMPLETED': 'Completed',
      'CANCELLED_BY_USER': 'Cancelled by User',
      'CANCELLED_BY_PRO': 'Cancelled by Professional'
    };
    return mapping[status] || status;
  };

  // Export handlers
  const handleExport = async (format) => {
    setIsExporting(true);
    try {
      const reportDate = new Date().toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });

      switch (format) {
        case 'pdf':
          await exportToPDF(reportDate);
          break;
        case 'excel':
          await exportToExcel(reportDate);
          break;
        case 'csv':
          await exportToCSV(reportDate);
          break;
        default:
          throw new Error('Invalid export format');
      }
      setShowExportModal(false);
    } catch (error) {
      console.error('Export failed:', error);
      alert('Export failed. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  const exportColumns = {
    includeStatus: statusFilter === 'all',
    includeService: serviceFilter === '',
    serviceLabel: serviceFilter || ''
  };

  const getExportPatientDetails = async (items) => {
    const uids = [...new Set(items.map(item => item.uid).filter(Boolean))];
    const details = {};
    await Promise.all(uids.map(async uid => {
      const snapshot = await getDoc(doc(db, 'users', uid));
      const data = snapshot.exists() ? snapshot.data() : {};
      const location = [
        data.contactDetails?.barangay,
        data.contactDetails?.province,
        data.contactDetails?.region
      ].filter(Boolean).join(', ') || data.personalInformation?.province || 'N/A';
      details[uid] = {
        uic: data.uniqueIdentifierCode || data.accountSetup?.uniqueIdentifierCode || 'N/A',
        dob: data.personalInformation?.dob || 'N/A',
        location
      };
    }));
    return details;
  };

  const getExportHeaders = () => [
    ...(exportColumns.includeStatus ? ['Status'] : []),
    'Patient', 'UIC', 'DOB', 'Address', 'Reference #', 'Clinic', 'Medical Pro',
    ...(exportColumns.includeService ? ['Service'] : []),
    'Date & Time'
  ];

  const getExportRow = (apt, patientDetails) => [
    ...(exportColumns.includeStatus ? [getStatusLabel(apt.status)] : []),
    apt.patientName || 'N/A',
    patientDetails[apt.uid]?.uic || 'N/A',
    patientDetails[apt.uid]?.dob || 'N/A',
    patientDetails[apt.uid]?.location || 'N/A',
    apt.reference || 'N/A',
    apt.clinicName || 'N/A',
    apt.proName,
    ...(exportColumns.includeService ? [(apt.serviceName || 'N/A').replace(/<br\/>/g, ' ')] : []),
    `${apt.date || 'N/A'} ${apt.time || 'N/A'}`
  ];

  const exportServiceLabel = exportColumns.serviceLabel
    ? `Service: ${exportColumns.serviceLabel}`
    : null;
  const exportStatusLabel = statusFilter !== 'all'
    ? `Status: ${getStatusLabel(statusFilter)}`
    : null;
  const exportDateRangeLabel = dateFrom || dateTo
    ? `Date Range: ${dateFrom || 'Any'} - ${dateTo || 'Any'}`
    : null;

  const exportToPDF = async (reportDate) => {
    const pdf = createPDF({ title: "Appointments Report" });
    const patientDetails = await getExportPatientDetails(filteredAppointments);
    let startY = pdf.contentStartY;
    if (exportServiceLabel) {
      pdf.doc.setFontSize(11);
      pdf.doc.text(exportServiceLabel, pdf.margin, startY);
      startY += 10;
    }
    if (exportStatusLabel) {
      pdf.doc.setFontSize(10);
      pdf.doc.text(exportStatusLabel, pdf.margin, startY);
      startY += 10;
    }
    pdf.doc.setFontSize(10);
    pdf.doc.text(`Total Appointments: ${filteredAppointments.length}`, pdf.margin, startY);
    if (exportDateRangeLabel) {
      pdf.doc.text(exportDateRangeLabel, pdf.doc.internal.pageSize.getWidth() - pdf.margin, startY, { align: 'right' });
    }
    startY += 10;
    pdf.autoTable({
      head: [getExportHeaders()],
      body: filteredAppointments.map(apt => getExportRow(apt, patientDetails)),
      startY,
      styles: { overflow: 'linebreak' },
      columnStyles: { [exportColumns.includeStatus ? 4 : 3]: { cellWidth: 42 } }
    });
    pdf.save(`appointments-report-${new Date().toISOString().split('T')[0]}.pdf`);
  };

  const exportToExcel = async (reportDate) => {
    const workbook = XLSX.utils.book_new();
    const patientDetails = await getExportPatientDetails(filteredAppointments);
    const summaryData = [
      ['APPOINTMENTS REPORT'],
      ['Report Date', reportDate],
      ['Status Filter', statusFilter === 'all' ? 'All' : getStatusLabel(statusFilter)],
      ...(exportServiceLabel ? [['Service', exportColumns.serviceLabel]] : []),
      ...(exportStatusLabel ? [[exportStatusLabel]] : []),
      [],
      ['APPOINTMENT SUMMARY'],
      ['Total Appointments', filteredAppointments.length, ...(exportDateRangeLabel ? ['Date Range', exportDateRangeLabel] : [])],
      ['Pending', filteredAppointments.filter(apt => apt.status === 'PENDING').length],
      ['Confirmed', filteredAppointments.filter(apt => apt.status === 'CONFIRMED').length],
      ['Accepted', filteredAppointments.filter(apt => apt.status === 'ACCEPTED').length],
      ['Completed', filteredAppointments.filter(apt => apt.status === 'COMPLETED').length],
      ['Cancelled by User', filteredAppointments.filter(apt => apt.status === 'CANCELLED_BY_USER').length],
      ['Cancelled by Professional', filteredAppointments.filter(apt => apt.status === 'CANCELLED_BY_PRO').length]
    ];
    XLSX.utils.book_append_sheet(workbook, XLSX.utils.aoa_to_sheet(summaryData), 'Summary');
    const appointmentData = [['APPOINTMENT DETAILS']];
    if (exportServiceLabel) appointmentData.push([exportServiceLabel]);
    if (exportStatusLabel) appointmentData.push([exportStatusLabel]);
    appointmentData.push(getExportHeaders(), ...filteredAppointments.map(apt => getExportRow(apt, patientDetails)));
    XLSX.utils.book_append_sheet(workbook, XLSX.utils.aoa_to_sheet(appointmentData), 'Appointments');
    XLSX.writeFile(workbook, `appointments-report-${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  const exportToCSV = async (reportDate) => {
    const patientDetails = await getExportPatientDetails(filteredAppointments);
    const csvData = [
      ['APPOINTMENTS REPORT'],
      ['Report Date', reportDate],
      ['Status Filter', statusFilter === 'all' ? 'All' : getStatusLabel(statusFilter)],
      ...(exportServiceLabel ? [['Service', exportColumns.serviceLabel]] : []),
      ...(exportStatusLabel ? [[exportStatusLabel]] : []),
      [],
      ['APPOINTMENT SUMMARY'],
      ['Total Appointments', filteredAppointments.length, ...(exportDateRangeLabel ? ['Date Range', exportDateRangeLabel] : [])],
      [],
      ['APPOINTMENT DETAILS'],
      ...(exportServiceLabel ? [[exportServiceLabel]] : []),
      ...(exportStatusLabel ? [[exportStatusLabel]] : []),
      getExportHeaders(),
      ...filteredAppointments.map(apt => getExportRow(apt, patientDetails))
    ];
    const csvContent = csvData.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `appointments-report-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  // View Data modal states
  const [adminViewDataTarget, setAdminViewDataTarget] = useState(null);
  const [adminPatientInfo, setAdminPatientInfo] = useState(null);
  const [adminIsLoadingRecords, setAdminIsLoadingRecords] = useState(false);

  // Attachments modal states
  const [adminAttachmentsTarget, setAdminAttachmentsTarget] = useState(null);
  const [adminExistingNotes, setAdminExistingNotes] = useState([]);
  const appointmentDataCache = useRef({});
  const lastFetchedAppointmentId = useRef(null);

  // Fetch appointment data when admin attachments modal opens
  useEffect(() => {
    const fetchLatestAppointmentData = async () => {
      if (adminAttachmentsTarget?.id) {
        // Skip if we already fetched this appointment recently
        if (lastFetchedAppointmentId.current === adminAttachmentsTarget.id && appointmentDataCache.current[adminAttachmentsTarget.id]) {
          const cachedData = appointmentDataCache.current[adminAttachmentsTarget.id];
          setAdminExistingNotes(cachedData.attachmentNotes || []);
          setAdminAttachmentsTarget(prev => ({
            ...prev,
            attachments: cachedData.attachments || [],
            attachmentNotes: cachedData.attachmentNotes || []
          }));
          return;
        }

        try {
          const apptDoc = await getDoc(doc(db, "appointments", adminAttachmentsTarget.id));
          if (apptDoc.exists()) {
            const data = apptDoc.data();
            appointmentDataCache.current[adminAttachmentsTarget.id] = data;
            lastFetchedAppointmentId.current = adminAttachmentsTarget.id;
            setAdminExistingNotes(data.attachmentNotes || []);
            setAdminAttachmentsTarget(prev => ({
              ...prev,
              attachments: data.attachments || [],
              attachmentNotes: data.attachmentNotes || []
            }));
          }
        } catch (err) {
          console.error("Error fetching appointment data:", err);
        }
      }
    };

    fetchLatestAppointmentData();
  }, [adminAttachmentsTarget?.id]);

  // Admin profile — fetched once on mount for proName resolution
  const [adminProfile, setAdminProfile] = useState(null);

  // Fetch latest appointment data when attachments modal opens
  useEffect(() => {
    const fetchLatestAppointmentData = async () => {
      if (adminAttachmentsTarget?.id) {
        try {
          const apptDoc = await getDoc(doc(db, "appointments", adminAttachmentsTarget.id));
          if (apptDoc.exists()) {
            const data = apptDoc.data();
            setAdminAttachmentsTarget(prev => ({
              ...prev,
              attachments: data.attachments || [],
              attachmentNotes: data.attachmentNotes || []
            }));
          }
        } catch (err) {
          console.error("Error fetching latest appointment data:", err);
        }
      }
    };
    fetchLatestAppointmentData();
  }, [adminAttachmentsTarget?.id]);

  // Request Data Access modal states (for COMPLETED non-admin-credited appointments)
  const [adminAccessTarget, setAdminAccessTarget] = useState(null);
  const [adminAccessDuration, setAdminAccessDuration] = useState(1);
  const [adminAccessStaffId, setAdminAccessStaffId] = useState("");
  const [adminAccessStaffIdValid, setAdminAccessStaffIdValid] = useState(false);
  const [adminAccessStaffIdError, setAdminAccessStaffIdError] = useState("");
  const [adminAccessPassword, setAdminAccessPassword] = useState("");
  const [adminAccessShowPassword, setAdminAccessShowPassword] = useState(false);
  const [adminAccessPasswordError, setAdminAccessPasswordError] = useState("");
  const [adminAccessBannerMessage, setAdminAccessBannerMessage] = useState("");
  const [adminAccessBannerType, setAdminAccessBannerType] = useState("error");
  const [adminAccessIsSubmitting, setAdminAccessIsSubmitting] = useState(false);
  const [adminCurrentTime, setAdminCurrentTime] = useState(new Date());

  // Fetch admin's own Firestore user document once on mount for proName resolution
  useEffect(() => {
    const fetchAdminProfile = async () => {
      const currentUser = auth.currentUser;
      if (!currentUser) return;
      try {
        const userSnap = await getDoc(doc(db, 'users', currentUser.uid));
        if (userSnap.exists()) {
          setAdminProfile(userSnap.data());
        }
      } catch (err) {
        console.error('Error fetching admin profile:', err);
      }
    };
    fetchAdminProfile();
  }, []);

  // Update adminCurrentTime every minute for data-access expiry checks
  useEffect(() => {
    const timer = setInterval(() => setAdminCurrentTime(new Date()), 60000);
    return () => clearInterval(timer);
  }, []);

  // Helper: resolve admin display name from profile with same fallback chain as MedicalProfessional
  const getAdminProName = () => {
    const currentUser = auth.currentUser;
    return adminProfile?.fullName ||
      `${adminProfile?.firstName || ''} ${adminProfile?.lastName || ''}`.trim() ||
      currentUser?.displayName ||
      currentUser?.email ||
      'Admin';
  };

  // Helper: check if a data access request is currently active (mirrors AppointmentHistory)
  const isAdminAccessActive = (request) => {
    if (!request || request.status !== 'ACTIVE') return false;
    const requesterId = request.professionalId || request.proId;
    if (!requesterId || requesterId !== auth.currentUser?.uid) return false;
    if (!request.expiresAt) return true;
    return new Date(request.expiresAt) > adminCurrentTime;
  };

  // Helper: generate appointment PDF slip
  const handleAdminDownloadSlip = (appt) => {
    createAppointmentSlipPDF(appt, {
      serviceName: (appt.serviceName || '').replace(/<[^>]*>/g, ''),
    });
  };

  // Staff ID validation for Request Data Access modal
  const handleAdminAccessStaffIdChange = (e) => {
    const value = e.target.value;
    setAdminAccessStaffId(value);
    if (!value) { setAdminAccessStaffIdValid(false); setAdminAccessStaffIdError(""); return; }
    if (value.length > 11) { setAdminAccessStaffIdValid(false); setAdminAccessStaffIdError("Invalid Staff ID format"); return; }
    const legacyPattern = /^STAFF-\d{4}$/;
    const currentPattern = /^AURA\d{7}$/;
    if (value !== ADMIN_ACCOUNT_ID && !legacyPattern.test(value) && !currentPattern.test(value)) {
      setAdminAccessStaffIdValid(false);
      setAdminAccessStaffIdError("Staff ID must be in format AURA####### or ADMAURA2026");
      return;
    }
    const actualStaffId = adminProfile?.staffId ||
      adminProfile?.adminId ||
      (adminProfile?.accountType === "Admin" ? ADMIN_ACCOUNT_ID : "");
    if (value === actualStaffId || (value === ADMIN_ACCOUNT_ID && actualStaffId === ADMIN_ACCOUNT_ID)) {
      setAdminAccessStaffIdValid(true);
      setAdminAccessStaffIdError("");
    } else {
      setAdminAccessStaffIdValid(false);
      setAdminAccessStaffIdError("Staff ID does not match your account");
    }
  };

  // Password validation for Request Data Access modal
  const handleAdminAccessPasswordChange = (e) => {
    const value = e.target.value;
    setAdminAccessPassword(value);
    if (!value) { setAdminAccessPasswordError(""); return; }
    if (value.length < 8) { setAdminAccessPasswordError("Password must be at least 8 characters"); return; }
    if (value.length > 24) { setAdminAccessPasswordError("Password must not exceed 24 characters"); return; }
    if (/(.)\1{2,}/.test(value)) { setAdminAccessPasswordError("Password must not contain repeated characters"); return; }
    setAdminAccessPasswordError("");
  };

  // Submit Request Data Access
  const handleAdminRequestAccess = async (e) => {
    e.preventDefault();
    if (!adminAccessDuration || !adminAccessStaffId || !adminAccessPassword) {
      setAdminAccessBannerMessage("Please fill in all fields.");
      setAdminAccessBannerType("error");
      return;
    }
    const actualStaffId = adminProfile?.staffId ||
      adminProfile?.adminId ||
      (adminProfile?.accountType === "Admin" ? ADMIN_ACCOUNT_ID : "");
    if (!adminAccessStaffIdValid || adminAccessStaffId !== actualStaffId) {
      setAdminAccessBannerMessage("The provided Staff ID does not match your account.");
      setAdminAccessBannerType("error");
      return;
    }
    setAdminAccessIsSubmitting(true);
    setAdminAccessBannerMessage("");
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) throw new Error("No authenticated user");
      const credential = EmailAuthProvider.credential(currentUser.email, adminAccessPassword);
      await reauthenticateWithCredential(currentUser, credential);
      await appointmentService.requestDataAccess(
        adminAccessTarget.id,
        currentUser.uid,
        parseInt(adminAccessDuration),
        actualStaffId,
        {
          fullName: getAdminProName(),
          role: "Admin",
          department: "Administration",
          adminId: ADMIN_ACCOUNT_ID,
        }
      );
      setAdminAccessBannerMessage("Access request sent successfully!");
      setAdminAccessBannerType("success");
      setTimeout(() => {
        setAdminAccessTarget(null);
        setAdminAccessBannerMessage("");
        setAdminAccessDuration(1);
        setAdminAccessStaffId("");
        setAdminAccessPassword("");
        setAdminAccessStaffIdValid(false);
        setAdminAccessStaffIdError("");
      }, 2000);
    } catch (err) {
      if (err.code === 'auth/wrong-password' || err.code === 'auth/invalid-credential') {
        setAdminAccessBannerMessage("Incorrect password. Please try again.");
      } else {
        setAdminAccessBannerMessage("Failed to send access request: " + err.message);
      }
      setAdminAccessBannerType("error");
    } finally {
      setAdminAccessIsSubmitting(false);
    }
  };

  // Task 1: Real-time appointments listener with server-side filtering
  useEffect(() => {
    // Only run if user is authenticated
    if (!auth.currentUser) return;
    
    setIsLoading(true);
    
    // Build query with server-side filters
    let q = query(collection(db, "appointments"), limit(500));
    
    // Add status filter if not "all"
    if (statusFilter !== "all") {
      q = query(q, where("status", "==", statusFilter));
    }
    
    // Add date range filters, defaulting to the last 90 days when no start date is selected.
    if (dateFrom) {
      const startDate = new Date(dateFrom);
      q = query(q, where("date", ">=", startDate.toISOString().split('T')[0]));
    } else {
      // Default to last 90 days to reduce initial load
      const ninetyDaysAgo = new Date();
      ninetyDaysAgo.setDate(ninetyDaysAgo.getDate() - 90);
      q = query(q, where("date", ">=", ninetyDaysAgo.toISOString().split('T')[0]));
    }

    if (dateTo) {
      const endDate = new Date(dateTo);
      q = query(q, where("date", "<=", endDate.toISOString().split('T')[0]));
    }
    
    const unsubscribe = onSnapshot(q, (snap) => {
      const data = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setAppointments(data);
      setIsLoading(false);
      setIsRefreshing(false);
    }, (err) => {
      console.error("Error subscribing to appointments:", err);
      setIsLoading(false);
      setIsRefreshing(false);
    });
    
    return () => unsubscribe();
  }, [statusFilter, dateFrom, dateTo, refreshKey]);

  // Fetch patient assistance data for appointments
  useEffect(() => {
    const fetchPatientAssistance = async () => {
      if (appointments.length === 0) {
        setPatientAssistanceData({});
        return;
      }

      const uniqueUids = [...new Set(appointments.map(apt => apt.uid).filter(Boolean))];
      const assistanceData = {};

      await Promise.all(uniqueUids.map(async (uid) => {
        try {
          const userDoc = await getDoc(doc(db, "users", uid));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            const assistanceRequired = userData.assistance?.assistanceRequired || userData.assistanceRequired || userData.accountSetup?.assistanceRequired || "";
            const reasonForAssistance = userData.assistance?.reasonForAssistance || userData.reasonForAssistance || userData.accountSetup?.reasonForAssistance || [];
            const reasonsArray = Array.isArray(reasonForAssistance) ? reasonForAssistance : (reasonForAssistance ? [reasonForAssistance] : []);
            assistanceData[uid] = {
              assistanceRequired,
              reasonForAssistance: reasonsArray
            };
          }
        } catch (err) {
          console.error('Error fetching patient assistance:', err);
        }
      }));

      setPatientAssistanceData(assistanceData);
    };

    fetchPatientAssistance();
  }, [appointments]);

  // Task 5: Real-time assigned staff listener - OPTIMIZED
  useEffect(() => {
    // Only run if user is authenticated
    if (!auth.currentUser) return;
    
    setIsStaffLoading(true);
    
    const q = query(
      collection(db, "users"), 
      where("accountType", "==", "Staff"),
      where("servicesAssigned", "==", true),
      limit(50) // Add limit
    );
    
    const unsubscribe = onSnapshot(q, (snap) => {
      const data = snap.docs.map(doc => ({ uid: doc.id, ...doc.data() }));
      setAssignedStaff(data);
      setIsStaffLoading(false);
    }, (err) => {
      console.error("Error subscribing to assigned staff:", err);
      setIsStaffLoading(false);
    });
    
    return () => unsubscribe();
  }, []);

  // Fetch tamper alerts
  useEffect(() => {
    if (!auth.currentUser) return;
    
    const q = query(
      collection(db, "tamper_alerts"),
      where("status", "==", "active")
    );
    
    const unsubscribe = onSnapshot(q, (snap) => {
      const data = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setTamperAlerts(data);
    }, (err) => {
      console.error("Error subscribing to tamper alerts:", err);
    });
    
    return () => unsubscribe();
  }, []);

  // Task 1: Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [search, statusFilter]);

  // Task 1: Filter logic (status is now filtered server-side)
  const filteredAppointments = (() => {
    const adminUid = auth.currentUser?.uid;
    const filtered = appointments.filter(apt => {
      const searchLower = search.toLowerCase();
      const matchesSearch =
        (apt.patientName?.toLowerCase().includes(searchLower)) ||
        (apt.proName?.toLowerCase().includes(searchLower)) ||
        (apt.serviceName?.toLowerCase().includes(searchLower)) ||
        (apt.date?.toLowerCase().includes(searchLower)) ||
        (getStatusLabel(apt.status).toLowerCase().includes(searchLower));
      const matchesService = matchesServiceFilter(apt, serviceFilter);
      return matchesSearch && matchesService;
    });
    // Part 8: Sort by date (latest first), then float admin-credited appointments to the top
    return filtered.sort((a, b) => {
      // Sort by date descending (latest first)
      const dateCompare = new Date(b.date) - new Date(a.date);
      if (dateCompare !== 0) return dateCompare;
      
      // Secondary sort: admin-credited appointments to the top
      const aOwned = a.proId === adminUid ? 0 : 1;
      const bOwned = b.proId === adminUid ? 0 : 1;
      return aOwned - bOwned;
    });
  })();

  // Task 1: Pagination logic
  const sortedAppointments = sortAppointments(filteredAppointments, sortConfig, (appointment, key) => {
    switch (key) {
      case "status":
        return appointment.status ? getStatusLabel(appointment.status) : null;
      case "patientName":
        return appointment.patientName || null;
      case "reference":
        return appointment.reference || null;
      case "clinicName":
        return appointment.clinicName || appointment.clinic || null;
      case "proName":
        return appointment.proName || null;
      case "serviceName":
        return getAppointmentTextValue(appointment.serviceName) || null;
      case "dateTime":
        return getAppointmentDateTimeValue(appointment);
      case "blockchainStatus":
        return appointment.blockchain?.status || null;
      case "cancelReason":
        return appointment.cancelReason || appointment.reason || appointment.cancellationReason || null;
      default:
        return null;
    }
  });

  const totalPages = Math.ceil(sortedAppointments.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedAppointments = sortedAppointments.slice(startIndex, endIndex);

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handleSort = (key) => {
    setSortConfig((current) => getNextAppointmentSortConfig(current, key));
    setCurrentPage(1);
  };

  const getSortHeaderProps = (key, label) => ({
    onClick: () => handleSort(key),
    onKeyDown: (event) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        handleSort(key);
      }
    },
    tabIndex: 0,
    title: getAppointmentSortTitle(label, key, sortConfig),
    "aria-label": getAppointmentSortTitle(label, key, sortConfig),
    "aria-sort": sortConfig?.key !== key
      ? "none"
      : sortConfig.direction === "asc" ? "ascending" : "descending",
    style: { cursor: "pointer" }
  });

  // Service Assignment filter logic
  const filteredAssignedStaff = assignedStaff.filter(staff => {
    const searchLower = staffSearch.toLowerCase();
    const matchesSearch = 
      (staff.fullName?.toLowerCase().includes(searchLower)) ||
      (staff.role?.toLowerCase().includes(searchLower)) ||
      (staff.staffId?.toLowerCase().includes(searchLower)) ||
      (staff.uid?.toLowerCase().includes(searchLower));
    
    const selectedServiceKey = getServiceFilterKey(staffServiceFilter);
    const assignedServices = Array.isArray(staff.assignServices)
      ? staff.assignServices
      : [staff.assignServices];
    const matchesService = !staffServiceFilter || assignedServices.some(
      (service) => getServiceFilterKey(service) === selectedServiceKey
    );
    
    return matchesSearch && matchesService;
  });

  // Service Assignment pagination logic
  const staffTotalPages = Math.ceil(filteredAssignedStaff.length / staffItemsPerPage);
  const staffStartIndex = (staffCurrentPage - 1) * staffItemsPerPage;
  const staffEndIndex = staffStartIndex + staffItemsPerPage;
  const paginatedAssignedStaff = filteredAssignedStaff.slice(staffStartIndex, staffEndIndex);

  const handleStaffPrevPage = () => {
    if (staffCurrentPage > 1) {
      setStaffCurrentPage(staffCurrentPage - 1);
    }
  };

  const handleStaffNextPage = () => {
    if (staffCurrentPage < staffTotalPages) {
      setStaffCurrentPage(staffCurrentPage + 1);
    }
  };

  // Task 4: Handle service assignment
  const handleAssignService = async (services, staff) => {
    try {
      await updateDoc(doc(db, "users", staff.uid), {
        assignServices: services,
        servicesAssigned: true,
        updatedAt: serverTimestamp()
      });
      setNotification({ message: `Services assigned to ${staff.fullName}`, type: "success" });
      setShowAssignModal(false);
    } catch (err) {
      console.error("Error assigning service:", err);
      setNotification({ message: "Failed to assign service. Please try again.", type: "error" });
    }
  };

  // Admin reschedule helpers
  useEffect(() => {
    if (adminRescheduleTarget) {
      const slots = generateDynamicSlots(adminRescheduleTarget.serviceId || adminRescheduleTarget.serviceName);
      setAdminTimeSlots(slots);
      setSelectedAdminRescheduleDate(getNextBusinessDay(new Date()));
      setSelectedAdminRescheduleTime("");
      setAdminRescheduleReason("schedule_conflict");
      setAdminRescheduleReasonOther("");
      setAdminRescheduleReasonError("");
    }
  }, [adminRescheduleTarget]);

  useEffect(() => {
    const fetchBooked = async () => {
      if (!adminRescheduleTarget || !selectedAdminRescheduleDate) return;
      const dateStr = formatDateToPH(selectedAdminRescheduleDate);
      if (adminBookedSlotsCache.current[dateStr]) {
        setAdminBookedSlots(adminBookedSlotsCache.current[dateStr]);
        return;
      }
      try {
        const slots = await appointmentService.getBookedSlots(dateStr);
        adminBookedSlotsCache.current[dateStr] = slots;
        setAdminBookedSlots(slots);
      } catch (err) {
        console.error('Error fetching booked slots for admin reschedule:', err);
      }
    };
    fetchBooked();
  }, [adminRescheduleTarget, selectedAdminRescheduleDate]);

  const isAdminTimeSlotValid = (time) => {
    if (!adminRescheduleTarget) return { valid: false };
    if (isWeekend(selectedAdminRescheduleDate)) return { valid: false, reason: 'weekend' };

    const rule = getServiceRule(adminRescheduleTarget.serviceId || adminRescheduleTarget.serviceName);
    const bookingType = adminRescheduleTarget.bookingType || (adminRescheduleTarget.type === "walk-in" ? "WALK_IN" : "ONLINE");

    const conflict = adminBookedSlots.some(slot => {
      if (slot.time !== time) return false;
      const sameService = slot.serviceId === adminRescheduleTarget.serviceId || slot.serviceName === adminRescheduleTarget.serviceName;
      if (!sameService) return false;
      if (rule && rule.separateBookingType === false) return true;
      return slot.bookingType === bookingType;
    });

    if (conflict) return { valid: false, reason: 'Already booked' };

    const phNow = getPHNow();
    const [timeStr, period] = time.split(' ');
    let [hours, minutes] = timeStr.split(':').map(Number);
    if (period === 'PM' && hours !== 12) hours += 12;
    if (period === 'AM' && hours === 12) hours = 0;

    const selectedDateStr = formatDateToPH(selectedAdminRescheduleDate);
    const todayPHStr = formatDateToPH(new Date());
    const isToday = selectedDateStr === todayPHStr;
    const isPastDate = selectedDateStr < todayPHStr;
    if (isPastDate) return { valid: false, reason: 'past' };
    if (isToday) {
      if (hours < phNow.hour) return { valid: false, reason: 'past' };
      if (hours === phNow.hour && minutes <= phNow.minute) return { valid: false, reason: 'past' };
    }
    return { valid: true };
  };

  const handleAdminReschedule = async () => {
    if (!adminRescheduleTarget || !selectedAdminRescheduleDate || !selectedAdminRescheduleTime) return;
    if (isWeekend(selectedAdminRescheduleDate)) {
      setNotification({ message: 'The clinic is closed on weekends. Please select a weekday.', type: 'error' });
      return;
    }

    const finalReason = adminRescheduleReason === 'other' ? adminRescheduleReasonOther : (
      adminRescheduleReason === 'schedule_conflict' ? 'Schedule conflict with another confirmed appointment' :
      adminRescheduleReason === 'staff_unavailable' ? 'Medical staff temporarily unavailable' :
      adminRescheduleReason === 'schedule_passed' ? 'Selected appointment schedule has already passed' :
      adminRescheduleReason === 'clinic_adjustment' ? 'Clinic operational adjustment' : adminRescheduleReason
    );

    if (!finalReason || !finalReason.trim()) {
      setAdminRescheduleReasonError('Please provide a reason for rescheduling.');
      return;
    }
    const trimmed = finalReason.trim();
    if (trimmed.length < 10) {
      setAdminRescheduleReasonError('Reason must be at least 10 characters long.');
      return;
    }
    if (trimmed.length > 500) {
      setAdminRescheduleReasonError('Reason must not exceed 500 characters.');
      return;
    }

    setAdminRescheduleReasonError("");
    setAdminIsSubmittingReschedule(true);
    try {
      const dateStr = formatDateToPH(selectedAdminRescheduleDate);
      await appointmentService.rescheduleAppointment(
        adminRescheduleTarget.id,
        dateStr,
        selectedAdminRescheduleTime,
        trimmed,
        adminProfile
      );
      setNotification({ message: 'Appointment rescheduled and confirmed successfully!', type: 'success' });
      setAdminRescheduleTarget(null);
      setSelectedAdminRescheduleTime("");
      setAdminRescheduleReason('schedule_conflict');
      setAdminRescheduleReasonOther('');
    } catch (err) {
      console.error('Admin reschedule failed:', err);
      setNotification({ message: err?.message || 'Failed to reschedule appointment.', type: 'error' });
    } finally {
      setAdminIsSubmittingReschedule(false);
    }
  };

  // View Data handler
  useEffect(() => {
    const fetchPatientData = async () => {
      if (adminViewDataTarget?.uid) {
        setAdminIsLoadingRecords(true);
        try {
          const userRef = doc(db, "users", adminViewDataTarget.uid);
          const userSnap = await getDoc(userRef);
          if (userSnap.exists()) {
            const userData = userSnap.data();
            const isAssistedAccount = `${userData.assistanceRequired || userData.assistance?.assistanceRequired || ""}`.toLowerCase() === "yes";
            const guardianDetails = userData.guardianContactDetails || null;
            const hasGuardianDetails = Boolean(
              guardianDetails && (
                guardianDetails.guardianFullName ||
                guardianDetails.guardianRelationshipToUser ||
                guardianDetails.guardianContactNumber ||
                guardianDetails.guardianAddress
              )
            );
            setAdminPatientInfo({
              healthInformation: {
                medicalHistory: userData.medicalHistory || [],
                medicalOther: userData.medicalOther || "",
                pastSurgeries: userData.pastSurgeries || [],
                surgeryOther: userData.surgeryOther || "",
                allergies: userData.allergies || [],
                allergyOther: userData.allergyOther || "",
                smokingStatus: userData.smoking || "",
                alcoholUse: userData.alcoholUse || ""
              },
              medicationHistory: {
                currentMedications: userData.currentMedications || "",
                hormonalMedications: userData.hormonalMedications || "",
                otherContraception: userData.contraceptionOther || ""
              },
              sexualHistory: {
                stiHistory: userData.stiHistory || [],
                stiOther: userData.stiOther || "",
                hivStatus: userData.hivStatus || "",
                hivDetails: userData.hivOther || "",
                lastHivTest: userData.lastTestedDate || ""
              },
              patientInformation: {
                fullName: ((userData.personalInformation?.firstName || "") + " " + (userData.personalInformation?.lastName || "")).trim(),
                uic: userData.uniqueIdentifierCode || userData.accountSetup?.uniqueIdentifierCode || "Not Provided",
                dateOfBirth: userData.personalInformation?.dob || "",
                sex: userData.personalInformation?.sex || "",
                contactNumber: formatPhilippineContactNumber(
                  userData.personalInformation?.phone ||
                  userData.phone ||
                  userData.contact ||
                  userData.contactDetails?.contact
                ),
                location: [userData.contactDetails?.barangay, userData.contactDetails?.province, userData.contactDetails?.region].filter(Boolean).join(", ") || "",
                email: userData.email || ""
              },
              ...(isAssistedAccount && hasGuardianDetails && {
                guardianInformation: {
                  guardianFullName: guardianDetails.guardianFullName || "",
                  relationship: guardianDetails.guardianRelationshipToUser || "",
                  contactNumber: formatPhilippineContactNumber(guardianDetails.guardianContactNumber),
                  guardianAddress: guardianDetails.guardianAddress || ""
                }
              })
            });
          }
          setAdminIsLoadingRecords(false);
        } catch (err) {
          console.error("Error fetching patient data:", err);
          setAdminIsLoadingRecords(false);
        }
      }
    };
    fetchPatientData();
  }, [adminViewDataTarget?.uid]);

  // Complete handler
  const handleAdminComplete = async (appointment) => {
    try {
      const currentUser = auth.currentUser;
      const additionalData = {};
      if (currentUser) {
        additionalData.proId = currentUser.uid;
        additionalData.proName = getAdminProName();
        additionalData.auditCompletedId = currentUser.uid;
      }
      await appointmentService.updateStatus(appointment.id, "COMPLETED", additionalData);
      setNotification({ message: "Appointment completed.", type: "success" });
    } catch (err) {
      console.error("Admin complete failed:", err);
      setNotification({ message: "Failed to complete appointment.", type: "error" });
    }
  };

  // Accept handler
  const handleAdminAccept = async (appointment) => {
    try {
      const currentUser = auth.currentUser;
      const additionalData = {};
      if (currentUser) {
        additionalData.proId = currentUser.uid;
        additionalData.proName = getAdminProName();
        additionalData.auditAcceptedId = currentUser.uid;
      }
      await appointmentService.updateStatus(appointment.id, "ACCEPTED", additionalData);
      setNotification({ message: "Appointment accepted.", type: "success" });
    } catch (err) {
      console.error("Admin accept failed:", err);
      setNotification({ message: "Failed to accept appointment.", type: "error" });
    }
  };

  // Resolve tamper alert handler
  // Helper: close & reset the resolve modal completely
  const closeResolveModal = () => {
    setShowResolveModal(false);
    setResolveTarget(null);
    setResolvePassword("");
    setResolvePasswordError("");
    setResolveStep("review");
    setResolveAuditLogs([]);
    setResolveAuditLoading(false);
    setResolveRestoreResult(null);
    setResolveShowPassword(false);
  };

  // Step 1 — open modal: fetch audit log immediately so admin can review history
  const openResolveModal = async (appointment) => {
    setResolveTarget(appointment);
    setResolveStep("review");
    setResolveAuditLogs([]);
    setResolveAuditLoading(true);
    setResolveRestoreResult(null);
    setResolvePassword("");
    setResolvePasswordError("");
    setResolveShowPassword(false);
    setShowResolveModal(true);

    try {
      const getAuditLog = httpsCallable(functionsService, "getAuditLog");
      const result = await getAuditLog({ appointmentId: appointment.id });
      setResolveAuditLogs(result.data?.logs || []);
    } catch (err) {
      console.error("Failed to fetch audit log:", err);
      setResolveAuditLogs([]);
    } finally {
      setResolveAuditLoading(false);
    }
  };

  // Step 2 — Restore Original: re-auth → call restoreHealthRecord CF
  const handleRestoreOriginal = async (e) => {
    e.preventDefault();
    if (!resolvePassword) {
      setResolvePasswordError("Please enter your password to authorise the restore.");
      return;
    }

    setIsResolving(true);
    setResolvePasswordError("");
    setResolveStep("restoring");

    try {
      const currentUser = auth.currentUser;
      if (!currentUser) throw new Error("No authenticated user.");

      // Re-authenticate before performing a destructive restore
      const credential = EmailAuthProvider.credential(currentUser.email, resolvePassword);
      await reauthenticateWithCredential(currentUser, credential);

      // Call the Cloud Function — it restores fields, re-hashes, and returns isValid
      const restoreHealthRecord = httpsCallable(functionsService, "restoreHealthRecord");
      const result = await restoreHealthRecord({ appointmentId: resolveTarget.id });
      const data = result.data;

      setResolveRestoreResult(data);
      setResolveStep("restored");
    } catch (err) {
      if (err.code === "auth/wrong-password" || err.code === "auth/invalid-credential") {
        setResolvePasswordError("Incorrect password. Please try again.");
      } else if (err.code === "functions/not-found") {
        setResolvePasswordError("No backup found for this record. It may have been finalized before backup support was added.");
      } else {
        setResolvePasswordError("Restore failed: " + (err.message || String(err)));
      }
      setResolveStep("review");
    } finally {
      setIsResolving(false);
    }
  };

  // Step 3 — Resolve Alert: only allowed once restore confirmed hash match
  const handleResolveTamperAlert = async () => {
    if (!resolveRestoreResult?.isValid) return;

    setIsResolving(true);
    setResolveStep("resolving");

    try {
      const currentUser = auth.currentUser;
      if (!currentUser) throw new Error("No authenticated user.");

      const alertQuery = query(
        collection(db, "tamper_alerts"),
        where("appointmentId", "==", resolveTarget.id),
        where("status", "==", "active")
      );
      const alertSnapshot = await getDocs(alertQuery);

      for (const alertDoc of alertSnapshot.docs) {
        await updateDoc(alertDoc.ref, {
          status: "resolved",
          resolvedAt: serverTimestamp(),
          resolvedBy: currentUser.uid
        });
      }

      setNotification({ message: "Tamper alert resolved — record successfully restored.", type: "success" });
      closeResolveModal();
    } catch (err) {
      setResolvePasswordError("Failed to resolve alert: " + err.message);
      setResolveStep("restored");
    } finally {
      setIsResolving(false);
    }
  };

  return (
    <div className="apt-page">
      <NotificationModal 
        message={notification.message} 
        type={notification.type} 
        onClose={() => setNotification({ message: "", type: "" })} 
      />

      <CreateAppointmentModal
        isOpen={showCreateAppointmentModal}
        onClose={() => {
          setShowCreateAppointmentModal(false);
          setFollowUpAppointmentTarget(null);
        }}
        setNotification={setNotification}
        followUpAppointment={followUpAppointmentTarget}
        onCreated={(newAppointment) => {
          const createdAppointments = Array.isArray(newAppointment) ? newAppointment : [newAppointment];
          setAppointments(prev => {
            const existingIds = new Set(prev.map(appointment => appointment.id));
            return [...createdAppointments.filter(appointment => !existingIds.has(appointment.id)), ...prev];
          });
          setShowCreateAppointmentModal(false);
          setFollowUpAppointmentTarget(null);
        }}
      />

      <CancelAppointmentModal
        isOpen={!!adminCancelTarget}
        appointment={adminCancelTarget}
        onClose={() => setAdminCancelTarget(null)}
        isSubmitting={adminIsCancelling}
        theme="staff"
        onConfirm={async (appointment, finalReason) => {
          if (!appointment) return;
          try {
            setAdminIsCancelling(true);
            await appointmentService.updateStatus(appointment.id, 'CANCELLED_BY_PRO', {
              cancelReason: finalReason,
              cancelledByRole: 'the Administrator',
              cancelledByEmail: auth.currentUser?.email || ''
            });
            setNotification({ message: 'Appointment cancelled.', type: 'success' });
            setAdminCancelTarget(null);
          } catch (err) {
            console.error('Admin cancel failed:', err);
            setNotification({ message: 'Failed to cancel appointment.', type: 'error' });
          } finally {
            setAdminIsCancelling(false);
          }
        }}
      />

      <AdminRescheduleModal
        isOpen={!!adminRescheduleTarget}
        appointment={adminRescheduleTarget}
        onClose={() => setAdminRescheduleTarget(null)}
        onReschedule={handleAdminReschedule}
        isSubmitting={adminIsSubmittingReschedule}
        bookedSlots={adminBookedSlots}
        timeSlots={adminTimeSlots}
        selectedDate={selectedAdminRescheduleDate}
        setSelectedDate={setSelectedAdminRescheduleDate}
        selectedTime={selectedAdminRescheduleTime}
        setSelectedTime={setSelectedAdminRescheduleTime}
        reason={adminRescheduleReason}
        setReason={setAdminRescheduleReason}
        reasonOther={adminRescheduleReasonOther}
        setReasonOther={setAdminRescheduleReasonOther}
        reasonError={adminRescheduleReasonError}
      />

      {/* Request Data Access Modal — for COMPLETED non-admin-credited appointments */}
      {adminAccessTarget && createPortal(
        <div className="ma-modal-backdrop">
          <div className="ma-modal-card" style={{ maxWidth: '450px' }}>
            <button
              className="ma-modal-close"
              type="button"
              onClick={() => {
                setAdminAccessTarget(null);
                setAdminAccessBannerMessage("");
                setAdminAccessDuration(1);
                setAdminAccessStaffId("");
                setAdminAccessPassword("");
                setAdminAccessStaffIdValid(false);
                setAdminAccessStaffIdError("");
                setAdminAccessPasswordError("");
              }}
            >
              <FiX />
            </button>
            <h3>Request Patient Data Access</h3>

            {adminAccessBannerMessage && (
              <div style={{
                padding: '10px 14px',
                borderRadius: '8px',
                marginBottom: '16px',
                fontSize: '13px',
                fontWeight: '500',
                background: adminAccessBannerType === 'success' ? '#ecfdf5' : '#fef2f2',
                color: adminAccessBannerType === 'success' ? '#065f46' : '#991b1b',
                border: `1px solid ${adminAccessBannerType === 'success' ? '#6ee7b7' : '#fca5a5'}`
              }}>
                {adminAccessBannerMessage}
              </div>
            )}

            <div className="ma-modal-details" style={{ marginBottom: '24px' }}>
              <div className="ma-detail-row">
                <span className="ma-detail-label">Patient</span>
                <span className="ma-detail-value">{adminAccessTarget?.patientName}</span>
              </div>
              <div className="ma-detail-row">
                <span className="ma-detail-label">Reference #</span>
                <span className="ma-detail-value">{adminAccessTarget?.reference}</span>
              </div>
            </div>

            <form onSubmit={handleAdminRequestAccess}>
              <div className="ma-form-field">
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#374151' }}>Duration of Access (Weeks)</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <button
                    type="button"
                    onClick={() => setAdminAccessDuration(prev => Math.max(1, (parseInt(prev) || 1) - 1))}
                    style={{ width: '36px', height: '36px', borderRadius: '8px', border: '1px solid #d1d5db', background: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px', fontWeight: '600', color: '#374151' }}
                  >-</button>
                  <div style={{ flex: 1, padding: '8px 16px', borderRadius: '8px', border: '1px solid #d1d5db', textAlign: 'center', fontSize: '15px', fontWeight: '600', color: '#111827', background: '#f9fafb' }}>
                    {adminAccessDuration} week{adminAccessDuration !== 1 ? 's' : ''}
                  </div>
                  <button
                    type="button"
                    onClick={() => setAdminAccessDuration(prev => Math.min(4, (parseInt(prev) || 1) + 1))}
                    style={{ width: '36px', height: '36px', borderRadius: '8px', border: '1px solid #d1d5db', background: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px', fontWeight: '600', color: '#374151' }}
                  >+</button>
                </div>
                <p style={{ fontSize: '11px', color: '#6b7280', marginTop: '6px' }}>Minimum: 1 week, Maximum: 4 weeks</p>
              </div>

              <div className="ma-form-field" style={{ marginTop: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#374151' }}>Your Staff/Admin ID</label>
                <input
                  type="text"
                  placeholder="AURA#######, STAFF-####, or ADMAURA2026"
                  value={adminAccessStaffId}
                  onChange={handleAdminAccessStaffIdChange}
                  maxLength={11}
                  required
                  style={{ width: '100%', padding: '12px', borderRadius: '8px', border: adminAccessStaffIdError ? '1px solid #ef4444' : '1px solid #e5e7eb', fontSize: '14px', color: '#374151', outline: 'none', transition: 'border-color 0.2s' }}
                />
                {adminAccessStaffIdError && <p style={{ color: '#ef4444', fontSize: '11px', marginTop: '4px' }}>{adminAccessStaffIdError}</p>}
              </div>

              <div className="ma-form-field" style={{ marginTop: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#374151' }}>Account Password</label>
                <div style={{ position: 'relative' }}>
                  <input
                    type={adminAccessShowPassword ? "text" : "password"}
                    placeholder="Enter your password to confirm"
                    value={adminAccessPassword}
                    onChange={handleAdminAccessPasswordChange}
                    onKeyDown={(e) => { if (e.key === " ") e.preventDefault(); }}
                    onPaste={(e) => e.preventDefault()}
                    maxLength={24}
                    required
                    style={{ width: '100%', padding: '12px', paddingRight: '40px', borderRadius: '8px', border: adminAccessPasswordError ? '1px solid #ef4444' : '1px solid #e5e7eb', fontSize: '14px', color: '#374151', outline: 'none', transition: 'border-color 0.2s' }}
                  />
                  <button
                    type="button"
                    onClick={() => setAdminAccessShowPassword(v => !v)}
                    style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', padding: '4px' }}
                  >
                    {adminAccessShowPassword ? <FiEye size={16} /> : <FiEyeOff size={16} />}
                  </button>
                </div>
                {adminAccessPasswordError && <p style={{ color: '#ef4444', fontSize: '11px', marginTop: '4px' }}>{adminAccessPasswordError}</p>}
              </div>

              <div className="ma-modal-actions" style={{ marginTop: '24px', display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  className="ma-modal-btn secondary"
                  onClick={() => {
                    setAdminAccessTarget(null);
                    setAdminAccessBannerMessage("");
                    setAdminAccessDuration(1);
                    setAdminAccessStaffId("");
                    setAdminAccessPassword("");
                    setAdminAccessStaffIdValid(false);
                    setAdminAccessStaffIdError("");
                    setAdminAccessPasswordError("");
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="ma-modal-btn primary"
                  disabled={adminAccessIsSubmitting}
                >
                  {adminAccessIsSubmitting ? "Processing..." : "Request Access"}
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}

      {/* View Data Modal */}
      {adminViewDataTarget && createPortal(
        <div className="ma-modal-backdrop">
          <div className="ma-modal-card" style={{ maxWidth: '600px' }}>
            <button
              className="ma-modal-close"
              type="button"
              onClick={() => {
                setAdminViewDataTarget(null);
                setAdminPatientInfo(null);
              }}
            >
              ✕
            </button>
            <h3>Patient Records - {adminViewDataTarget.patientName}</h3>
            <p style={{ fontSize: '14px', color: '#6b7280', marginBottom: '20px' }}>
              Viewing approved clinical notes and appointment details.
            </p>

            {adminIsLoadingRecords ? (
              <p style={{ textAlign: 'center', padding: '20px' }}>Loading patient records...</p>
            ) : (
              <div style={{ maxHeight: '600px', overflowY: 'auto', marginBottom: '20px', paddingRight: '12px' }}>
                {adminPatientInfo && (
                  <>
                    <div style={{ marginBottom: '24px' }}>
                      <h4 style={{ fontSize: '14px', color: '#6b7280', textTransform: 'uppercase', marginBottom: '12px', fontWeight: '700' }}>Health Information</h4>
                      <div style={{ background: '#f8fafc', padding: '16px', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                        {adminPatientInfo.healthInformation.medicalHistory?.length > 0 && (
                          <div className="ma-detail-row" style={{ padding: '8px 0', borderBottom: '1px solid rgba(226, 232, 240, 0.5)' }}>
                            <span className="ma-detail-label" style={{ color: '#64748b', fontSize: '13px' }}>Medical History</span>
                            <span className="ma-detail-value" style={{ color: '#1e293b', fontWeight: '500', fontSize: '13px', display: 'flex', justifyContent: 'flex-end', textAlign: 'right' }}>
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', justifyContent: 'flex-end' }}>
                                {adminPatientInfo.healthInformation.medicalHistory.map((item, idx) => (
                                  <span key={idx} style={{ padding: '2px 8px', background: '#eff6ff', color: '#1d4ed8', borderRadius: '4px', fontSize: '11px' }}>{item}</span>
                                ))}
                              </div>
                            </span>
                          </div>
                        )}
                        {adminPatientInfo.healthInformation.medicalOther && (
                          <div className="ma-detail-row" style={{ padding: '8px 0', borderBottom: '1px solid rgba(226, 232, 240, 0.5)' }}>
                            <span className="ma-detail-label" style={{ color: '#64748b', fontSize: '13px' }}>Medical Other</span>
                            <span className="ma-detail-value" style={{ color: '#1e293b', fontWeight: '500', fontSize: '13px' }}>{adminPatientInfo.healthInformation.medicalOther}</span>
                          </div>
                        )}
                        {adminPatientInfo.healthInformation.pastSurgeries?.length > 0 && (
                          <div className="ma-detail-row" style={{ padding: '8px 0', borderBottom: '1px solid rgba(226, 232, 240, 0.5)' }}>
                            <span className="ma-detail-label" style={{ color: '#64748b', fontSize: '13px' }}>Past Surgeries</span>
                            <span className="ma-detail-value" style={{ color: '#1e293b', fontWeight: '500', fontSize: '13px', display: 'flex', justifyContent: 'flex-end', textAlign: 'right' }}>
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', justifyContent: 'flex-end' }}>
                                {adminPatientInfo.healthInformation.pastSurgeries.map((item, idx) => (
                                  <span key={idx} style={{ padding: '2px 8px', background: '#eff6ff', color: '#1d4ed8', borderRadius: '4px', fontSize: '11px' }}>{item}</span>
                                ))}
                              </div>
                            </span>
                          </div>
                        )}
                        {adminPatientInfo.healthInformation.surgeryOther && (
                          <div className="ma-detail-row" style={{ padding: '8px 0', borderBottom: '1px solid rgba(226, 232, 240, 0.5)' }}>
                            <span className="ma-detail-label" style={{ color: '#64748b', fontSize: '13px' }}>Surgery Other</span>
                            <span className="ma-detail-value" style={{ color: '#1e293b', fontWeight: '500', fontSize: '13px' }}>{adminPatientInfo.healthInformation.surgeryOther}</span>
                          </div>
                        )}
                        {adminPatientInfo.healthInformation.allergies?.length > 0 && (
                          <div className="ma-detail-row" style={{ padding: '8px 0', borderBottom: '1px solid rgba(226, 232, 240, 0.5)' }}>
                            <span className="ma-detail-label" style={{ color: '#64748b', fontSize: '13px' }}>Allergies</span>
                            <span className="ma-detail-value" style={{ color: '#1e293b', fontWeight: '500', fontSize: '13px', display: 'flex', justifyContent: 'flex-end', textAlign: 'right' }}>
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', justifyContent: 'flex-end' }}>
                                {adminPatientInfo.healthInformation.allergies.map((item, idx) => (
                                  <span key={idx} style={{ padding: '2px 8px', background: '#fef2f2', color: '#dc2626', borderRadius: '4px', fontSize: '11px' }}>{item}</span>
                                ))}
                              </div>
                            </span>
                          </div>
                        )}
                        {adminPatientInfo.healthInformation.allergyOther && (
                          <div className="ma-detail-row" style={{ padding: '8px 0', borderBottom: '1px solid rgba(226, 232, 240, 0.5)' }}>
                            <span className="ma-detail-label" style={{ color: '#64748b', fontSize: '13px' }}>Allergy Other</span>
                            <span className="ma-detail-value" style={{ color: '#1e293b', fontWeight: '500', fontSize: '13px' }}>{adminPatientInfo.healthInformation.allergyOther}</span>
                          </div>
                        )}
                        {adminPatientInfo.healthInformation.smokingStatus && (
                          <div className="ma-detail-row" style={{ padding: '8px 0', borderBottom: '1px solid rgba(226, 232, 240, 0.5)' }}>
                            <span className="ma-detail-label" style={{ color: '#64748b', fontSize: '13px' }}>Smoking Status</span>
                            <span className="ma-detail-value" style={{ color: '#1e293b', fontWeight: '500', fontSize: '13px' }}>{adminPatientInfo.healthInformation.smokingStatus}</span>
                          </div>
                        )}
                        {adminPatientInfo.healthInformation.alcoholUse && (
                          <div className="ma-detail-row" style={{ padding: '8px 0' }}>
                            <span className="ma-detail-label" style={{ color: '#64748b', fontSize: '13px' }}>Alcohol Use</span>
                            <span className="ma-detail-value" style={{ color: '#1e293b', fontWeight: '500', fontSize: '13px' }}>{adminPatientInfo.healthInformation.alcoholUse}</span>
                          </div>
                        )}
                      </div>
                    </div>
                    <div style={{ marginBottom: '24px' }}>
                      <h4 style={{ fontSize: '14px', color: '#6b7280', textTransform: 'uppercase', marginBottom: '12px', fontWeight: '700' }}>Patient Information</h4>
                      <div style={{ background: '#f8fafc', padding: '16px', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                        <div className="ma-detail-row" style={{ padding: '8px 0', borderBottom: '1px solid rgba(226, 232, 240, 0.5)' }}>
                          <span className="ma-detail-label" style={{ color: '#64748b', fontSize: '13px' }}>Full Name</span>
                          <span className="ma-detail-value" style={{ color: '#1e293b', fontWeight: '500', fontSize: '13px' }}>{adminPatientInfo.patientInformation.fullName}</span>
                        </div>
                        <div className="ma-detail-row" style={{ padding: '8px 0', borderBottom: '1px solid rgba(226, 232, 240, 0.5)' }}>
                          <span className="ma-detail-label" style={{ color: '#64748b', fontSize: '13px' }}>UIC</span>
                          <span className="ma-detail-value" style={{ color: '#1e293b', fontWeight: '500', fontSize: '13px' }}>{adminPatientInfo.patientInformation.uic}</span>
                        </div>
                        <div className="ma-detail-row" style={{ padding: '8px 0', borderBottom: '1px solid rgba(226, 232, 240, 0.5)' }}>
                          <span className="ma-detail-label" style={{ color: '#64748b', fontSize: '13px' }}>Date Of Birth</span>
                          <span className="ma-detail-value" style={{ color: '#1e293b', fontWeight: '500', fontSize: '13px' }}>{adminPatientInfo.patientInformation.dateOfBirth}</span>
                        </div>
                        <div className="ma-detail-row" style={{ padding: '8px 0', borderBottom: '1px solid rgba(226, 232, 240, 0.5)' }}>
                          <span className="ma-detail-label" style={{ color: '#64748b', fontSize: '13px' }}>Sex</span>
                          <span className="ma-detail-value" style={{ color: '#1e293b', fontWeight: '500', fontSize: '13px' }}>{adminPatientInfo.patientInformation.sex}</span>
                        </div>
                        <div className="ma-detail-row" style={{ padding: '8px 0', borderBottom: '1px solid rgba(226, 232, 240, 0.5)' }}>
                          <span className="ma-detail-label" style={{ color: '#64748b', fontSize: '13px' }}>Contact Number</span>
                          <span className="ma-detail-value" style={{ color: '#1e293b', fontWeight: '500', fontSize: '13px' }}>{adminPatientInfo.patientInformation.contactNumber}</span>
                        </div>
                        <div className="ma-detail-row" style={{ padding: '8px 0', borderBottom: '1px solid rgba(226, 232, 240, 0.5)' }}>
                          <span className="ma-detail-label" style={{ color: '#64748b', fontSize: '13px' }}>Location</span>
                          <span className="ma-detail-value" style={{ color: '#1e293b', fontWeight: '500', fontSize: '13px' }}>{adminPatientInfo.patientInformation.location}</span>
                        </div>
                        <div className="ma-detail-row" style={{ padding: '8px 0' }}>
                          <span className="ma-detail-label" style={{ color: '#64748b', fontSize: '13px' }}>Email</span>
                          <span className="ma-detail-value" style={{ color: '#1e293b', fontWeight: '500', fontSize: '13px' }}>{adminPatientInfo.patientInformation.email}</span>
                        </div>
                      </div>
                    </div>
                    {adminPatientInfo.guardianInformation && (
                      <div className="ma-patient-summary" style={{ marginBottom: '24px' }}>
                        <h4 style={{ fontSize: '14px', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '12px', fontWeight: '700' }}>
                          Guardian / Accompanist Information
                        </h4>
                        <div className="ma-modal-details" style={{ background: '#f8fafc', padding: '16px', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                          {adminPatientInfo.guardianInformation.guardianFullName && (
                            <div className="ma-detail-row" style={{ padding: '8px 0', borderBottom: '1px solid rgba(226, 232, 240, 0.5)' }}>
                              <span className="ma-detail-label" style={{ color: '#64748b', fontSize: '13px' }}>Guardian Full Name</span>
                              <span className="ma-detail-value" style={{ color: '#1e293b', fontWeight: '500', fontSize: '13px' }}>{adminPatientInfo.guardianInformation.guardianFullName}</span>
                            </div>
                          )}
                          {adminPatientInfo.guardianInformation.relationship && (
                            <div className="ma-detail-row" style={{ padding: '8px 0', borderBottom: '1px solid rgba(226, 232, 240, 0.5)' }}>
                              <span className="ma-detail-label" style={{ color: '#64748b', fontSize: '13px' }}>Relationship</span>
                              <span className="ma-detail-value" style={{ color: '#1e293b', fontWeight: '500', fontSize: '13px' }}>{adminPatientInfo.guardianInformation.relationship}</span>
                            </div>
                          )}
                          {adminPatientInfo.guardianInformation.contactNumber && (
                            <div className="ma-detail-row" style={{ padding: '8px 0' }}>
                              <span className="ma-detail-label" style={{ color: '#64748b', fontSize: '13px' }}>Contact Number</span>
                              <span className="ma-detail-value" style={{ color: '#1e293b', fontWeight: '500', fontSize: '13px' }}>{adminPatientInfo.guardianInformation.contactNumber}</span>
                            </div>
                          )}
                          {adminPatientInfo.guardianInformation.guardianAddress && (
                            <div className="ma-detail-row" style={{ padding: '8px 0' }}>
                              <span className="ma-detail-label" style={{ color: '#64748b', fontSize: '13px' }}>Address</span>
                              <span className="ma-detail-value" style={{ color: '#1e293b', fontWeight: '500', fontSize: '13px' }}>{adminPatientInfo.guardianInformation.guardianAddress}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
            <div className="ma-modal-footer">
              <button 
                className="ma-primary-btn" 
                onClick={() => setAdminViewDataTarget(null)}
              >
                Close
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Attachments Modal */}
      {adminAttachmentsTarget && createPortal(
        <div className="ma-modal-backdrop">
          <div className="ma-modal-card wide" style={{ maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
            <button
              className="ma-modal-close"
              type="button"
              onClick={() => {
                setAdminAttachmentsTarget(null);
                setAdminExistingNotes([]);
                setNotification({ message: "", type: "" });
              }}
            >
              <FiX />
            </button>
            <h3>Appointment Attachments & Findings</h3>

            {(() => {
              // Only show expiry banner for completed appointments
              if (adminAttachmentsTarget?.status === 'COMPLETED') {
                const attachmentExpiry = getAttachmentExpiry(adminAttachmentsTarget);
                const isExpired = attachmentExpiry ? attachmentExpiry <= new Date() : false;
                return (
                  <div style={{
                    margin: '0 0 20px',
                    padding: '12px 16px',
                    borderRadius: '10px',
                    border: `1px solid ${isExpired ? '#fecaca' : '#bfdbfe'}`,
                    background: isExpired ? '#fef2f2' : '#eff6ff',
                    color: isExpired ? '#991b1b' : '#1e40af',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    fontSize: '13px'
                  }}>
                    <FiClock size={18} />
                    <span>
                      <strong>Attachment availability:</strong>{' '}
                      {attachmentExpiry
                        ? `${isExpired ? 'Expired on' : 'Available until'} ${attachmentExpiry.toLocaleString()}`
                        : 'Expiry date unavailable'}
                    </span>
                  </div>
                );
              }
              return null;
            })()}

            <div style={{ overflowY: 'auto', flex: 1, minHeight: 0, paddingRight: '4px' }}>

            <div className="ma-modal-details" style={{ marginBottom: '24px' }}>
              <div className="ma-detail-row">
                <span className="ma-detail-label">Patient</span>
                <span className="ma-detail-value">{adminAttachmentsTarget?.patientName}</span>
              </div>
              <div className="ma-detail-row">
                <span className="ma-detail-label">Reference #</span>
                <span className="ma-detail-value">{adminAttachmentsTarget?.reference}</span>
              </div>
            </div>

            <div style={{ marginTop: '32px', paddingTop: '20px', borderTop: '1px solid #f3f4f6' }}>
              <div style={{ display: 'flex', gap: '24px' }}>
                {/* Left: Existing Notes */}
                <div style={{ flex: '0 0 50%', minWidth: 0 }}>
                  <h4 style={{ fontSize: '14px', fontWeight: '700', color: '#374151', marginBottom: '12px' }}>Consultation Findings</h4>
                  {adminExistingNotes.length > 0 ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', maxHeight: '250px', overflowY: 'auto', paddingRight: '8px' }}>
                      {adminExistingNotes.map((note, idx) => (
                        <div key={idx} style={{ padding: '12px', borderRadius: '8px', border: '1px solid #e5e7eb', background: '#f9fafb' }}>
                          <p style={{ margin: 0, fontSize: '13px', color: '#374151', whiteSpace: 'pre-wrap' }}>{note.text}</p>
                          <div style={{ marginTop: '6px', fontSize: '10px', color: '#9ca3af', display: 'flex', justifyContent: 'space-between' }}>
                            <span>By {note.createdBy}</span>
                            <span>{new Date(note.createdAt).toLocaleDateString()}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p style={{ fontSize: '13px', color: '#9ca3af', fontStyle: 'italic' }}>No findings recorded yet.</p>
                  )}
                </div>

                {/* Right: Existing Attachments */}
                <div style={{ flex: '0 0 calc(50% - 24px)', minWidth: 0 }}>
                  <h4 style={{ fontSize: '14px', fontWeight: '700', color: '#374151', marginBottom: '12px' }}>Uploaded Documents</h4>
                  {adminAttachmentsTarget?.attachments?.length > 0 ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '250px', overflowY: 'auto', paddingRight: '8px' }}>
                      {adminAttachmentsTarget.attachments.map((file, index) => (
                        <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px', borderRadius: '8px', border: '1px solid #e5e7eb', background: '#fff' }}>
                          <FiPaperclip color="#6b7280" />
                          <div style={{ flex: 1, overflow: 'hidden' }}>
                            <a href={file.fileUrl} target="_blank" rel="noopener noreferrer" style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#2563eb', textDecoration: 'none', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {file.fileName || 'Unnamed File'}
                            </a>
                            <span style={{ fontSize: '10px', color: '#9ca3af' }}>{new Date(file.uploadedAt).toLocaleDateString()} by {file.uploadedBy}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p style={{ fontSize: '13px', color: '#9ca3af', fontStyle: 'italic' }}>No files found.</p>
                  )}
                </div>
              </div>
            </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Blockchain Modal */}
      {blockchainViewing && showBlockchainModal && createPortal(
        <div className="ma-modal-backdrop">
          <div className="ma-modal-card" style={{ maxWidth: '600px' }}>
            <button
              className="ma-modal-close"
              type="button"
              onClick={() => {
                setShowBlockchainModal(false);
                setBlockchainViewing(null);
              }}
            >
              <FiX />
            </button>
            
            <h3 style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <FiShield size={20} color="#2563eb" />
              Blockchain Verification
            </h3>

            <div style={{ overflowY: 'auto', flex: 1, minHeight: 0, paddingRight: '4px' }}>
              {tamperAlerts.some(alert => alert.appointmentId === blockchainViewing.id && alert.status === 'active') && (
                <div style={{ marginBottom: '16px', padding: '12px', background: '#fef2f2', borderRadius: '8px', border: '1px solid #fecaca', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <FiAlertCircle size={16} color="#dc2626" />
                  <span style={{ fontSize: '13px', color: '#991b1b', fontWeight: '500' }}>
                    Tampering detected - Data does not match blockchain record
                  </span>
                </div>
              )}

              <div className="ma-modal-details" style={{ background: '#fff', padding: '16px', borderRadius: '12px', border: '1px solid #e5e7eb', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>
                <div className="ma-detail-row" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid #f3f4f6' }}>
                  <div style={{ fontSize: '12px', color: '#6b7280' }}>Status</div>
                  <div style={{ fontSize: '14px', fontWeight: '600' }}>
                    <span 
                      style={{
                        fontSize: '12px',
                        padding: '4px 8px',
                        borderRadius: '999px',
                        fontWeight: '600',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        ...(tamperAlerts.some(alert => alert.appointmentId === blockchainViewing.id && alert.status === 'active') ? {
                          background: '#fef2f2',
                          color: '#dc2626',
                          border: '1px solid #ef4444'
                        } : blockchainViewing.blockchain.status === 'verified' ? {
                          background: '#ecfdf5',
                          color: '#059669',
                          border: '1px solid #10b981'
                        } : blockchainViewing.blockchain.status === 'pending' ? {
                          background: '#fef3c7',
                          color: '#d97706',
                          border: '1px solid #f59e0b'
                        } : {
                          background: '#f3f4f6',
                          color: '#6b7280',
                          border: '1px solid #9ca3af'
                        })
                      }}
                    >
                      {tamperAlerts.some(alert => alert.appointmentId === blockchainViewing.id && alert.status === 'active') ? (
                        <>
                          <FiAlertCircle size={10} style={{ marginRight: '4px', verticalAlign: 'middle' }} />
                          Tampered
                        </>
                      ) : (
                        <>
                          {blockchainViewing.blockchain.status === 'verified' && <FiShield size={10} style={{ marginRight: '4px', verticalAlign: 'middle' }} />}
                          {blockchainViewing.blockchain.status}
                        </>
                      )}
                    </span>
                  </div>
                </div>
                {blockchainViewing.blockchain.network && (
                  <div className="ma-detail-row" style={{ display: 'block', padding: '8px 0', borderBottom: '1px solid #f3f4f6' }}>
                    <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '2px' }}>Network</div>
                    <div style={{ fontSize: '14px', fontWeight: '500' }}>{blockchainViewing.blockchain.network}</div>
                  </div>
                )}
                {blockchainViewing.blockchain.verifiedAt && (
                  <div className="ma-detail-row" style={{ display: 'block', padding: '8px 0' }}>
                    <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '2px' }}>Verified At</div>
                    <div style={{ fontSize: '14px', fontWeight: '500' }}>{new Date(blockchainViewing.blockchain.verifiedAt).toLocaleString()}</div>
                  </div>
                )}
              </div>
            </div>

            {tamperAlerts.some(alert => alert.appointmentId === blockchainViewing.id && alert.status === 'active') && (
              <div className="ma-modal-actions" style={{ borderTop: '1px solid #e5e7eb', paddingTop: '20px', display: 'flex', gap: '12px' }}>
                <button
                  type="button"
                  className="ma-modal-btn"
                  onClick={() => {
                    openResolveModal(blockchainViewing);
                    setShowBlockchainModal(false);
                  }}
                  style={{ 
                    flex: 1,
                    background: '#fef2f2',
                    color: '#dc2626',
                    border: '1px solid #ef4444',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px'
                  }}
                >
                  <FiShield size={16} />
                  Resolve Tamper Alert
                </button>
              </div>
            )}
          </div>
        </div>,
        document.body
      )}

      {/* Resolve Tamper Alert Modal */}
      {resolveTarget && showResolveModal && createPortal(
        <div className="ma-modal-backdrop">
          <div className="ma-modal-card" style={{ maxWidth: '560px', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
            {/* Header */}
            <button
              className="ma-modal-close"
              type="button"
              onClick={closeResolveModal}
            >
              <FiX />
            </button>

            <h3 style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
              <FiShield size={20} color="#dc2626" />
              Resolve Tamper Alert
            </h3>

            {/* Record info */}
            <div className="ma-modal-details" style={{ marginBottom: '16px', flexShrink: 0 }}>
              <div className="ma-detail-row">
                <span className="ma-detail-label">Patient</span>
                <span className="ma-detail-value">{resolveTarget?.patientName}</span>
              </div>
              <div className="ma-detail-row">
                <span className="ma-detail-label">Reference #</span>
                <span className="ma-detail-value">{resolveTarget?.reference}</span>
              </div>
            </div>

            {/* ── STEP INDICATOR ── */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '20px', flexShrink: 0 }}>
              {[
                { key: 'review',    label: '1. Review' },
                { key: 'restoring', label: '2. Restore' },
                { key: 'restored',  label: '3. Confirm' }
              ].map((s, idx) => {
                const stepOrder = { review: 0, restoring: 1, restored: 2, resolving: 2 };
                const currentOrder = stepOrder[resolveStep] ?? 0;
                const isActive  = resolveStep === s.key || (s.key === 'restoring' && resolveStep === 'restoring') || (s.key === 'restored' && (resolveStep === 'restored' || resolveStep === 'resolving'));
                const isDone    = currentOrder > idx;
                return (
                  <React.Fragment key={s.key}>
                    {idx > 0 && <div style={{ flex: 1, height: '2px', background: isDone ? '#10b981' : '#e5e7eb', borderRadius: '1px' }} />}
                    <span style={{
                      fontSize: '11px', fontWeight: '600', padding: '3px 10px',
                      borderRadius: '999px', whiteSpace: 'nowrap',
                      background: isDone ? '#ecfdf5' : isActive ? '#fef2f2' : '#f3f4f6',
                      color:      isDone ? '#059669' : isActive ? '#dc2626' : '#9ca3af',
                      border:     `1px solid ${isDone ? '#10b981' : isActive ? '#ef4444' : '#e5e7eb'}`
                    }}>{s.label}</span>
                  </React.Fragment>
                );
              })}
            </div>

            {/* ── SCROLLABLE BODY ── */}
            <div style={{ overflowY: 'auto', flex: 1, minHeight: 0, paddingRight: '4px' }}>

              {/* ── AUDIT HISTORY (always visible) ── */}
              <div style={{ marginBottom: '20px' }}>
                <h4 style={{ fontSize: '13px', fontWeight: '700', color: '#374151', marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <FiFileText size={14} color="#6b7280" />
                  Audit History (Grace Period Changes)
                </h4>

                {resolveAuditLoading ? (
                  <div style={{ padding: '20px', textAlign: 'center', color: '#9ca3af', fontSize: '13px' }}>
                    Loading audit log...
                  </div>
                ) : resolveAuditLogs.length === 0 ? (
                  <div style={{ padding: '12px 14px', borderRadius: '8px', background: '#f9fafb', border: '1px solid #e5e7eb', fontSize: '13px', color: '#6b7280', fontStyle: 'italic' }}>
                    No changes were recorded during the grace period.
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '220px', overflowY: 'auto', paddingRight: '4px' }}>
                    {resolveAuditLogs.map((log, idx) => {
                      const isRestore = log.action === 'ADMIN_RESTORE';
                      return (
                        <div key={log.id || idx} style={{
                          padding: '10px 12px', borderRadius: '8px', fontSize: '12px',
                          background: isRestore ? '#ecfdf5' : '#fefce8',
                          border: `1px solid ${isRestore ? '#10b981' : '#fbbf24'}`,
                        }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                            <span style={{ fontWeight: '700', color: isRestore ? '#065f46' : '#92400e' }}>
                              {isRestore ? '✓ Admin Restore' : 'Field Update'}
                            </span>
                            <span style={{ color: '#6b7280', fontSize: '11px' }}>
                              {log.changedAtIso ? new Date(log.changedAtIso).toLocaleString() : '—'}
                            </span>
                          </div>
                          {log.note && (
                            <p style={{ margin: 0, color: '#374151' }}>{log.note}</p>
                          )}
                          {!isRestore && Object.keys(log.changedFields || {}).length > 0 && (
                            <p style={{ margin: '2px 0 0 0', color: '#6b7280' }}>
                              Changed: {Object.keys(log.changedFields).join(', ')}
                            </p>
                          )}
                          {isRestore && log.isValid !== null && (
                            <p style={{ margin: '2px 0 0 0', color: log.isValid ? '#059669' : '#dc2626', fontWeight: '600' }}>
                              Hash match: {log.isValid ? 'Yes — verified' : 'No — mismatch'}
                            </p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* ── RESTORE RESULT BANNER ── */}
              {resolveRestoreResult && (
                <div style={{
                  padding: '12px 14px', borderRadius: '8px', marginBottom: '16px',
                  background: resolveRestoreResult.isValid ? '#ecfdf5' : '#fef2f2',
                  border: `1px solid ${resolveRestoreResult.isValid ? '#10b981' : '#ef4444'}`
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                    {resolveRestoreResult.isValid
                      ? <FiCheckCircle size={16} color="#059669" />
                      : <FiAlertCircle size={16} color="#dc2626" />
                    }
                    <span style={{ fontWeight: '700', fontSize: '13px', color: resolveRestoreResult.isValid ? '#065f46' : '#991b1b' }}>
                      {resolveRestoreResult.isValid
                        ? 'Restore successful — hash matches blockchain'
                        : 'Restore completed but hash still does not match'}
                    </span>
                  </div>
                  <p style={{ margin: 0, fontSize: '11px', color: '#6b7280', fontFamily: 'monospace', wordBreak: 'break-all' }}>
                    {resolveRestoreResult.currentHash}
                  </p>
                </div>
              )}

              {/* ── RESTORE FORM (step: review) ── */}
              {(resolveStep === 'review') && (
                <form id="restore-form" onSubmit={handleRestoreOriginal}>
                  <div style={{ padding: '14px', borderRadius: '10px', background: '#fef2f2', border: '1px solid #fecaca', marginBottom: '16px' }}>
                    <p style={{ margin: '0 0 8px 0', fontSize: '13px', fontWeight: '600', color: '#991b1b', display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <FiAlertCircle size={14} />
                      Tampering detected
                    </p>
                    <p style={{ margin: 0, fontSize: '12px', color: '#7f1d1d', lineHeight: '1.5' }}>
                      The current Firestore record does not match its blockchain hash. Enter your admin password below to retrieve the protected backup and restore the original verified data.
                    </p>
                  </div>

                  <div className="ma-form-field">
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#374151', fontSize: '13px' }}>
                      Admin Password — required to authorise restore
                    </label>
                    <div style={{ position: 'relative' }}>
                      <input
                        type={resolveShowPassword ? 'text' : 'password'}
                        placeholder="Enter your password"
                        value={resolvePassword}
                        onChange={(e) => { setResolvePassword(e.target.value); if (resolvePasswordError) setResolvePasswordError(''); }}
                        required
                        style={{ width: '100%', padding: '12px 42px 12px 12px', borderRadius: '8px', border: resolvePasswordError ? '1px solid #ef4444' : '1px solid #e5e7eb', fontSize: '14px', color: '#374151', outline: 'none', boxSizing: 'border-box' }}
                      />
                      <button
                        type="button"
                        onClick={() => setResolveShowPassword(p => !p)}
                        style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', display: 'flex', alignItems: 'center' }}
                      >
                        {resolveShowPassword ? <FiEye size={16} /> : <FiEyeOff size={16} />}
                      </button>
                    </div>
                    {resolvePasswordError && (
                      <p style={{ color: '#ef4444', fontSize: '11px', marginTop: '4px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <FiAlertCircle size={11} /> {resolvePasswordError}
                      </p>
                    )}
                  </div>
                </form>
              )}

              {/* ── RESTORING SPINNER ── */}
              {resolveStep === 'restoring' && (
                <div style={{ padding: '24px', textAlign: 'center', color: '#6b7280' }}>
                  <FiRefreshCw size={24} style={{ animation: 'spin 1s linear infinite', marginBottom: '10px' }} />
                  <p style={{ margin: 0, fontSize: '13px', fontWeight: '500' }}>Retrieving backup and restoring record…</p>
                </div>
              )}

            </div>
            {/* ── END SCROLLABLE BODY ── */}

            {/* ── FOOTER ACTIONS ── */}
            <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: '16px', marginTop: '8px', display: 'flex', gap: '10px', justifyContent: 'flex-end', flexShrink: 0 }}>
              <button
                type="button"
                onClick={closeResolveModal}
                disabled={resolveStep === 'restoring' || resolveStep === 'resolving'}
                style={{ padding: '10px 20px', borderRadius: '10px', border: '1px solid #e2e8f0', background: '#fff', color: '#4b5563', fontWeight: 600, cursor: 'pointer', fontSize: '13px', opacity: (resolveStep === 'restoring' || resolveStep === 'resolving') ? 0.5 : 1 }}
              >
                Cancel
              </button>

              {/* Step 1 → Restore Original button */}
              {resolveStep === 'review' && (
                <button
                  type="submit"
                  form="restore-form"
                  disabled={isResolving || !resolvePassword}
                  style={{ padding: '10px 20px', borderRadius: '10px', border: 'none', background: 'linear-gradient(135deg, #7c3aed 0%, #5b21b6 100%)', color: '#fff', fontWeight: 700, cursor: (!resolvePassword || isResolving) ? 'not-allowed' : 'pointer', opacity: (!resolvePassword || isResolving) ? 0.6 : 1, fontSize: '13px', display: 'flex', alignItems: 'center', gap: '6px', boxShadow: '0 4px 12px rgba(124,58,237,0.35)' }}
                >
                  <FiShield size={14} />
                  Restore Original
                </button>
              )}

              {/* Step 3 → Resolve Alert (only when hash confirmed) */}
              {(resolveStep === 'restored' || resolveStep === 'resolving') && (
                <button
                  type="button"
                  onClick={handleResolveTamperAlert}
                  disabled={!resolveRestoreResult?.isValid || isResolving}
                  style={{ padding: '10px 20px', borderRadius: '10px', border: 'none', background: resolveRestoreResult?.isValid ? 'linear-gradient(135deg, #059669 0%, #047857 100%)' : '#9ca3af', color: '#fff', fontWeight: 700, cursor: (resolveRestoreResult?.isValid && !isResolving) ? 'pointer' : 'not-allowed', opacity: isResolving ? 0.7 : 1, fontSize: '13px', display: 'flex', alignItems: 'center', gap: '6px', boxShadow: resolveRestoreResult?.isValid ? '0 4px 12px rgba(5,150,105,0.35)' : 'none' }}
                  title={!resolveRestoreResult?.isValid ? 'Hash does not match — cannot resolve until record is verified' : ''}
                >
                  <FiCheckCircle size={14} />
                  {isResolving ? 'Resolving…' : 'Resolve Alert'}
                </button>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}

      <div className="content-library-header-custom" style={{
        background: 'linear-gradient(135deg, #3b82f6 0%, #1e40af 100%)',
        borderRadius: '20px',
        padding: '32px',
        marginBottom: '24px',
        boxShadow: '0 8px 32px rgba(59, 130, 246, 0.3)',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Administrator - Left Aligned */}
        <div style={{
          position: 'absolute',
          left: '32px',
          top: '50%',
          transform: 'translateY(-50%)'
        }}>
          <div style={{
            padding: '16px 32px',
            borderRadius: '50px',
            background: 'rgba(147, 197, 253, 0.2)',
            border: '1px solid rgba(147, 197, 253, 0.3)'
          }}>
            <h2 style={{
              margin: 0,
              fontSize: '28px',
              fontWeight: '700',
              background: 'linear-gradient(135deg, #60a5fa 0%, #3b82f6 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              color: 'transparent',
              letterSpacing: '0.02em',
              textShadow: 'none'
            }}>
              Administrator
            </h2>
          </div>
        </div>

        {/* Title and Subtitle */}
        <div style={{ textAlign: 'right' }}>
          <h2 style={{
            margin: 0,
            fontSize: '36px',
            fontWeight: '800',
            color: '#ffffff !important',
            background: 'none !important',
            WebkitBackgroundClip: 'unset !important',
            WebkitTextFillColor: '#ffffff !important',
            backgroundClip: 'unset !important',
            letterSpacing: '-0.02em',
            textShadow: '0 2px 8px rgba(0,0,0,0.2)'
          }}>
            Appointment Management
          </h2>
          <p style={{
            margin: '12px 0 0 0',
            fontSize: '18px',
            color: '#ffffff',
            fontWeight: '500',
            textShadow: '0 1px 4px rgba(0,0,0,0.15)'
          }}>
            Schedule and manage appointments across all services
          </p>
        </div>

        {/* CSS Animations */}
        <style>{`
          .content-library-header-custom h2 {
            color: #ffffff !important;
            background: none !important;
            -webkit-background-clip: unset !important;
            -webkit-text-fill-color: #ffffff !important;
            background-clip: unset !important;
          }
          .content-library-header-custom p {
            color: #ffffff !important;
          }
        `}</style>
      </div>

      {/* Tabs Row */}
      <div className="content-filter-tabs-wrapper" style={{ marginBottom: '-10px' }}>
        <div className="content-filter-tabs">
          <button
            className={`content-filter-tab ${activeTab === "appointments" ? "active" : ""}`}
            onClick={() => setActiveTab("appointments")}
          >
            Appointments
          </button>
          <button
            className={`content-filter-tab ${activeTab === "service-assignment" ? "active" : ""}`}
            onClick={() => setActiveTab("service-assignment")}
          >
            Service Assignment
          </button>
          <button
            className="refresh-btn"
            onClick={() => {
              setFollowUpAppointmentTarget(null);
              setShowCreateAppointmentModal(true);
            }}
            style={{ marginLeft: 'auto' }}
          >
            <FiPlus /> Create Appointment
          </button>
        </div>


      </div>

      
      {activeTab === "appointments" && (
        <>
          {/* ── Custom Tooltip for Action Buttons ── */}
          {tooltip.visible && (
            <div
              className={`admin-action-tooltip ${tooltip.type}`}
              style={{
                left: tooltip.x,
                top: tooltip.y
              }}
            >
              {tooltip.text}
            </div>
          )}
          {/* Task 1: Search Controls */}
          <div className="admin-aura-top-controls" style={{ marginTop: '10px', marginBottom: '8px' }}>
            <input
              className="admin-aura-search"
              placeholder="Search Patient, Professional, Service, Date..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            <input
              type="date"
              className="admin-aura-search"
              aria-label="Date from"
              value={dateFrom}
              onChange={e => {
                setDateFrom(e.target.value);
                setCurrentPage(1);
              }}
            />
            <span style={{ fontSize: '14px', color: '#6b7280', fontWeight: '500' }}>to</span>
            <input
              type="date"
              className="admin-aura-search"
              aria-label="Date to"
              value={dateTo}
              onChange={e => {
                setDateTo(e.target.value);
                setCurrentPage(1);
              }}
            />
            <select value={serviceFilter} onChange={e => setServiceFilter(e.target.value)}>
              <option value="">All Services</option>
              {SERVICE_FILTER_OPTIONS.map((service) => (
                <option key={service.value} value={service.value}>{service.label}</option>
              ))}
            </select>
            <button 
              className="admin-primary-gradient-btn"
              onClick={() => {
                setIsRefreshing(true);
                setRefreshKey(k => k + 1);
              }}
              disabled={isRefreshing}
              style={{ opacity: isRefreshing ? 0.6 : 1, cursor: isRefreshing ? 'not-allowed' : 'pointer' }}
            >
              <FiRefreshCw className={isRefreshing ? "spinning" : ""} /> {isRefreshing ? "Refreshing..." : "Refresh"}
            </button>
            <button 
              className="admin-primary-gradient-btn"
              onClick={() => setShowExportModal(true)}
            >
              <FiDownload /> Export Report
            </button>
          </div>

          {/* Status Filter Tabs with Pagination + Export PDF */}
          <div className="status-tabs-wrapper">
            <div className="status-tabs">
              <button
                className={`status-tab ${statusFilter === "all" ? "active" : ""}`}
                onClick={() => setStatusFilter("all")}
              >
                All
                <span className="tab-count">{appointments.length}</span>
              </button>
              <button
                className={`status-tab ${statusFilter === "PENDING" ? "active" : ""}`}
                onClick={() => setStatusFilter("PENDING")}
              >
                Pending
                <span className="tab-count">
                  {appointments.filter(apt => apt.status === "PENDING").length}
                </span>
              </button>
              <button
                className={`status-tab ${statusFilter === "CONFIRMED" ? "active" : ""}`}
                onClick={() => setStatusFilter("CONFIRMED")}
              >
                Confirmed
                <span className="tab-count">
                  {appointments.filter(apt => apt.status === "CONFIRMED").length}
                </span>
              </button>
              <button
                className={`status-tab ${statusFilter === "ACCEPTED" ? "active" : ""}`}
                onClick={() => setStatusFilter("ACCEPTED")}
              >
                Accepted
                <span className="tab-count">
                  {appointments.filter(apt => apt.status === "ACCEPTED").length}
                </span>
              </button>
              <button
                className={`status-tab ${statusFilter === "COMPLETED" ? "active" : ""}`}
                onClick={() => setStatusFilter("COMPLETED")}
              >
                Completed
                <span className="tab-count">
                  {appointments.filter(apt => apt.status === "COMPLETED").length}
                </span>
              </button>
              <button
                className={`status-tab ${statusFilter === "CANCELLED_BY_USER" ? "active" : ""}`}
                onClick={() => setStatusFilter("CANCELLED_BY_USER")}
              >
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <div>Cancelled by</div>
                  <div>User</div>
                </div>
                <span className="tab-count">
                  {appointments.filter(apt => apt.status === "CANCELLED_BY_USER").length}
                </span>
              </button>
              <button
                className={`status-tab ${statusFilter === "CANCELLED_BY_PRO" ? "active" : ""}`}
                onClick={() => setStatusFilter("CANCELLED_BY_PRO")}
              >
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <div>Cancelled by</div>
                  <div>Professional</div>
                </div>
                <span className="tab-count">
                  {appointments.filter(apt => apt.status === "CANCELLED_BY_PRO").length}
                </span>
              </button>
            </div>
            
            {/* Right Side: Pagination */}
            <div style={{ display: "flex", gap: "16px", alignItems: "center" }}>
              <div style={{ fontSize: "14px", color: "#6b7280" }}>
                {filteredAppointments.length > 0 ? (
                  `${startIndex + 1}-${Math.min(endIndex, filteredAppointments.length)} of ${filteredAppointments.length}`
                ) : (
                  '0 of 0'
                )}
              </div>
              <div style={{ display: "flex", gap: "8px" }}>
                <button 
                  onClick={handlePrevPage}
                  disabled={currentPage === 1}
                  style={{ 
                    backgroundColor: '#1e3a8a', 
                    color: 'white', 
                    border: 'none', 
                    padding: '8px 12px', 
                    borderRadius: '4px', 
                    cursor: 'pointer',
                    width: '32px'
                  }}
                >
                  ←
                </button>
                
                <button 
                  onClick={handleNextPage}
                  disabled={currentPage === totalPages || totalPages === 0}
                  style={{ 
                    backgroundColor: '#1e3a8a', 
                    color: 'white', 
                    border: 'none', 
                    padding: '8px 12px', 
                    borderRadius: '4px', 
                    cursor: 'pointer',
                    width: '32px'
                  }}
                >
                  →
                </button>
              </div>
            </div>
          </div>

          
          <div className="admin-aura-table-wrapper" style={{ marginTop: 10 }}>
            <table className="admin-aura-table">
              <thead>
                <tr>
                  {statusFilter === "all" && <th {...getSortHeaderProps("status", "Status")}>Status {getAppointmentSortIndicator(sortConfig, "status")}</th>}
                  <th {...getSortHeaderProps("patientName", "Patient Name")}>Patient {getAppointmentSortIndicator(sortConfig, "patientName")}</th>
                  <th {...getSortHeaderProps("reference", "Reference Number")}>Reference # {getAppointmentSortIndicator(sortConfig, "reference")}</th>
                  <th {...getSortHeaderProps("clinicName", "Clinic")}>Clinic {getAppointmentSortIndicator(sortConfig, "clinicName")}</th>
                  <th {...getSortHeaderProps("proName", "Medical Professional")}>Medical Pro {getAppointmentSortIndicator(sortConfig, "proName")}</th>
                  <th {...getSortHeaderProps("serviceName", "Service")}>Service {getAppointmentSortIndicator(sortConfig, "serviceName")}</th>
                  <th {...getSortHeaderProps("dateTime", "Appointment Date & Time")}>Date & Time {getAppointmentSortIndicator(sortConfig, "dateTime")}</th>
                  <th {...getSortHeaderProps("blockchainStatus", "Blockchain Status")}>Blockchain {getAppointmentSortIndicator(sortConfig, "blockchainStatus")}</th>
                  {(statusFilter === "CANCELLED_BY_USER" || statusFilter === "CANCELLED_BY_PRO") && <th {...getSortHeaderProps("cancelReason", "Cancellation Reason")}>Cancel Reason {getAppointmentSortIndicator(sortConfig, "cancelReason")}</th>}
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <>
                    {[...Array(10)].map((_, i) => (
                      <tr key={i} className="admin-aura-skeleton-row">
                        {statusFilter === "all" && <td><div className="admin-aura-skeleton admin-aura-skeleton-badge" /></td>}
                        <td><div className="admin-aura-skeleton admin-aura-skeleton-text" style={{ width: `${60 + (i % 3) * 15}%` }} /></td>
                        <td><div className="admin-aura-skeleton admin-aura-skeleton-text" style={{ width: '70%' }} /></td>
                        <td><div className="admin-aura-skeleton admin-aura-skeleton-text" style={{ width: `${55 + (i % 4) * 10}%` }} /></td>
                        <td><div className="admin-aura-skeleton admin-aura-skeleton-text" style={{ width: '65%' }} /></td>
                        <td><div className="admin-aura-skeleton admin-aura-skeleton-text" style={{ width: `${50 + (i % 3) * 15}%` }} /></td>
                        <td><div className="admin-aura-skeleton admin-aura-skeleton-text" style={{ width: '60%' }} /></td>
                        <td><div className="admin-aura-skeleton admin-aura-skeleton-badge" /></td>
                        {(statusFilter === "CANCELLED_BY_USER" || statusFilter === "CANCELLED_BY_PRO") && <td><div className="admin-aura-skeleton admin-aura-skeleton-text" style={{ width: `${55 + (i % 3) * 15}%` }} /></td>}
                        <td><div className="admin-aura-skeleton admin-aura-skeleton-actions" /></td>
                      </tr>
                    ))}
                  </>
                ) : paginatedAppointments.length === 0 ? (
                  <tr>
                    <td colSpan={statusFilter === "all" ? 9 : (statusFilter === "CANCELLED_BY_USER" || statusFilter === "CANCELLED_BY_PRO") ? 9 : 8} style={{ textAlign: "center", padding: 20 }}>No appointments found.</td>
                  </tr>
                ) : (
                  paginatedAppointments.map((r) => (
                      <tr key={r.id}>
                        {statusFilter === "all" && (
                          <td>
                            <span className={`ma-badge ma-badge-${r.status}`}>
                              {getStatusLabel(r.status)}
                            </span>
                          </td>
                        )}
                        <td>
                          <div>{r.patientName}</div>
                          {r.uid && patientAssistanceData[r.uid]?.reasonForAssistance?.length > 0 && (
                            <div style={{ marginTop: '4px', display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                              {patientAssistanceData[r.uid].reasonForAssistance.map((reason, idx) => (
                                <span
                                  key={idx}
                                  style={{
                                    fontSize: '10px',
                                    padding: '2px 6px',
                                    borderRadius: '4px',
                                    fontWeight: '500',
                                    textTransform: 'capitalize',
                                    backgroundColor: getAssistanceBadgeColor(reason),
                                    color: getAssistanceBadgeTextColor(reason)
                                  }}
                                >
                                  {reason}
                                </span>
                              ))}
                            </div>
                          )}
                        </td>
                        <td style={{ fontSize: '13px', fontWeight: '500', color: '#374151' }}>{r.reference || "—"}</td>
                        <td>{r.clinicName || r.clinic || "—"}</td>
                        <td>{r.proName}</td>
                        <td dangerouslySetInnerHTML={{ __html: r.serviceName }} />
                        <td>
                          <div style={{ display: "flex", flexDirection: "column", gap: "2px", lineHeight: 1.3 }}>
                            <span>{r.date || "—"}</span>
                            <span style={{ color: "#64748b", fontSize: "13px" }}>{r.time || "—"}</span>
                          </div>
                        </td>
                        <td style={{ width: 'fit-content', minWidth: 'auto' }}>
                          {r.blockchain?.status ? (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                              <span 
                                style={{
                                  fontSize: '11px',
                                  padding: '4px 8px',
                                  borderRadius: '999px',
                                  fontWeight: '600',
                                  textTransform: 'uppercase',
                                  letterSpacing: '0.05em',
                                  cursor: r.blockchain.status === 'verified' ? 'pointer' : 'default',
                                  ...(r.blockchain.status === 'verified' ? {
                                    background: '#ecfdf5',
                                    color: '#059669',
                                    border: '1px solid #10b981'
                                  } : r.blockchain.status === 'pending' ? {
                                    background: '#fef3c7',
                                    color: '#d97706',
                                    border: '1px solid #f59e0b'
                                  } : {
                                    background: '#f3f4f6',
                                    color: '#6b7280',
                                    border: '1px solid #9ca3af'
                                  })
                                }}
                                onClick={() => {
                                  if (r.blockchain.status === 'verified') {
                                    setBlockchainViewing(r);
                                    setShowBlockchainModal(true);
                                  }
                                }}
                                title={r.blockchain.status === 'verified' ? 'Click to view blockchain details' : ''}
                              >
                                {r.blockchain.status === 'verified' && <FiShield size={10} style={{ marginRight: '4px', verticalAlign: 'middle' }} />}
                                {r.blockchain.status}
                              </span>
                              {tamperAlerts.some(alert => alert.appointmentId === r.id && alert.status === 'active') && (
                                <span style={{
                                  fontSize: '10px',
                                  padding: '2px 6px',
                                  borderRadius: '4px',
                                  fontWeight: '600',
                                  background: '#fef2f2',
                                  color: '#dc2626',
                                  border: '1px solid #fecaca'
                                }}>
                                  Tampered - Need Attending
                                </span>
                              )}
                            </div>
                          ) : (
                            <span style={{ fontSize: '11px', color: '#9ca3af', fontStyle: 'italic' }}>None</span>
                          )}
                        </td>
                        {(statusFilter === "CANCELLED_BY_USER" || statusFilter === "CANCELLED_BY_PRO") && (
                          <td style={{ maxWidth: "220px", whiteSpace: "normal", wordBreak: "break-word" }}>
                            {r.cancelReason || r.reason || r.cancellationReason || "—"}
                          </td>
                        )}
                        <td style={{ textAlign: "right" }}>
                          <div className="ma-row-actions" style={{ justifyContent: "flex-end" }}>
                            {r.status === "PENDING" && (
                              <>
                                <button
                                  type="button"
                                  className="ma-action-btn approve"
                                  aria-label="Approve Appointment"
                                  onMouseEnter={(e) => { e.target.style.background = '#d1fae5'; handleActionMouseEnter(e, "Approve Appointment", 'approve'); }}
                                  onMouseMove={(e) => { handleActionMouseMove(e); }}
                                  onMouseLeave={(e) => { e.target.style.background = '#ecfdf5'; handleActionMouseLeave(); }}
                                  style={{ background: '#ecfdf5', color: '#059669', borderColor: '#10b981' }}
                                  onClick={async () => {
                                    try {
                                      const currentUser = auth.currentUser;
                                      const additionalData = {};
                                      if (currentUser) {
                                        additionalData.proId = null;
                                        additionalData.proName = null;
                                        additionalData.auditApprovedId = currentUser.uid;
                                      }
                                      await appointmentService.updateStatus(r.id, "CONFIRMED", additionalData);
                                      setNotification({ message: "Appointment approved.", type: "success" });
                                    } catch (err) {
                                      console.error("Admin approve failed:", err);
                                      setNotification({ message: "Failed to approve appointment.", type: "error" });
                                    }
                                  }}
                                >
                                  <FiCheckCircle size={16} />
                                </button>

                                <button
                                  type="button"
                                  className="ma-action-btn reschedule"
                                  aria-label="Reschedule Appointment"
                                  onMouseEnter={(e) => { e.target.style.background = '#fde68a'; handleActionMouseEnter(e, "Reschedule Appointment", 'info'); }}
                                  onMouseMove={(e) => { handleActionMouseMove(e); }}
                                  onMouseLeave={(e) => { e.target.style.background = '#fef3c7'; handleActionMouseLeave(); }}
                                  style={{ background: '#fef3c7', color: '#d97706', borderColor: '#f59e0b' }}
                                  onClick={() => setAdminRescheduleTarget(r)}
                                >
                                  <FiClock size={16} />
                                </button>

                                <button
                                  type="button"
                                  className="ma-action-btn cancel"
                                  aria-label="Cancel Appointment"
                                  onMouseEnter={(e) => { e.target.style.background = '#fecaca'; handleActionMouseEnter(e, "Cancel Appointment", 'delete'); }}
                                  onMouseMove={(e) => { handleActionMouseMove(e); }}
                                  onMouseLeave={(e) => { e.target.style.background = '#fee2e2'; handleActionMouseLeave(); }}
                                  style={{ background: '#fee2e2', color: '#dc2626', borderColor: '#ef4444' }}
                                  onClick={() => setAdminCancelTarget(r)}
                                >
                                  <FiX size={16} />
                                </button>
                              </>
                            )}
                            {r.status === "CONFIRMED" && (
                              <>
                                <button
                                  type="button"
                                  className="ma-action-btn approve"
                                  aria-label="Accept Appointment"
                                  onMouseEnter={(e) => { e.target.style.background = '#d1fae5'; handleActionMouseEnter(e, "Accept Appointment", 'approve'); }}
                                  onMouseMove={(e) => { handleActionMouseMove(e); }}
                                  onMouseLeave={(e) => { e.target.style.background = '#ecfdf5'; handleActionMouseLeave(); }}
                                  style={{ background: '#ecfdf5', color: '#059669', borderColor: '#10b981' }}
                                  onClick={() => handleAdminAccept(r)}
                                >
                                  <FiCheckCircle size={16} />
                                </button>

                                <button
                                  type="button"
                                  className="ma-action-btn reschedule"
                                  aria-label="Reschedule Appointment"
                                  onMouseEnter={(e) => { e.target.style.background = '#fde68a'; handleActionMouseEnter(e, "Reschedule Appointment", 'info'); }}
                                  onMouseMove={(e) => { handleActionMouseMove(e); }}
                                  onMouseLeave={(e) => { e.target.style.background = '#fef3c7'; handleActionMouseLeave(); }}
                                  style={{ background: '#fef3c7', color: '#d97706', borderColor: '#f59e0b' }}
                                  onClick={() => setAdminRescheduleTarget(r)}
                                >
                                  <FiClock size={16} />
                                </button>

                                <button
                                  type="button"
                                  className="ma-action-btn cancel"
                                  aria-label="Cancel Appointment"
                                  onMouseEnter={(e) => { e.target.style.background = '#fecaca'; handleActionMouseEnter(e, "Cancel Appointment", 'delete'); }}
                                  onMouseMove={(e) => { handleActionMouseMove(e); }}
                                  onMouseLeave={(e) => { e.target.style.background = '#fee2e2'; handleActionMouseLeave(); }}
                                  style={{ background: '#fee2e2', color: '#dc2626', borderColor: '#ef4444' }}
                                  onClick={() => setAdminCancelTarget(r)}
                                >
                                  <FiX size={16} />
                                </button>
                              </>
                            )}
                            {r.status === "CANCELLING" && (
                              <button
                                type="button"
                                className="ma-action-btn cancel"
                                aria-label="Cancel Appointment"
                                onMouseEnter={(e) => { e.target.style.background = '#fecaca'; handleActionMouseEnter(e, "Cancel Appointment", 'delete'); }}
                                onMouseMove={(e) => { handleActionMouseMove(e); }}
                                onMouseLeave={(e) => { e.target.style.background = '#fee2e2'; handleActionMouseLeave(); }}
                                style={{ background: '#fee2e2', color: '#dc2626', borderColor: '#ef4444' }}
                                onClick={() => setAdminCancelTarget(r)}
                              >
                                <FiX size={16} />
                              </button>
                            )}
                            {r.status === "ACCEPTED" && adminProfile?.accountType === "Admin" && r.proId === auth.currentUser?.uid && (
                              <>
                                <button
                                  type="button"
                                  className="ma-action-btn approve"
                                  aria-label="View Data"
                                  onMouseEnter={(e) => { e.target.style.background = '#bfdbfe'; handleActionMouseEnter(e, "View Data", 'view'); }}
                                  onMouseMove={(e) => { handleActionMouseMove(e); }}
                                  onMouseLeave={(e) => { e.target.style.background = '#dbeafe'; handleActionMouseLeave(); }}
                                  style={{ background: '#dbeafe', color: '#1d4ed8', borderColor: '#3b82f6' }}
                                  onClick={() => setAdminViewDataTarget(r)}
                                >
                                  <FiEye size={16} />
                                </button>
                                <button
                                  type="button"
                                  className="ma-action-btn complete"
                                  aria-label="Complete Appointment"
                                  onMouseEnter={(e) => { e.target.style.background = '#d1fae5'; handleActionMouseEnter(e, "Complete Appointment", 'approve'); }}
                                  onMouseMove={(e) => { handleActionMouseMove(e); }}
                                  onMouseLeave={(e) => { e.target.style.background = '#ecfdf5'; handleActionMouseLeave(); }}
                                  style={{ background: '#ecfdf5', color: '#059669', borderColor: '#10b981' }}
                                  onClick={() => handleAdminComplete(r)}
                                >
                                  <FiCheckSquare size={16} />
                                </button>
                                <button
                                  type="button"
                                  className="ma-action-btn reschedule"
                                  aria-label="Attachments"
                                  onMouseEnter={(e) => { e.target.style.background = '#fde68a'; handleActionMouseEnter(e, "Attachments", 'reschedule'); }}
                                  onMouseMove={(e) => { handleActionMouseMove(e); }}
                                  onMouseLeave={(e) => { e.target.style.background = '#fef3c7'; handleActionMouseLeave(); }}
                                  style={{ background: '#fef3c7', color: '#d97706', borderColor: '#f59e0b' }}
                                  onClick={() => setAdminAttachmentsTarget(r)}
                                >
                                  <FiPaperclip size={16} />
                                </button>
                                <button
                                  type="button"
                                  className="ma-action-btn cancel"
                                  aria-label="Cancel Appointment"
                                  onMouseEnter={(e) => { e.target.style.background = '#fecaca'; handleActionMouseEnter(e, "Cancel Appointment", 'delete'); }}
                                  onMouseMove={(e) => { handleActionMouseMove(e); }}
                                  onMouseLeave={(e) => { e.target.style.background = '#fee2e2'; handleActionMouseLeave(); }}
                                  style={{ background: '#fee2e2', color: '#dc2626', borderColor: '#ef4444' }}
                                  onClick={() => setAdminCancelTarget(r)}
                                >
                                  <FiX size={16} />
                                </button>
                              </>
                            )}
                            {r.status === "COMPLETED" && (
                              <>
                                <button
                                  type="button"
                                  className="ma-action-btn complete"
                                  aria-label="Add Follow-Up Appointment"
                                  title="Add Follow-Up Appointment"
                                  onMouseEnter={(e) => { e.target.style.background = '#dbeafe'; handleActionMouseEnter(e, "Add Follow-Up Appointment", 'approve'); }}
                                  onMouseMove={(e) => { handleActionMouseMove(e); }}
                                  onMouseLeave={(e) => { e.target.style.background = '#eff6ff'; handleActionMouseLeave(); }}
                                  style={{ background: '#eff6ff', color: '#1d4ed8', borderColor: '#3b82f6' }}
                                  onClick={() => {
                                    setFollowUpAppointmentTarget(r);
                                    setShowCreateAppointmentModal(true);
                                  }}
                                >
                                  <FiPlus size={16} />
                                </button>
                                {tamperAlerts.some(alert => alert.appointmentId === r.id && alert.status === 'active') && (
                                  <button
                                    type="button"
                                    className="ma-action-btn"
                                    aria-label="Resolve Tamper Alert"
                                    onMouseEnter={(e) => { e.target.style.background = '#fecaca'; handleActionMouseEnter(e, "Resolve Tamper Alert", 'approve'); }}
                                    onMouseMove={(e) => { handleActionMouseMove(e); }}
                                    onMouseLeave={(e) => { e.target.style.background = '#fef2f2'; handleActionMouseLeave(); }}
                                    style={{ background: '#fef2f2', color: '#dc2626', borderColor: '#ef4444' }}
                                    onClick={() => {
                                      openResolveModal(r);
                                    }}
                                  >
                                    <FiShield size={16} />
                                  </button>
                                )}
                                {/* View Attachments button for completed appointments */}
                                <button
                                  type="button"
                                  className="ma-action-btn reschedule"
                                  aria-label="View Attachments"
                                  onMouseEnter={(e) => { e.target.style.background = '#fde68a'; handleActionMouseEnter(e, "View Attachments", 'reschedule'); }}
                                  onMouseMove={(e) => { handleActionMouseMove(e); }}
                                  onMouseLeave={(e) => { e.target.style.background = '#fef3c7'; handleActionMouseLeave(); }}
                                  style={{ background: '#fef3c7', color: '#d97706', borderColor: '#f59e0b' }}
                                  onClick={() => setAdminAttachmentsTarget(r)}
                                >
                                  <FiPaperclip size={16} />
                                </button>
                                {/* Download Slip button for completed appointments */}
                                <button
                                  type="button"
                                  className="ma-action-btn download"
                                  aria-label="Download Slip"
                                  onMouseEnter={(e) => { e.target.style.background = '#e5e7eb'; handleActionMouseEnter(e, "Download Slip", 'gray'); }}
                                  onMouseMove={(e) => { handleActionMouseMove(e); }}
                                  onMouseLeave={(e) => { e.target.style.background = '#f3f4f6'; handleActionMouseLeave(); }}
                                  style={{ background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: '6px', cursor: 'pointer', color: '#6b7280', transition: 'all 0.2s', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '8px' }}
                                  onClick={() => handleAdminDownloadSlip(r)}
                                >
                                  <FiDownload size={16} />
                                </button>
                              </>
                            )}
                            {(r.status === "CANCELLED_BY_USER" || r.status === "CANCELLED_BY_PRO") && (
                              <button
                                type="button"
                                className="ma-action-btn download"
                                aria-label="Download Slip"
                                onMouseEnter={(e) => { e.target.style.background = '#e5e7eb'; handleActionMouseEnter(e, "Download Slip", 'info'); }}
                                onMouseMove={(e) => { handleActionMouseMove(e); }}
                                onMouseLeave={(e) => { e.target.style.background = '#f3f4f6'; handleActionMouseLeave(); }}
                                style={{ background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: '6px', cursor: 'pointer', color: '#6b7280', transition: 'all 0.2s', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '8px' }}
                                onClick={() => handleAdminDownloadSlip(r)}
                              >
                                <FiDownload size={16} />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                )}
                  </tbody>
                </table>
          </div>
        </>
      )}

      {activeTab === "records" && (
        <AdminRecords
          appointments={appointments}
          staffProfile={adminProfile}
          refreshAppointments={() => {
            setIsRefreshing(true);
            setRefreshKey(k => k + 1);
          }}
          sidebarCollapsed={false}
        />
      )}

      {activeTab === "service-assignment" && (
        <>
          {/* Search Controls */}
          <div className="admin-aura-top-controls" style={{ marginTop: '10px', marginBottom: '8px' }}>
            <input
              className="admin-aura-search"
              placeholder="Search Staff Name, Role, Staff ID, UID..."
              value={staffSearch}
              onChange={e => setStaffSearch(e.target.value)}
            />
            <select value={staffServiceFilter} onChange={e => setStaffServiceFilter(e.target.value)}>
              <option value="">All Services</option>
              {SERVICE_FILTER_OPTIONS.map((service) => (
                <option key={service.value} value={service.value}>{service.label}</option>
              ))}
            </select>
            <button 
              className="admin-primary-gradient-btn"
              onClick={() => {
                setIsRefreshing(true);
                setRefreshKey(k => k + 1);
              }}
              disabled={isRefreshing}
              style={{ opacity: isRefreshing ? 0.6 : 1, cursor: isRefreshing ? 'not-allowed' : 'pointer' }}
            >
              <FiRefreshCw className={isRefreshing ? "spinning" : ""} /> {isRefreshing ? "Refreshing..." : "Refresh"}
            </button>
            <button 
              className="refresh-btn"
              onClick={() => setShowAssignModal(true)}
            >
              <FiPlus /> Assign Services
            </button>
          </div>

          {/* Pagination Row */}
          <div style={{ marginTop: '8px', display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
            <div style={{ display: "flex", gap: "16px", alignItems: "center" }}>
              <div style={{ fontSize: "14px", color: "#6b7280" }}>
                {filteredAssignedStaff.length > 0 ? (
                  `${staffStartIndex + 1}-${Math.min(staffEndIndex, filteredAssignedStaff.length)} of ${filteredAssignedStaff.length}`
                ) : (
                  '0 of 0'
                )}
              </div>
              <div style={{ display: "flex", gap: "8px" }}>
                <button 
                  onClick={handleStaffPrevPage}
                  disabled={staffCurrentPage === 1}
                  style={{ 
                    backgroundColor: '#1e3a8a', 
                    color: 'white', 
                    border: 'none', 
                    padding: '8px 12px', 
                    borderRadius: '4px', 
                    cursor: 'pointer',
                    width: '32px'
                  }}
                >
                  ←
                </button>
                
                <button 
                  onClick={handleStaffNextPage}
                  disabled={staffCurrentPage === staffTotalPages || staffTotalPages === 0}
                  style={{ 
                    backgroundColor: '#1e3a8a', 
                    color: 'white', 
                    border: 'none', 
                    padding: '8px 12px', 
                    borderRadius: '4px', 
                    cursor: 'pointer',
                    width: '32px'
                  }}
                >
                  →
                </button>
              </div>
            </div>
          </div>

          {/* Service Assignment Table */}
          <div className="admin-aura-table-wrapper" style={{ marginTop: 20 }}>
            {isStaffLoading ? (
              <p style={{ padding: 20 }}>Loading assigned services...</p>
            ) : (
              <table className="admin-aura-table">
                <thead>
                  <tr>
                    <th>Full Name</th>
                    <th>Role</th>
                    <th>Assigned Service</th>
                    <th>Staff ID</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedAssignedStaff.map((staff) => (
                    <tr key={staff.uid}>
                      <td>{staff.fullName}</td>
                      <td>{staff.role}</td>
                      <td>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                          {Array.isArray(staff.assignServices) ? (
                            staff.assignServices.map((s, idx) => (
                              <span key={idx} style={{ 
                                background: '#eff6ff', 
                                color: '#294a9c', 
                                padding: '4px 10px', 
                                borderRadius: '999px', 
                                fontSize: '11px',
                                fontWeight: '500',
                                border: '1px solid #dbeafe',
                                whiteSpace: 'nowrap'
                              }}>
                                {s}
                              </span>
                            ))
                          ) : (
                            <span style={{ 
                              background: '#eff6ff', 
                              color: '#294a9c', 
                              padding: '4px 10px', 
                              borderRadius: '999px', 
                              fontSize: '11px',
                              fontWeight: '500',
                              border: '1px solid #dbeafe'
                            }}>
                              {staff.assignServices}
                            </span>
                          )}
                        </div>
                      </td>
                      <td>{staff.staffId || 'N/A'}</td>
                    </tr>
                  ))}
                  {paginatedAssignedStaff.length === 0 && (
                    <tr>
                      <td colSpan="4" style={{ textAlign: "center", padding: 20 }}>No services assigned to staff yet.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}

      {/* Task 2 & 3: Assign Services Modal */}
       <AssignServicesModal 
         isOpen={showAssignModal} 
         onClose={() => setShowAssignModal(false)} 
         onAssign={handleAssignService}
       />

      {/* Export Modal */}
      <ExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        onExport={handleExport}
        isExporting={isExporting}
        activeTab={activeTab}
      />
     </div>
  );
}

/* ======================================================
   ACCOUNT MANAGEMENT VIEW
====================================================== */
function AccountManagement() {
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [activeTab, setActiveTab] = useState("all"); // "all", "users", "staff"
  const [panelOpen, setPanelOpen] = useState(false);
  const [panelMode, setPanelMode] = useState("view");
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [roleError, setRoleError] = useState("");
  const [licenseError, setLicenseError] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [showCreateStaff, setShowCreateStaff] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const itemsPerPage = 10;

  const API_BASE =
    process.env.NODE_ENV === "development"
      ? "http://localhost:5030/auravit-bb843/us-central1"
      : "https://us-central1-auravit-bb843.cloudfunctions.net";
  
  const roleDropdownRef = useRef(null);

  // Multi-step deactivation states
  const [deactivationStep, setDeactivationStep] = useState(0); // 0: idle, 1: confirmation, 2: reason, 3: password
  const [deactivationAccount, setDeactivationAccount] = useState(null);
  const [deactivationReason, setDeactivationReason] = useState("");
  const [adminPassword, setAdminPassword] = useState("");
  const [deactivationError, setDeactivationError] = useState("");
  const [isDeactivating, setIsDeactivating] = useState(false);

  // Multi-step reactivation states
  const [reactivationStep, setReactivationStep] = useState(0); // 0: idle, 1: confirmation, 2: reason, 3: password
  const [reactivationAccount, setReactivationAccount] = useState(null);
  const [reactivationReason, setReactivationReason] = useState("");
  const [reactivationPassword, setReactivationPassword] = useState("");
  const [reactivationError, setReactivationError] = useState("");
  const [isReactivating, setIsReactivating] = useState(false);

  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(false);

  // Email change states
  const [emailDraft, setEmailDraft] = useState("");
  const [emailError, setEmailError] = useState("");
  const [showEmailPasswordModal, setShowEmailPasswordModal] = useState(false);
  const [showEmailOtpModal, setShowEmailOtpModal] = useState(false);
  const [emailOtpId, setEmailOtpId] = useState("");
  const [emailOtpTimeLeft, setEmailOtpTimeLeft] = useState(60);
  const [emailOtpExpired, setEmailOtpExpired] = useState(false);
  const [emailOtpDigits, setEmailOtpDigits] = useState(Array(6).fill(""));
  const [emailOtpError, setEmailOtpError] = useState("");
  const [emailOtpLoading, setEmailOtpLoading] = useState(false);
  const [emailOtpVerifying, setEmailOtpVerifying] = useState(false);
  const [emailOtpTimerActive, setEmailOtpTimerActive] = useState(false);
  const emailOtpInputsRef = useRef([]);
  // "Try another way" states
  const [emailOtpMethod, setEmailOtpMethod] = useState("email"); // "email" | "sms"
  const [emailOtpDeliveryTarget, setEmailOtpDeliveryTarget] = useState("");
  const [emailOtpHasSmsBeenSent, setEmailOtpHasSmsBeenSent] = useState(false);
  const [emailOtpSwitchedFromSms, setEmailOtpSwitchedFromSms] = useState(false);
  const [adminContactNumber, setAdminContactNumber] = useState(""); // admin's phone for SMS

  // Tooltip state
  const [tooltip, setTooltip] = useState({ visible: false, text: '', x: 0, y: 0, type: 'default' });

  // Action button tooltip handlers
  const handleActionMouseEnter = (e, text, type = 'default') => {
    setTooltip({
      visible: true,
      text: text,
      x: e.clientX - 150,
      y: e.clientY,
      type: type
    });
  };

  const handleActionMouseMove = (e) => {
    if (tooltip.visible) {
      setTooltip(prev => ({
        ...prev,
        x: e.clientX - 150,
        y: e.clientY
      }));
    }
  };

  const handleActionMouseLeave = () => {
    setTooltip({ visible: false, text: '', x: 0, y: 0, type: 'default' });
  };

  // Export handlers
  const handleExport = async (format) => {
    setIsExporting(true);
    try {
      const reportDate = new Date().toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });

      switch (format) {
        case 'pdf':
          await exportToPDF(activeTab, reportDate);
          break;
        case 'excel':
          await exportToExcel(activeTab, reportDate);
          break;
        case 'csv':
          await exportToCSV(activeTab, reportDate);
          break;
        default:
          throw new Error('Invalid export format');
      }
      setShowExportModal(false);
    } catch (error) {
      console.error('Export failed:', error);
      alert('Export failed. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  const exportToPDF = async (tab, reportDate) => {
    const pdf = createPDF({
      title: "Accounts Report"
    });
    
    // Prepare data for table
    let headers = [];
    if (tab === "users") {
      headers = ['Unique Identifier Code', 'Email', 'Contact', 'Date Created', 'Last Login', 'Status'];
    } else if (tab === "staff") {
      headers = ['System ID', 'Role', 'Email', 'Contact', 'Date Created', 'Last Login', 'Status'];
    } else {
      headers = ['System ID/UIC', 'Email', 'Contact', 'Date Created', 'Last Login', 'Status'];
    }
    
    const rows = filteredAccounts.map(acc => {
      const row = [];
      if (tab === "all" || tab === "users") {
        row.push(acc.id);
      } else {
        row.push(acc.id);
        row.push(Array.isArray(acc.roles) ? acc.roles.join(', ') : (acc.role || ''));
      }
      row.push(acc.email);
      row.push(acc.contact);
      row.push(acc.created);
      row.push(acc.lastLogin);
      row.push(acc.status);
      return row;
    });
    
    pdf.autoTable({
      head: [headers],
      body: rows
    });
    
    pdf.save(`accounts-report-${new Date().toISOString().split('T')[0]}.pdf`);
  };

  const exportToExcel = async (tab, reportDate) => {
    const workbook = XLSX.utils.book_new();
    
    const summaryData = [
      ['ACCOUNTS REPORT'],
      ['Report Date', reportDate],
      ['Active Tab', tab.charAt(0).toUpperCase() + tab.slice(1)],
      [],
      ['ACCOUNT SUMMARY'],
      ['Total Accounts', accounts.length],
      ['Active', accounts.filter(acc => acc.status === 'Active').length],
      ['Inactive', accounts.filter(acc => acc.status === 'Inactive').length],
      ['Deactivated', accounts.filter(acc => acc.status === 'Deactivated').length],
    ];
    const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);
    XLSX.utils.book_append_sheet(workbook, summarySheet, 'Summary');
    
    // Account details
    let headers = [];
    if (tab === "users") {
      headers = ['Unique Identifier Code', 'Email', 'Contact', 'Date Created', 'Last Login', 'Status'];
    } else if (tab === "staff") {
      headers = ['System ID', 'Role', 'Email', 'Contact', 'Date Created', 'Last Login', 'Status'];
    } else {
      headers = ['System ID/UIC', 'Email', 'Contact', 'Date Created', 'Last Login', 'Status'];
    }
    
    const accountData = [
      ['ACCOUNT DETAILS'],
      ...[headers],
      ...filteredAccounts.map(acc => {
        const row = [];
        if (tab === "all" || tab === "users") {
          row.push(acc.id);
        } else {
          row.push(acc.id);
          row.push(Array.isArray(acc.roles) ? acc.roles.join(', ') : (acc.role || ''));
        }
        row.push(acc.email);
        row.push(acc.contact);
        row.push(acc.created);
        row.push(acc.lastLogin);
        row.push(acc.status);
        return row;
      })
    ];
    const accountSheet = XLSX.utils.aoa_to_sheet(accountData);
    XLSX.utils.book_append_sheet(workbook, accountSheet, 'Accounts');
    
    XLSX.writeFile(workbook, `accounts-report-${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  const exportToCSV = async (tab, reportDate) => {
    let csvData = [
      ['ACCOUNTS REPORT'],
      ['Report Date', reportDate],
      ['Active Tab', tab.charAt(0).toUpperCase() + tab.slice(1)],
      [],
      ['ACCOUNT SUMMARY'],
      ['Total Accounts', accounts.length],
      ['Active', accounts.filter(acc => acc.status === 'Active').length],
      ['Inactive', accounts.filter(acc => acc.status === 'Inactive').length],
      ['Deactivated', accounts.filter(acc => acc.status === 'Deactivated').length],
      [],
      ['ACCOUNT DETAILS'],
    ];
    
    let headers = [];
    if (tab === "users") {
      headers = ['Unique Identifier Code', 'Email', 'Contact', 'Date Created', 'Last Login', 'Status'];
    } else if (tab === "staff") {
      headers = ['System ID', 'Role', 'Email', 'Contact', 'Date Created', 'Last Login', 'Status'];
    } else {
      headers = ['System ID/UIC', 'Email', 'Contact', 'Date Created', 'Last Login', 'Status'];
    }
    
    csvData.push(headers);
    csvData.push(...filteredAccounts.map(acc => {
      const row = [];
      if (tab === "all" || tab === "users") {
        row.push(acc.id);
      } else {
        row.push(acc.id);
        row.push(Array.isArray(acc.roles) ? acc.roles.join(', ') : (acc.role || ''));
      }
      row.push(acc.email);
      row.push(acc.contact);
      row.push(acc.created);
      row.push(acc.lastLogin);
      row.push(acc.status);
      return row;
    }));
    
    const csvContent = csvData.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `accounts-report-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };
  
  // Helper function to get badge color based on assistance reason
  const getAssistanceBadgeColor = (reason) => {
    const colors = {
      minor: '#dbeafe',
      senior: '#fef3c7',
      pwd: '#d1fae5',
      nrnw: '#fce7f3',
      other: '#e5e7eb'
    };
    return colors[reason?.toLowerCase()] || colors.other;
  };

  const getAssistanceBadgeTextColor = (reason) => {
    const colors = {
      minor: '#1e40af',
      senior: '#92400e',
      pwd: '#065f46',
      nrnw: '#9d174d',
      other: '#374151'
    };
    return colors[reason?.toLowerCase()] || colors.other;
  };

  // Dedicated refresh function - OPTIMIZED
  const refreshAccounts = async () => {
    try {
      setLoading(true);
      const snap = await getDocs(query(collection(db, "users"), limit(1000))); // Add limit to prevent reading all users
      const mapped = snap.docs.map((d) => {
        const u = d.data() || {};
        const email = u.email || "";
        const phone = u.phone || "";
        const createdAt = u.createdAt?.toDate?.() || (u.createdAt ? new Date(u.createdAt) : null);
        const lastLogin = u.lastLogin?.toDate?.() || (u.lastLogin ? new Date(u.lastLogin) : null);
        const accountType =
          u.accountType ||
          (email.toLowerCase().endsWith("@staff.auravit.com")
            ? "Staff"
            : email.toLowerCase().endsWith("@admin.auravit.com")
              ? "Admin"
              : "User");

        const roles = Array.isArray(u.roles) && u.roles.length ? u.roles : (u.role ? [u.role] : []);
        // For user accounts, system ID is the Firestore document ID internally
        const uic = u.accountSetup?.uniqueIdentifierCode || u.uniqueIdentifierCode;
        // For admin display, show UIC for users, staffId for staff, adminId for admins
        const displayId = accountType === "User" ? (uic || "N/A") :
                          accountType === "Admin" ? (u.adminId || d.id) :
                          (u.staffId || u.systemId || d.id);

        const isDeactivated = u.deactivated === true || u.status === "deactivated";

        // Assistance information for patients - check multiple possible locations
        const assistanceRequired = u.assistance?.assistanceRequired || u.assistanceRequired || u.accountSetup?.assistanceRequired || "";
        const reasonForAssistance = u.assistance?.reasonForAssistance || u.reasonForAssistance || u.accountSetup?.reasonForAssistance || [];
        const reasonsArray = Array.isArray(reasonForAssistance) ? reasonForAssistance : (reasonForAssistance ? [reasonForAssistance] : []);

        return {
          id: displayId,
          uid: d.id,
          type: accountType,
          adminId: u.adminId || "",
          staffId: u.staffId || "",
          role: roles[0] || "",
          roles,
          firstName: u.firstName || "",
          lastName: u.lastName || "",
          fullName: u.fullName || `${u.firstName || ""} ${u.lastName || ""}`.trim() || uic || "User",
          email,
          contact: phone ? `+63${phone.replace(/\D/g, "").slice(-10)}` : "",
          createdAt: createdAt, // Store original date for sorting
          created: createdAt
            ? createdAt.toLocaleDateString("en-US", { month: "short", day: "2-digit", year: "numeric" })
            : "",
          lastLogin: lastLogin
            ? lastLogin.toLocaleDateString("en-US", { month: "short", day: "2-digit", year: "numeric" })
            : "-",
          status: isDeactivated ? "Deactivated" : "Active",
          deactivationReason: u.deactivationReason || "",
          license: u.medicalLicenseNumber || "",
          accountCreatorSystemId: u.accountCreatorSystemId || "",
          originalSystemId: u.systemId || d.id,
          uniqueIdentifierCode: uic || "",
          assistanceRequired,
          reasonForAssistance: reasonsArray,
        };
      });
      // Sort by createdAt descending (latest first)
      mapped.sort((a, b) => {
        if (!a.createdAt) return 1;
        if (!b.createdAt) return -1;
        return b.createdAt - a.createdAt;
      });
      setAccounts(mapped);
    } catch (error) {
      console.error("AccountManagement: Error refreshing accounts:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refreshAccounts();
    const refreshHandler = () => refreshAccounts();
    window.addEventListener('admin-accounts-refresh', refreshHandler);
    return () => window.removeEventListener('admin-accounts-refresh', refreshHandler);
  }, []);

  // Fetch admin's own contact number for SMS OTP option
  useEffect(() => {
    const fetchAdminContact = async () => {
      const user = auth.currentUser;
      if (!user) return;
      try {
        const snap = await getDoc(doc(db, "users", user.uid));
        if (snap.exists()) {
          const data = snap.data();
          const raw = data.contactNumber || data.contact || data.phone || "";
          // Normalise to +63XXXXXXXXXX
          const digits = String(raw).replace(/\D/g, "");
          if (digits.length >= 10) {
            setAdminContactNumber(`+63${digits.slice(-10)}`);
          }
        }
      } catch { /* non-critical */ }
    };
    fetchAdminContact();
  }, []);

  // Backfill selectedAccount.uid when opening old-shaped objects
  useEffect(() => {
    if (!selectedAccount) return;
    if (selectedAccount.uid) return;
    const match = accounts.find((a) => a.id === selectedAccount.id);
    if (match?.uid) setSelectedAccount((p) => ({ ...p, uid: match.uid }));
  }, [accounts, selectedAccount]);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [search, statusFilter, roleFilter]);

  // Close role dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (roleDropdownRef.current && !roleDropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };
    if (dropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [dropdownOpen]);

  const filteredAccounts = accounts.filter(acc => {
    const keywordMatch =
      acc.id.toLowerCase().includes(search.toLowerCase()) ||
      acc.email.toLowerCase().includes(search.toLowerCase()) ||
      acc.contact.includes(search);
    const statusMatch = statusFilter === "" || acc.status === statusFilter;
    const typeMatch = true;
    const roleMatch =
      roleFilter === "" ||
      (Array.isArray(acc.roles) && acc.roles.includes(roleFilter)) ||
      acc.role === roleFilter;
    
    // Apply tab filtering
    const tabMatch = 
      activeTab === "all" || 
      (activeTab === "users" && acc.type === "User") ||
      (activeTab === "staff" && acc.type === "Staff");
    
    return keywordMatch && statusMatch && typeMatch && roleMatch && tabMatch;
  });

  // Pagination logic
  const totalPages = Math.ceil(filteredAccounts.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedAccounts = filteredAccounts.slice(startIndex, endIndex);

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const openPanel = (account, mode) => {
    setSelectedAccount(account);
    setPanelMode(mode);
    setPanelOpen(true);
    setDropdownOpen(false);
    setRoleError("");
    setLicenseError("");
    setEmailDraft(account.email || "");
    setEmailError("");
  };

  // ── Email change flow ──────────────────────────────────────────────────────

  const handleEmailSaveClick = () => {
    const trimmed = emailDraft.trim();
    if (!trimmed || trimmed === selectedAccount.email) {
      setEmailError("Enter a new email address first");
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(trimmed)) {
      setEmailError("Enter a valid email address");
      return;
    }
    setEmailError("");
    setShowEmailPasswordModal(true);
  };

  const handleEmailPasswordConfirm = async (password) => {
    const user = auth.currentUser;
    if (!user) throw new Error("Not authenticated");
    const credential = EmailAuthProvider.credential(user.email, password);
    await reauthenticateWithCredential(user, credential);
    // Password OK — send OTP to the ADMIN's own email
    setShowEmailPasswordModal(false);
    await sendEmailChangeOtp();
  };

  const sendEmailChangeOtp = async (overrideMethod = null) => {
    const { sendOtpSms } = await import("../services/otpService");
    const methodToUse = overrideMethod === "email" || overrideMethod === "sms"
      ? overrideMethod
      : emailOtpMethod;

    setEmailOtpLoading(true);
    setEmailOtpError("");
    try {
      const otp = Math.floor(100000 + Math.random() * 900000).toString();
      const requestId = `email_change_${Date.now()}`;
      const expiresAt = new Date(Date.now() + 5 * 60 * 1000).toISOString();
      setEmailOtpId(requestId);

      if (methodToUse === "sms" && adminContactNumber) {
        await sendOtpSms({
          phone: adminContactNumber,
          otp,
          purpose: "email_change",
          requestId,
          expiresAt,
        });
        setEmailOtpDeliveryTarget(adminContactNumber);
      } else {
        await sendOtpEmail({
          email: auth.currentUser.email,
          otp,
          purpose: "email_change",
          requestId,
          expiresAt,
        });
        setEmailOtpDeliveryTarget(auth.currentUser.email);
      }

      localStorage.setItem(requestId, JSON.stringify({ otp, expiresAt }));
      setEmailOtpDigits(Array(6).fill(""));
      setEmailOtpTimeLeft(60);
      setEmailOtpExpired(false);
      setEmailOtpTimerActive(true);
      setShowEmailOtpModal(true);
    } catch (err) {
      setEmailOtpError(err.message || "Failed to send OTP");
      setShowEmailOtpModal(true);
    } finally {
      setEmailOtpLoading(false);
    }
  };

  const handleEmailTryAnotherWay = () => {
    const newMethod = emailOtpMethod === "email" ? "sms" : "email";
    setEmailOtpMethod(newMethod);
    setEmailOtpHasSmsBeenSent(false);
    setEmailOtpSwitchedFromSms(emailOtpMethod === "sms"); // true only when switching sms→email
    setEmailOtpTimerActive(false);
    setEmailOtpDigits(Array(6).fill(""));
    setEmailOtpError("");
    setEmailOtpTimeLeft(60);
    setEmailOtpExpired(false);
    if (newMethod === "sms" && adminContactNumber) {
      setEmailOtpDeliveryTarget(adminContactNumber);
    } else {
      setEmailOtpDeliveryTarget(auth.currentUser?.email || "");
    }
  };

  const handleEmailSendSms = async () => {
    if (emailOtpLoading) return;
    await sendEmailChangeOtp("sms");
    setEmailOtpHasSmsBeenSent(true);
    setEmailOtpTimerActive(true);
  };

  const handleEmailSendEmail = async () => {
    if (emailOtpLoading) return;
    await sendEmailChangeOtp("email");
    setEmailOtpHasSmsBeenSent(false);
    setEmailOtpSwitchedFromSms(false);
    setEmailOtpTimerActive(true);
  };

  // Timer for the email-change OTP
  useEffect(() => {
    if (!showEmailOtpModal || !emailOtpTimerActive || emailOtpTimeLeft <= 0) return;
    const t = setInterval(() => {
      setEmailOtpTimeLeft(prev => {
        if (prev <= 1) { setEmailOtpExpired(true); return 0; }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(t);
  }, [showEmailOtpModal, emailOtpTimerActive, emailOtpTimeLeft]);

  const handleEmailOtpChange = (value, index) => {
    if (!/^\d?$/.test(value)) return;
    const next = [...emailOtpDigits];
    next[index] = value;
    setEmailOtpDigits(next);
    setEmailOtpError("");
    if (value && index < 5) emailOtpInputsRef.current[index + 1]?.focus();
  };

  const handleEmailOtpKeyDown = (e, index) => {
    if (e.key === "Backspace" && !emailOtpDigits[index] && index > 0) {
      emailOtpInputsRef.current[index - 1]?.focus();
    }
  };

  const handleEmailOtpPaste = (e) => {
    const data = e.clipboardData.getData("text").trim().slice(0, 6);
    if (/^\d+$/.test(data)) {
      const arr = Array(6).fill("");
      data.split("").forEach((ch, i) => { if (i < 6) arr[i] = ch; });
      setEmailOtpDigits(arr);
      setEmailOtpError("");
      emailOtpInputsRef.current[Math.min(data.length, 5)]?.focus();
    }
    e.preventDefault();
  };

  const handleEmailOtpVerify = async () => {
    if (!emailOtpDigits.every(d => d !== "")) {
      setEmailOtpError("Please enter the full 6-digit code.");
      return;
    }
    if (emailOtpExpired) {
      setEmailOtpError("OTP expired. Please request a new code.");
      return;
    }
    setEmailOtpVerifying(true);
    try {
      const stored = localStorage.getItem(emailOtpId);
      if (!stored) throw new Error("OTP expired or invalid");
      const { otp: storedOtp, expiresAt } = JSON.parse(stored);
      if (new Date(expiresAt) < new Date()) { localStorage.removeItem(emailOtpId); throw new Error("OTP expired"); }
      if (emailOtpDigits.join("") !== storedOtp) throw new Error("Incorrect code. Please try again.");
      localStorage.removeItem(emailOtpId);
      // Update email via Cloud Function (updates both Auth + Firestore)
      const newEmail = emailDraft.trim();
      const user = auth.currentUser;
      const idToken = await user.getIdToken(true);
      const fnBase = "https://us-central1-auravit-bb843.cloudfunctions.net";
      const response = await fetch(`${fnBase}/updateStaffEmail`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify({ staffUid: selectedAccount.uid, newEmail }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || "Failed to update email");
      setAccounts(prev => prev.map(acc => acc.uid === selectedAccount.uid ? { ...acc, email: newEmail } : acc));
      setSelectedAccount(prev => prev ? { ...prev, email: newEmail } : prev);
      setEmailDraft(newEmail);
      setShowEmailOtpModal(false);
      setEmailOtpDigits(Array(6).fill(""));
      setEmailOtpTimerActive(false);
    } catch (err) {
      setEmailOtpError(err.message || "Verification failed");
      setEmailOtpDigits(Array(6).fill(""));
      emailOtpInputsRef.current[0]?.focus();
    } finally {
      setEmailOtpVerifying(false);
    }
  };

  const closeEmailOtpModal = () => {
    setShowEmailOtpModal(false);
    setEmailOtpDigits(Array(6).fill(""));
    setEmailOtpError("");
    setEmailOtpTimerActive(false);
    setEmailOtpMethod("email");
    setEmailOtpDeliveryTarget("");
    setEmailOtpHasSmsBeenSent(false);
    setEmailOtpSwitchedFromSms(false);
  };

  const handleSave = async updated => {
    // Validate roles before saving
    const currentRoles = Array.isArray(updated.roles) && updated.roles.length
      ? updated.roles
      : (updated.role ? [updated.role] : []);
    
    if (currentRoles.length === 0) {
      setRoleError("At least one role must be selected");
      return;
    }

    // Validate license if Medical Professional
    if (currentRoles.includes("Medical Professional")) {
      if (!updated.license || updated.license.trim() === "") {
        setLicenseError("License Number required");
        return;
      }
      if (!/^\d{7}$/.test(updated.license)) {
        setLicenseError("Must be 7 digits");
        return;
      }
    }

    try {
      const nextRoles = Array.isArray(updated.roles) && updated.roles.length
        ? updated.roles
        : (updated.role ? [updated.role] : []);

      if (nextRoles.includes("Medical Professional")) {
        const lic = String(updated.license || "").trim();
        const licSnap = await getDocs(
          query(collection(db, "users"), where("medicalLicenseNumber", "==", lic), limit(1))
        );
        if (!licSnap.empty && licSnap.docs[0].id !== updated.uid) {
          setLicenseError("License number already in use");
          return;
        }
      }

      await updateDoc(doc(db, "users", updated.uid), {
        accountType: "Staff",
        role: nextRoles[0] || "",
        roles: nextRoles,
        medicalLicenseNumber: nextRoles.includes("Medical Professional") ? String(updated.license || "").trim() : "",
        updatedAt: serverTimestamp(),
      });

      setAccounts((prev) =>
        prev.map((acc) =>
          acc.uid === updated.uid
            ? { ...acc, role: nextRoles[0] || "", roles: nextRoles, license: updated.license }
            : acc
        )
      );
      setSelectedAccount((prev) =>
        prev && prev.uid === updated.uid
          ? { ...prev, role: nextRoles[0] || "", roles: nextRoles, license: updated.license }
          : prev
      );
    } catch (e) {
      alert(e?.message || "Failed to update staff");
    }
    setPanelOpen(false);
  };

  const handleConfirmAction = async () => {
    if (!confirmAction) return;

    if (confirmAction.type === "delete") {
      try {
        // Client-side app can't delete Firebase Auth users safely; delete Firestore profile only.
        await deleteDoc(doc(db, "users", confirmAction.account.uid));
        setAccounts((prev) => prev.filter((acc) => acc.uid !== confirmAction.account.uid));
        if (selectedAccount?.uid === confirmAction.account.uid) {
          setPanelOpen(false);
          setSelectedAccount(null);
        }
      } catch {
        alert("Connection error while deleting account");
      } finally {
        setConfirmAction(null);
      }
      return;
    }
  };

  // Multi-step deactivation handlers
  const initiateDeactivation = (account) => {
    setDeactivationAccount(account);
    setDeactivationStep(1);
    setDeactivationReason("");
    setAdminPassword("");
    setDeactivationError("");
  };

  const handleDeactivationConfirm = () => {
    setDeactivationStep(2);
  };

  const handleDeactivationReason = (reason) => {
    setDeactivationReason(reason);
    setDeactivationStep(3);
  };

  const handleAdminPasswordVerification = async () => {
    if (!adminPassword.trim()) {
      setDeactivationError("Admin password is required");
      return;
    }

    setIsDeactivating(true);
    setDeactivationError("");

    try {
      // Get current admin user
      const user = auth.currentUser;
      if (!user) {
        throw new Error("Admin authentication required");
      }

      // Re-authenticate with provided password
      const credential = EmailAuthProvider.credential(user.email, adminPassword);
      await reauthenticateWithCredential(user, credential);

      // Password verified, proceed with deactivation
      await updateDoc(doc(db, "users", deactivationAccount.uid), {
        status: "deactivated",
        deactivated: true,
        deactivatedAt: serverTimestamp(),
        deactivationReason: deactivationReason,
        twoFactorEnabled: true,
        updatedAt: serverTimestamp(),
      });

      // Send deactivation email
      try {
        const deactivationDate = new Date().toLocaleDateString();
        await fetch(`${API_BASE}/sendStaffDeactivationEmail`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            staffName: deactivationAccount.fullName || "Staff",
            deactivationDate,
            reason: deactivationReason,
            staffEmail: deactivationAccount.email,
          }),
        });
      } catch (emailError) {
        console.error("Failed to send deactivation email:", emailError);
        // Don't fail the whole process if email fails
      }

      setAccounts((prev) =>
        prev.map((acc) =>
          acc.uid === deactivationAccount.uid ? { ...acc, status: "Deactivated" } : acc
        )
      );

      if (selectedAccount?.uid === deactivationAccount.uid) {
        setSelectedAccount(prev => ({ ...prev, status: "Deactivated" }));
      }

      // Reset deactivation state
      setDeactivationStep(0);
      setDeactivationAccount(null);
      setDeactivationReason("");
      setAdminPassword("");
      setDeactivationError("");
    } catch (error) {
      console.error("Deactivation failed:", error);
      if (error.code === "auth/wrong-password" || error.code === "auth/invalid-credential") {
        setDeactivationError("Incorrect admin password");
      } else {
        setDeactivationError("Failed to deactivate account. Please try again.");
      }
    } finally {
      setIsDeactivating(false);
    }
  };

  const cancelDeactivation = () => {
    setDeactivationStep(0);
    setDeactivationAccount(null);
    setDeactivationReason("");
    setAdminPassword("");
    setDeactivationError("");
  };

  // Multi-step reactivation handlers
  const initiateReactivation = (account) => {
    setReactivationAccount(account);
    setReactivationStep(1);
    setReactivationReason("");
    setReactivationPassword("");
    setReactivationError("");
  };

  const handleReactivationConfirm = () => {
    setReactivationStep(2);
  };

  const handleReactivationReason = (reason) => {
    setReactivationReason(reason);
    setReactivationStep(3);
  };

  const handleReactivationPasswordVerification = async () => {
    if (!reactivationPassword.trim()) {
      setReactivationError("Admin password is required");
      return;
    }

    setIsReactivating(true);
    setReactivationError("");

    try {
      // Get current admin user
      const user = auth.currentUser;
      if (!user) {
        throw new Error("Admin authentication required");
      }

      // Re-authenticate with provided password
      const credential = EmailAuthProvider.credential(user.email, reactivationPassword);
      await reauthenticateWithCredential(user, credential);

      // Password verified, proceed with reactivation
      await updateDoc(doc(db, "users", reactivationAccount.uid), {
        status: "active",
        deactivated: false,
        deactivatedAt: null,
        deactivationReason: null,
        reactivatedAt: serverTimestamp(),
        reactivateReason: reactivationReason,
        updatedAt: serverTimestamp(),
      });

      // Send reactivation email
      try {
        const reactivationDate = new Date().toLocaleDateString();
        await fetch(`${API_BASE}/sendStaffReactivationEmail`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            staffName: reactivationAccount.fullName || "Staff",
            reactivationDate,
            reason: reactivationReason,
            staffEmail: reactivationAccount.email,
          }),
        });
      } catch (emailError) {
        console.error("Failed to send reactivation email:", emailError);
        // Don't fail the whole process if email fails
      }

      setAccounts((prev) =>
        prev.map((acc) =>
          acc.uid === reactivationAccount.uid ? { ...acc, status: "Active", deactivationReason: "" } : acc
        )
      );

      if (selectedAccount?.uid === reactivationAccount.uid) {
        setSelectedAccount(prev => ({ ...prev, status: "Active", deactivationReason: "" }));
      }

      // Reset reactivation state
      setReactivationStep(0);
      setReactivationAccount(null);
      setReactivationReason("");
      setReactivationPassword("");
      setReactivationError("");
    } catch (error) {
      console.error("Reactivation failed:", error);
      if (error.code === "auth/wrong-password" || error.code === "auth/invalid-credential") {
        setReactivationError("Incorrect admin password");
      } else {
        setReactivationError("Failed to reactivate account. Please try again.");
      }
    } finally {
      setIsReactivating(false);
    }
  };

  const cancelReactivation = () => {
    setReactivationStep(0);
    setReactivationAccount(null);
    setReactivationReason("");
    setReactivationPassword("");
    setReactivationError("");
  };

  const initiateAction = acc => {
    const type = acc.status === "Active" ? "deactivate" : "restore";
    if (type === "deactivate") {
      initiateDeactivation(acc);
    } else {
      initiateReactivation(acc);
    }
  };

  return (
    <div className="account-management-container">
      <div className="content-library-header-custom" style={{
        background: 'linear-gradient(135deg, #3b82f6 0%, #1e40af 100%)',
        borderRadius: '20px',
        padding: '32px',
        marginBottom: '24px',
        boxShadow: '0 8px 32px rgba(59, 130, 246, 0.3)',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Administrator - Left Aligned */}
        <div style={{
          position: 'absolute',
          left: '32px',
          top: '50%',
          transform: 'translateY(-50%)'
        }}>
          <div style={{
            padding: '16px 32px',
            borderRadius: '50px',
            background: 'rgba(147, 197, 253, 0.2)',
            border: '1px solid rgba(147, 197, 253, 0.3)'
          }}>
            <h2 style={{
              margin: 0,
              fontSize: '28px',
              fontWeight: '700',
              background: 'linear-gradient(135deg, #60a5fa 0%, #3b82f6 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              color: 'transparent',
              letterSpacing: '0.02em',
              textShadow: 'none'
            }}>
              Administrator
            </h2>
          </div>
        </div>

        {/* Title and Subtitle */}
        <div style={{ textAlign: 'right' }}>
          <h2 style={{
            margin: 0,
            fontSize: '36px',
            fontWeight: '800',
            color: '#ffffff !important',
            background: 'none !important',
            WebkitBackgroundClip: 'unset !important',
            WebkitTextFillColor: '#ffffff !important',
            backgroundClip: 'unset !important',
            letterSpacing: '-0.02em',
            textShadow: '0 2px 8px rgba(0,0,0,0.2)'
          }}>
            Account Management
          </h2>
          <p style={{
            margin: '12px 0 0 0',
            fontSize: '18px',
            color: '#ffffff',
            fontWeight: '500',
            textShadow: '0 1px 4px rgba(0,0,0,0.15)'
          }}>
            Manage user accounts, roles, and access permissions
          </p>
        </div>

        {/* CSS Animations */}
        <style>{`
          .content-library-header-custom h2 {
            color: #ffffff !important;
            background: none !important;
            -webkit-background-clip: unset !important;
            -webkit-text-fill-color: #ffffff !important;
            background-clip: unset !important;
          }
          .content-library-header-custom p {
            color: #ffffff !important;
          }
        `}</style>
      </div>

      {/* Top Controls */}
      <div className="admin-aura-top-controls" style={{
        background: 'rgba(255,255,255,0.95)',
        padding: '20px',
        borderRadius: '24px',
        border: '1px solid rgba(255,255,255,0.3)',
        boxShadow: '0 8px 32px rgba(0,0,0,0.06)',
        backdropFilter: 'blur(10px)'
      }}>
        <div style={{ position: 'relative', flex: 1, maxWidth: '500px' }}>
          <FiSearch style={{ 
            position: 'absolute', 
            left: '16px', 
            top: '50%', 
            transform: 'translateY(-50%)', 
            color: '#8b8b8b',
            fontSize: '20px'
          }} />
          <input
            style={{
              paddingLeft: '52px',
              paddingRight: '20px',
              paddingTop: '16px',
              paddingBottom: '16px',
              borderRadius: '20px',
              border: '2px solid rgba(59, 130, 246, 0.15)',
              background: '#ffffff',
              fontSize: '15px',
              fontWeight: '500',
              outline: 'none',
              width: '100%',
              transition: 'all 0.3s cubic-bezier(0.4,0,0.2,1)',
              boxSizing: 'border-box'
            }}
            placeholder="Search System ID/UIC, Email, Contact"
            value={search}
            onChange={e => {
              setSearch(e.target.value);
              setCurrentPage(1);
            }}
            onFocus={(e) => {
              e.target.style.borderColor = '#3b82f6';
              e.target.style.boxShadow = '0 0 0 4px rgba(59,130,246,0.1)';
            }}
            onBlur={(e) => {
              e.target.style.borderColor = 'rgba(59, 130, 246, 0.15)';
              e.target.style.boxShadow = 'none';
            }}
          />
        </div>
        <select
          value={statusFilter}
          onChange={e => {
            setStatusFilter(e.target.value);
            setCurrentPage(1);
          }}
          style={{
            padding: '14px 20px',
            borderRadius: '16px',
            border: '2px solid rgba(59,130,246,0.15)',
            background: '#ffffff',
            fontSize: '15px',
            fontWeight: '600',
            color: '#1e293b',
            outline: 'none',
            cursor: 'pointer',
            minWidth: '140px',
            transition: 'all 0.2s ease'
          }}
        >
          <option value="">All Status</option>
          <option value="Active">Active</option>
          <option value="Deactivated">Deactivated</option>
        </select>
        {(activeTab === "staff" || activeTab === "all") && (
          <select
            value={roleFilter}
            onChange={e => {
              setRoleFilter(e.target.value);
              setCurrentPage(1);
            }}
            style={{
              padding: '14px 20px',
              borderRadius: '16px',
              border: '2px solid rgba(59,130,246,0.15)',
              background: '#ffffff',
              fontSize: '15px',
              fontWeight: '600',
              color: '#1e293b',
              outline: 'none',
              cursor: 'pointer',
              minWidth: '170px',
              transition: 'all 0.2s ease'
            }}
          >
            <option value="">All Roles</option>
            <option value="Peer Educator">Peer Educator</option>
            <option value="Content Curator">Content Creator</option>
            <option value="Medical Professional">Medical Professional</option>
          </select>
        )}
        <button 
          className="admin-primary-gradient-btn"
          onClick={() => {
            refreshAccounts();
          }}
          disabled={loading}
          style={{ opacity: loading ? 0.6 : 1, cursor: loading ? 'not-allowed' : 'pointer' }}
        >
          {loading ? <FiRefreshCw className="spinning" /> : <FiRefreshCw />}
          Refresh
        </button>
        <button
          className="admin-primary-gradient-btn"
          onClick={() => setShowExportModal(true)}
        >
          <FiDownload /> Export Report
        </button>
        {showCreateStaff ? (
          <button 
            onClick={() => setShowCreateStaff(false)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              padding: '14px 24px',
              borderRadius: '16px',
              border: '2px solid rgba(59,130,246,0.3)',
              background: '#ffffff',
              color: '#1e40af',
              fontSize: '15px',
              fontWeight: '700',
              cursor: 'pointer',
              transition: 'all 0.3s ease'
            }}
          >
            <FiX /> Back to Accounts
          </button>
        ) : (
          <button 
            onClick={() => setShowCreateStaff(true)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              padding: '14px 24px',
              borderRadius: '16px',
              border: 'none',
              background: 'linear-gradient(135deg, #3b82f6 0%, #1e3a8a 100%)',
              color: '#ffffff',
              fontSize: '15px',
              fontWeight: '700',
              cursor: 'pointer',
              boxShadow: '0 4px 16px rgba(30,58,138,0.35)',
              transition: 'all 0.3s cubic-bezier(0.4,0,0.2,1)'
            }}
            onMouseEnter={(e) => {
              e.target.style.transform = 'translateY(-2px)';
              e.target.style.boxShadow = '0 8px 24px rgba(30,58,138,0.45)';
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 4px 16px rgba(30,58,138,0.35)';
            }}
          >
            <FiUserPlus /> Create Staff
          </button>
        )}
      </div>

      {showCreateStaff ? (
        <CreateStaff 
          onCancel={() => setShowCreateStaff(false)} 
          onSuccess={() => setShowCreateStaff(false)} 
        />
      ) : (
        <>
          {/* ── Custom Tooltip for Action Buttons ── */}
          {tooltip.visible && (
            <div
              className={`admin-action-tooltip ${tooltip.type}`}
              style={{
                left: tooltip.x,
                top: tooltip.y
              }}
            >
              {tooltip.text}
            </div>
          )}
          {/* Tabs Row with Pagination and Export PDF */}
          <div className="floating-index-tabs-wrapper">
            <div className="floating-index-tabs">
              <button
                className={`floating-tab ${activeTab === "all" ? "active" : ""}`}
                onClick={() => {
                  setActiveTab("all");
                  setCurrentPage(1);
                }}
              >
                All
                <span className="tab-count">{accounts.length}</span>
              </button>
              <button
                className={`floating-tab ${activeTab === "users" ? "active" : ""}`}
                onClick={() => {
                  setActiveTab("users");
                  setCurrentPage(1);
                }}
              >
                Patients
                <span className="tab-count">
                  {accounts.filter(acc => acc.type === "User").length}
                </span>
              </button>
              <button
                className={`floating-tab ${activeTab === "staff" ? "active" : ""}`}
                onClick={() => {
                  setActiveTab("staff");
                  setCurrentPage(1);
                }}
              >
                Staff
                <span className="tab-count">
                  {accounts.filter(acc => acc.type === "Staff").length}
                </span>
              </button>
            </div>
            
            {/* Right Side: Pagination + Export PDF */}
            <div style={{ display: "flex", gap: "16px", alignItems: "center" }}>
              <div style={{ fontSize: "14px", color: "#6b7280" }}>
                {filteredAccounts.length > 0 ? (
                  `${startIndex + 1}-${Math.min(endIndex, filteredAccounts.length)} of ${filteredAccounts.length}`
                ) : (
                  '0 of 0'
                )}
              </div>
              <div style={{ display: "flex", gap: "8px" }}>
                <button 
                  onClick={handlePrevPage}
                  disabled={currentPage === 1}
                  style={{ 
                    backgroundColor: '#1e3a8a', 
                    color: 'white', 
                    border: 'none', 
                    padding: '8px 12px', 
                    borderRadius: '4px', 
                    cursor: 'pointer',
                    width: '32px'
                  }}
                >
                  ←
                </button>
                
                <button 
                  onClick={handleNextPage}
                  disabled={currentPage === totalPages || totalPages === 0}
                  style={{ 
                    backgroundColor: '#1e3a8a', 
                    color: 'white', 
                    border: 'none', 
                    padding: '8px 12px', 
                    borderRadius: '4px', 
                    cursor: 'pointer',
                    width: '32px'
                  }}
                >
                  →
                </button>
              </div>
            </div>
          </div>

          {/* Table */}
          <div className="admin-aura-table-wrapper with-floating-tabs">
        <table className="admin-aura-table">
          <thead>
            {/* Column Headers Row */}
            <tr>
              <th>
                {activeTab === "users" ? "Unique Identifier Code" : 
                 activeTab === "staff" ? "System ID" : 
                 "System ID/UIC"}
              </th>
              {activeTab === "staff" && <th>Role</th>}
              <th>Email</th>
              <th>Contact</th>
              <th>Date Created</th>
              <th>Last Login</th>
              <th>Status</th>
              {activeTab !== "users" && <th>{activeTab === "all" ? "Actions" : ""}</th>}
            </tr>
          </thead>
          <tbody>
          {loading ? (
              <>
                {[...Array(10)].map((_, i) => (
                  <tr key={i} className="admin-aura-skeleton-row">
                    <td><div className="admin-aura-skeleton admin-aura-skeleton-text" style={{ width: `${55 + (i % 3) * 15}%` }} /></td>
                    {activeTab === "staff" && <td><div className="admin-aura-skeleton admin-aura-skeleton-text" style={{ width: '70%' }} /></td>}
                    <td><div className="admin-aura-skeleton admin-aura-skeleton-text" style={{ width: `${60 + (i % 4) * 10}%` }} /></td>
                    <td><div className="admin-aura-skeleton admin-aura-skeleton-text" style={{ width: '80%' }} /></td>
                    <td><div className="admin-aura-skeleton admin-aura-skeleton-text" style={{ width: '65%' }} /></td>
                    <td><div className="admin-aura-skeleton admin-aura-skeleton-text" style={{ width: '55%' }} /></td>
                    <td><div className="admin-aura-skeleton admin-aura-skeleton-badge" /></td>
                    {activeTab !== "users" && <td><div className="admin-aura-skeleton admin-aura-skeleton-actions" /></td>}
                  </tr>
                ))}
              </>
            ) : paginatedAccounts.length === 0 ? (
              <tr>
                <td colSpan={activeTab === "users" ? 7 : 8} style={{ textAlign: "center", padding: "40px 20px", color: "#6b7280" }}>
                  No accounts found
                </td>
              </tr>
            ) : (
              paginatedAccounts.map(acc => (
                <tr
                  key={acc.uid}
                  onClick={() => openPanel(acc, "view")}
                  style={{ cursor: 'pointer' }}
                  className="admin-aura-clickable-row"
                >
                  <td>
                    <div>{acc.id}</div>
                    {acc.type === "User" && (
                      <div style={{ marginTop: '4px', display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                        {Array.isArray(acc.reasonForAssistance) && acc.reasonForAssistance.length > 0 && acc.reasonForAssistance.map((reason, idx) => (
                          <span
                            key={idx}
                            style={{
                              fontSize: '10px',
                              padding: '2px 6px',
                              borderRadius: '4px',
                              fontWeight: '500',
                              textTransform: 'capitalize',
                              backgroundColor: getAssistanceBadgeColor(reason),
                              color: getAssistanceBadgeTextColor(reason)
                            }}
                          >
                            {reason}
                          </span>
                        ))}
                      </div>
                    )}
                  </td>
                  {activeTab === "staff" && (
                    <td>
                      {acc.type === "Staff"
                        ? (Array.isArray(acc.roles) && acc.roles.length
                            ? (
                              <div className="admin-aura-role-stack">
                                {acc.roles.map(r => (
                                  <div key={r}>{r}</div>
                                ))}
                              </div>
                            )
                            : (acc.role || "-"))
                        : "-"}
                    </td>
                  )}
                  <td className="email-cell">{acc.email}</td>
                  <td>{acc.contact}</td>
                  <td>{acc.created}</td>
                  <td>{acc.lastLogin}</td>
                  <td>
                    <div>
                      <span
                        className={`admin-aura-status ${acc.status === "Active" ? "active" : "inactive"}`}
                      >
                        <span className="admin-aura-status-dot" />
                        {acc.status}
                      </span>
                      {acc.status === "Deactivated" && acc.deactivationReason && (
                        <div style={{
                          fontSize: "11px",
                          color: "#666",
                          marginTop: "2px",
                          fontStyle: "italic"
                        }}>
                          {acc.deactivationReason.charAt(0).toUpperCase() + acc.deactivationReason.slice(1).replace(/_/g, ' ')}
                        </div>
                      )}
                    </div>
                  </td>
                  {activeTab !== "users" && (
                    <td className="admin-aura-actions">
                      {acc.type === "Staff" && (
                        <button
                          type="button"
                          className="ma-action-btn approve"
                          onClick={(e) => {
                            e.stopPropagation();
                            openPanel(acc, "edit");
                          }}
                          aria-label="Edit Staff"
                          onMouseEnter={(e) => { e.target.style.background = '#bfdbfe'; e.target.style.color = '#1d4ed8'; handleActionMouseEnter(e, "Edit Staff", 'view'); }}
                          onMouseMove={handleActionMouseMove}
                          onMouseLeave={(e) => { e.target.style.background = '#dbeafe'; e.target.style.color = '#1d4ed8'; handleActionMouseLeave(); }}
                          style={{ background: '#dbeafe', color: '#1d4ed8', borderColor: '#3b82f6', border: '1px solid', borderRadius: '12px' }}
                        >
                          <FiEdit2 size={16} />
                        </button>
                      )}

                      {acc.type !== "Admin" && acc.type !== "User" && (
                        <button
                          type="button"
                          className="ma-action-btn cancel"
                          onClick={(e) => {
                            e.stopPropagation();
                            initiateAction(acc);
                          }}
                          aria-label={acc.status === "Active" ? "Deactivate Account" : "Reactivate Account"}
                          onMouseEnter={(e) => { e.target.style.background = acc.status === "Active" ? '#fecaca' : '#d1fae5'; e.target.style.color = acc.status === "Active" ? '#dc2626' : '#059669'; handleActionMouseEnter(e, acc.status === "Active" ? "Deactivate Account" : "Reactivate Account", acc.status === "Active" ? 'delete' : 'approve'); }}
                          onMouseMove={handleActionMouseMove}
                          onMouseLeave={(e) => { e.target.style.background = acc.status === "Active" ? '#fee2e2' : '#ecfdf5'; e.target.style.color = acc.status === "Active" ? '#dc2626' : '#059669'; handleActionMouseLeave(); }}
                          style={{ background: acc.status === "Active" ? '#fee2e2' : '#ecfdf5', color: acc.status === "Active" ? '#dc2626' : '#059669', borderColor: acc.status === "Active" ? '#ef4444' : '#10b981', border: '1px solid', borderRadius: '12px' }}
                        >
                          {acc.status === "Active" ? <MdOutlineArchive size={16} /> : <FiRefreshCw size={16} />}
                        </button>
                      )}
                      {acc.type === "Admin" && (
                        <button
                          type="button"
                          className="ma-action-btn cancel"
                          onClick={(e) => {
                            e.stopPropagation();
                            setConfirmAction({ account: acc, type: "delete" });
                          }}
                          aria-label="Delete Account"
                          onMouseEnter={(e) => { e.target.style.background = '#fecaca'; e.target.style.color = '#dc2626'; handleActionMouseEnter(e, "Delete Account", 'delete'); }}
                          onMouseMove={handleActionMouseMove}
                          onMouseLeave={(e) => { e.target.style.background = '#fee2e2'; e.target.style.color = '#dc2626'; handleActionMouseLeave(); }}
                          style={{ background: '#fee2e2', color: '#dc2626', borderColor: '#ef4444', border: '1px solid', borderRadius: '12px' }}
                        >
                          <MdOutlineArchive size={16} />
                        </button>
                      )}
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Export Modal */}
      <ExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        onExport={handleExport}
        isExporting={isExporting}
        activeTab={activeTab}
      />

      {/* =======================
         VIEW MODAL
      ====================== */}
      {panelOpen && selectedAccount && panelMode === "view" && (
        <div className="admin-aura-modal-overlay">
          <div
            className="account-details-card"
            style={{
              border: 'none !important',
              borderRadius: '16px',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
              overflow: 'hidden',
              padding: '0 !important',
              width: '600px'
            }}
          >
            {/* Modal Header */}
            <div style={{ 
              background: 'linear-gradient(135deg, #3f62b5 0%, #2563eb 100%)',
              padding: '24px 32px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              width: '100%',
              boxSizing: 'border-box'
            }}>
              <div>
                <h3 style={{ margin: 0, fontSize: '20px', fontWeight: 700, color: '#fff' }}>Account Details</h3>
              </div>
              <button
                onClick={() => setPanelOpen(false)}
                aria-label="Close"
                style={{
                  background: 'rgba(255, 255, 255, 0.2)',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '8px',
                  color: '#fff',
                  cursor: 'pointer',
                  fontSize: '20px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.2s',
                  zIndex: 10
                }}
                onMouseOver={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.3)'}
                onMouseOut={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)'}
              >
                <FiX />
              </button>
            </div>

            <div className="account-details-content" style={{ 
              padding: '32px', 
              background: '#fff',
              overflowY: 'auto',
              maxHeight: 'calc(80vh - 80px)',
              width: '100%',
              boxSizing: 'border-box'
            }}>
              {/* Status Tag */}
              <div style={{ 
                display: 'flex', 
                justifyContent: 'flex-end', 
                marginBottom: '24px' 
              }}>
                <span
                  className={`account-status-tag ${selectedAccount.status === "Active" ? "active" : "inactive"}`}
                  style={{
                    padding: '8px 16px',
                    borderRadius: '20px',
                    fontSize: '14px',
                    fontWeight: 600,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    background: selectedAccount.status === "Active" 
                      ? 'linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)' 
                      : 'linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%)',
                    color: selectedAccount.status === "Active" ? '#059669' : '#4b5563',
                    border: 'none',
                    boxSizing: 'border-box'
                  }}
                >
                  <span className="status-dot" style={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    background: selectedAccount.status === "Active" ? '#059669' : '#9ca3af',
                    flexShrink: 0
                  }} />
                  {selectedAccount.status}
                </span>
              </div>

              {/* Account Details Section */}
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: '1fr 1fr', 
                gap: '20px',
                width: '100%',
                boxSizing: 'border-box'
              }}>
                {selectedAccount.type !== "User" && (
                  <div style={{ gridColumn: '1 / -1', width: '100%', boxSizing: 'border-box' }}>
                    <label style={{ 
                      fontSize: '13px', 
                      fontWeight: 600, 
                      color: '#6b7280', 
                      marginBottom: '6px', 
                      display: 'block' 
                    }}>Full Name</label>
                    <input 
                      value={
                        selectedAccount.fullName || 
                        selectedAccount.name ||
                        selectedAccount.uniqueIdentifierCode ||
                        "Name not available"
                      } 
                      disabled 
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        borderRadius: '8px',
                        border: '1px solid #e5e7eb',
                        background: '#f9fafb',
                        color: '#1f2937',
                        fontSize: '14px',
                        boxSizing: 'border-box'
                      }}
                    />
                  </div>
                )}
                <div style={{ width: '100%', boxSizing: 'border-box' }}>
                  <label style={{ 
                    fontSize: '13px', 
                    fontWeight: 600, 
                    color: '#6b7280', 
                    marginBottom: '6px', 
                    display: 'block' 
                  }}>{selectedAccount.type === "User" ? "Unique Identifier Code (UIC)" : "System ID"}</label>
                  <input 
                    value={selectedAccount.id} 
                    disabled 
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      borderRadius: '8px',
                      border: '1px solid #e5e7eb',
                      background: '#f9fafb',
                      color: '#1f2937',
                      fontSize: '14px',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
                {selectedAccount.type === "User" && selectedAccount.firstName && selectedAccount.lastName && (
                  <div style={{ width: '100%', boxSizing: 'border-box' }}>
                    <label style={{ 
                      fontSize: '13px', 
                      fontWeight: 600, 
                      color: '#6b7280', 
                      marginBottom: '6px', 
                      display: 'block' 
                    }}>Full Name</label>
                    <input 
                      value={`${selectedAccount.firstName} ${selectedAccount.lastName}`} 
                      disabled 
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        borderRadius: '8px',
                        border: '1px solid #e5e7eb',
                        background: '#f9fafb',
                        color: '#1f2937',
                        fontSize: '14px',
                        boxSizing: 'border-box'
                      }}
                    />
                  </div>
                )}
                <div style={{ width: '100%', boxSizing: 'border-box' }}>
                  <label style={{ 
                    fontSize: '13px', 
                    fontWeight: 600, 
                    color: '#6b7280', 
                    marginBottom: '6px', 
                    display: 'block' 
                  }}>Account Type</label>
                  <input 
                    value={selectedAccount.type} 
                    disabled 
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      borderRadius: '8px',
                      border: '1px solid #e5e7eb',
                      background: '#f9fafb',
                      color: '#1f2937',
                      fontSize: '14px',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
                <div style={{ width: '100%', boxSizing: 'border-box' }}>
                  <label style={{ 
                    fontSize: '13px', 
                    fontWeight: 600, 
                    color: '#6b7280', 
                    marginBottom: '6px', 
                    display: 'block' 
                  }}>Date Created</label>
                  <input 
                    value={selectedAccount.created} 
                    disabled 
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      borderRadius: '8px',
                      border: '1px solid #e5e7eb',
                      background: '#f9fafb',
                      color: '#1f2937',
                      fontSize: '14px',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
                <div style={{ width: '100%', boxSizing: 'border-box' }}>
                  <label style={{ 
                    fontSize: '13px', 
                    fontWeight: 600, 
                    color: '#6b7280', 
                    marginBottom: '6px', 
                    display: 'block' 
                  }}>Last Login</label>
                  <input 
                    value={selectedAccount.lastLogin} 
                    disabled 
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      borderRadius: '8px',
                      border: '1px solid #e5e7eb',
                      background: '#f9fafb',
                      color: '#1f2937',
                      fontSize: '14px',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
                <div style={{ gridColumn: '1 / -1', width: '100%', boxSizing: 'border-box' }}>
                  <label style={{ 
                    fontSize: '13px', 
                    fontWeight: 600, 
                    color: '#6b7280', 
                    marginBottom: '6px', 
                    display: 'block' 
                  }}>Email</label>
                  <input 
                    value={selectedAccount.email} 
                    disabled 
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      borderRadius: '8px',
                      border: '1px solid #e5e7eb',
                      background: '#f9fafb',
                      color: '#1f2937',
                      fontSize: '14px',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
                <div style={{ gridColumn: '1 / -1', width: '100%', boxSizing: 'border-box' }}>
                  <label style={{ 
                    fontSize: '13px', 
                    fontWeight: 600, 
                    color: '#6b7280', 
                    marginBottom: '6px', 
                    display: 'block' 
                  }}>Contact Number</label>
                  <input 
                    value={selectedAccount.contact ? `+63${selectedAccount.contact.replace(/\D/g, "").slice(-10)}` : selectedAccount.contact} 
                    disabled 
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      borderRadius: '8px',
                      border: '1px solid #e5e7eb',
                      background: '#f9fafb',
                      color: '#1f2937',
                      fontSize: '14px',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
              </div>

              {selectedAccount.type === "Staff" && (
                <>
                  <h3 style={{ 
                    fontSize: '16px', 
                    fontWeight: 700, 
                    color: '#1e293b', 
                    marginTop: '32px', 
                    marginBottom: '16px',
                    paddingBottom: '8px',
                    borderBottom: '1px solid #e5e7eb',
                    width: '100%',
                    boxSizing: 'border-box'
                  }}>Staff Information</h3>
                  <div style={{ 
                    display: 'grid', 
                    gridTemplateColumns: '1fr 1fr', 
                    gap: '20px',
                    width: '100%',
                    boxSizing: 'border-box'
                  }}>
                    <div style={{ width: '100%', boxSizing: 'border-box' }}>
                      <label style={{ 
                        fontSize: '13px', 
                        fontWeight: 600, 
                        color: '#6b7280', 
                        marginBottom: '6px', 
                        display: 'block' 
                      }}>Staff ID</label>
                      <input 
                        value={selectedAccount.staffId || selectedAccount.id} 
                        disabled 
                        style={{
                          width: '100%',
                          padding: '12px 16px',
                          borderRadius: '8px',
                          border: '1px solid #e5e7eb',
                          background: '#f9fafb',
                          color: '#1f2937',
                          fontSize: '14px',
                          boxSizing: 'border-box'
                        }}
                      />
                    </div>
                    <div style={{ width: '100%', boxSizing: 'border-box' }}>
                      <label style={{ 
                        fontSize: '13px', 
                        fontWeight: 600, 
                        color: '#6b7280', 
                        marginBottom: '6px', 
                        display: 'block' 
                      }}>Account Creator</label>
                      <input 
                        value={selectedAccount.accountCreatorSystemId || ""} 
                        disabled 
                        style={{
                          width: '100%',
                          padding: '12px 16px',
                          borderRadius: '8px',
                          border: '1px solid #e5e7eb',
                          background: '#f9fafb',
                          color: '#1f2937',
                          fontSize: '14px',
                          boxSizing: 'border-box'
                        }}
                      />
                    </div>
                    <div style={{ gridColumn: '1 / -1', width: '100%', boxSizing: 'border-box' }}>
                      <label style={{ 
                        fontSize: '13px', 
                        fontWeight: 600, 
                        color: '#6b7280', 
                        marginBottom: '6px', 
                        display: 'block' 
                      }}>Role(s)</label>
                      <input
                        value={Array.isArray(selectedAccount.roles) && selectedAccount.roles.length
                          ? selectedAccount.roles.join(", ")
                          : (selectedAccount.role || "")}
                        disabled
                        style={{
                          width: '100%',
                          padding: '12px 16px',
                          borderRadius: '8px',
                          border: '1px solid #e5e7eb',
                          background: '#f9fafb',
                          color: '#1f2937',
                          fontSize: '14px',
                          boxSizing: 'border-box'
                        }}
                      />
                    </div>
                    {Array.isArray(selectedAccount.roles) && selectedAccount.roles.includes("Medical Professional") && (
                      <div style={{ width: '100%', boxSizing: 'border-box' }}>
                        <label style={{ 
                          fontSize: '13px', 
                          fontWeight: 600, 
                          color: '#6b7280', 
                          marginBottom: '6px', 
                          display: 'block' 
                        }}>License Number</label>
                        <input
                          value={selectedAccount.license || ""}
                          disabled
                          style={{
                            width: '100%',
                            padding: '12px 16px',
                            borderRadius: '8px',
                            border: '1px solid #e5e7eb',
                            background: '#f9fafb',
                            color: '#1f2937',
                            fontSize: '14px',
                            boxSizing: 'border-box'
                          }}
                        />
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* =======================
         EDIT MODAL
      ====================== */}
      {panelOpen && selectedAccount && panelMode === "edit" && (
        <div className="admin-aura-modal-overlay">
          <div
            className="account-details-card"
            style={{
              border:
                selectedAccount.status === "Deactivated"
                  ? "2px solid #ccc"
                  : "2px solid #4a90e2",
            }}
          >
            <div className="account-details-content">
              <div className="section-header-row">
                <h3 className="section-title">Edit Staff Account</h3>
                <span
                  className={`account-status-tag ${selectedAccount.status === "Active" ? "active" : "inactive"
                    }`}
                >
                  <span className="status-dot" />
                  {selectedAccount.status}
                </span>
              </div>

              <div className="edit-details-row">
                <label>Staff ID</label>
                <input value={selectedAccount.id} disabled />
              </div>

              {/* Editable email row */}
              <div className="edit-details-row" style={{ alignItems: "flex-start" }}>
                <label style={{ paddingTop: "10px" }}>Email</label>
                <div style={{ display: "flex", flexDirection: "column", flex: 1, gap: "6px" }}>
                  <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                    <input
                      type="text"
                      inputMode="email"
                      value={emailDraft}
                      onChange={e => {
                        setEmailDraft(e.target.value);
                        setEmailError("");
                      }}
                      placeholder="Enter new email"
                      style={{
                        flex: 1,
                        border: emailError ? "1.5px solid #e74c3c" : emailDraft !== selectedAccount.email ? "1.5px solid #3b82f6" : undefined,
                      }}
                    />
                  </div>
                  {emailError && (
                    <div className="field-error" style={{ marginTop: 0 }}>{emailError}</div>
                  )}
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: "4px" }}>
                    <span style={{ fontSize: "12px", color: "#64748b", fontStyle: "italic" }}>
                      Click here to change email
                    </span>
                    {emailDraft !== selectedAccount.email && (
                      <button
                        type="button"
                        onClick={handleEmailSaveClick}
                        style={{
                          padding: "6px 16px",
                          borderRadius: "8px",
                          border: "none",
                          background: "linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)",
                          color: "#fff",
                          fontSize: "12px",
                          fontWeight: 700,
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          gap: "5px",
                        }}
                      >
                        <FiShield style={{ fontSize: "12px" }} />
                        Change Email
                      </button>
                    )}
                  </div>
                </div>
              </div>

              <div className="edit-details-row">
                <label>Contact Number</label>
                <input value={selectedAccount.contact} disabled />
              </div>

              {/* Role dropdown */}
              <div className="edit-details-row">
                <label>Roles</label>
                <select
                  value={selectedAccount.role || (Array.isArray(selectedAccount.roles) && selectedAccount.roles[0]) || ""}
                  onChange={(e) => {
                    setSelectedAccount(prev => ({
                      ...prev,
                      role: e.target.value,
                      roles: [e.target.value]
                    }));
                    setRoleError("");
                  }}
                >
                  <option value="">Select a role</option>
                  <option value="Content Curator">Content Curator</option>
                  <option value="Peer Educator">Peer Educator</option>
                  <option value="Medical Professional">Medical Professional</option>
                </select>
              </div>
              {roleError && <div className="field-error" style={{ marginLeft: '146px', marginTop: '-8px', marginBottom: '16px' }}>{roleError}</div>}

              {(selectedAccount.role === "Medical Professional" || (Array.isArray(selectedAccount.roles) && selectedAccount.roles.includes("Medical Professional"))) && (
                <div className="edit-details-row">
                  <label>License Number</label>
                  <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                    <input
                      value={selectedAccount.license || ""}
                      onChange={e => {
                        setSelectedAccount(prev => ({
                          ...prev,
                          license: e.target.value.replace(/[^0-9]/g, "").slice(0, 7)
                        }));
                        setLicenseError("");
                      }}
                    />
                    {licenseError && <div className="field-error">{licenseError}</div>}
                  </div>
                </div>
              )}

              <div className="account-details-actions">
                <button onClick={() => setPanelOpen(false)}>Cancel</button>
                <button onClick={() => handleSave(selectedAccount)}>
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* =======================
         EMAIL CHANGE — PASSWORD CONFIRM MODAL
      ====================== */}
      {showEmailPasswordModal && (
        <Portal>
          <div className="modal-overlay">
            <div className="modal-content">
              <div className="modal-header">
                <h3 className="modal-title-white">Confirm Admin Password</h3>
                <button
                  className="modal-close"
                  onClick={() => { setShowEmailPasswordModal(false); setEmailError(""); }}
                >
                  <FiX />
                </button>
              </div>
              <p className="modal-subtitle" style={{ marginTop: "20px", marginLeft: "10px", marginBottom: "4px" }}>
                Enter your admin password to verify before changing the staff email.
              </p>
              <EmailPasswordConfirmForm
                onConfirm={handleEmailPasswordConfirm}
                onCancel={() => { setShowEmailPasswordModal(false); setEmailError(""); }}
              />
            </div>
          </div>
        </Portal>
      )}

      {/* =======================
         EMAIL CHANGE — OTP MODAL
      ====================== */}
      {showEmailOtpModal && (
        <Portal>
          <div className="twofactor-otp-modal-overlay">
            <div className="twofactor-otp-modal-card">
              <h1 className="twofactor-otp-title">Verify Email Change</h1>
              <p className="twofactor-otp-subtitle">
                Enter the 6-digit code sent to:{" "}
                <strong>
                  {emailOtpDeliveryTarget ||
                    (emailOtpMethod === "sms" ? adminContactNumber : auth.currentUser?.email)}
                </strong>
              </p>

              <div className={`twofactor-otp-inputs ${emailOtpError ? "shake" : ""}`}>
                {emailOtpDigits.map((digit, idx) => (
                  <input
                    key={idx}
                    ref={el => (emailOtpInputsRef.current[idx] = el)}
                    type="text"
                    maxLength="1"
                    value={digit}
                    onChange={e => handleEmailOtpChange(e.target.value, idx)}
                    onKeyDown={e => handleEmailOtpKeyDown(e, idx)}
                    onPaste={handleEmailOtpPaste}
                    className={`twofactor-otp-input ${emailOtpError && !digit ? "error" : ""}`}
                    disabled={emailOtpVerifying}
                    autoFocus={idx === 0}
                  />
                ))}
              </div>

              {emailOtpError && (
                <div className="twofactor-otp-error-text">{emailOtpError}</div>
              )}
              {emailOtpExpired && !emailOtpError && (
                <div className="twofactor-otp-error-text">Code has expired. Please request a new one.</div>
              )}

              {/* Send SMS button — shown when switched to SMS but not yet sent */}
              {emailOtpMethod === "sms" && !emailOtpHasSmsBeenSent && (
                <button
                  className="twofactor-otp-resend-link"
                  onClick={handleEmailSendSms}
                  disabled={emailOtpLoading}
                  style={{ pointerEvents: emailOtpLoading ? "none" : "auto", opacity: emailOtpLoading ? 0.5 : 1, marginBottom: "16px" }}
                >
                  {emailOtpLoading ? "Sending..." : "Send SMS"}
                </button>
              )}

              {/* Send Email button — shown when switched back to email from SMS */}
              {emailOtpMethod === "email" && emailOtpSwitchedFromSms && (
                <button
                  className="twofactor-otp-resend-link"
                  onClick={handleEmailSendEmail}
                  disabled={emailOtpLoading}
                  style={{ pointerEvents: emailOtpLoading ? "none" : "auto", opacity: emailOtpLoading ? 0.5 : 1, marginBottom: "16px" }}
                >
                  {emailOtpLoading ? "Sending..." : "Send Email"}
                </button>
              )}

              {/* Timer / Resend — shown when timer is active and OTP was actually sent */}
              {emailOtpTimerActive && ((emailOtpMethod === "email" && !emailOtpSwitchedFromSms) || emailOtpHasSmsBeenSent) && (
                <p className="twofactor-otp-timer-section">
                  {emailOtpTimeLeft > 0 ? (
                    <span className={`twofactor-otp-timer${emailOtpExpired ? " expired" : ""}`}>
                      Resend in {Math.floor(emailOtpTimeLeft / 60)}:{String(emailOtpTimeLeft % 60).padStart(2, "0")}
                    </span>
                  ) : (
                    <span
                      className={`twofactor-otp-resend-link${emailOtpLoading ? " disabled" : ""}`}
                      onClick={() => !emailOtpLoading && sendEmailChangeOtp()}
                      style={{ pointerEvents: emailOtpLoading ? "none" : "auto", opacity: emailOtpLoading ? 0.5 : 1 }}
                    >
                      {emailOtpLoading ? "Sending..." : "Resend"}
                    </span>
                  )}
                </p>
              )}

              {/* Try another way — only shown if both email and SMS are available */}
              {((emailOtpMethod === "email" && !!adminContactNumber) ||
                (emailOtpMethod === "sms" && !!auth.currentUser?.email)) && (
                <span
                  className="twofactor-otp-back-link"
                  onClick={handleEmailTryAnotherWay}
                  style={{ textDecoration: "underline", marginBottom: "16px", display: "block", cursor: "pointer" }}
                >
                  Try another way
                </span>
              )}

              <button
                className="twofactor-otp-verify-btn"
                onClick={handleEmailOtpVerify}
                disabled={emailOtpVerifying || !emailOtpDigits.every(d => d !== "") || emailOtpExpired}
              >
                {emailOtpVerifying ? "Verifying..." : "Verify & Update Email"}
              </button>

              <button
                className="twofactor-otp-cancel-btn"
                onClick={closeEmailOtpModal}
                disabled={emailOtpVerifying}
              >
                Cancel
              </button>
            </div>
          </div>
        </Portal>
      )}

      {/* =======================
         CONFIRMATION MODAL
      ====================== */}
      {confirmAction && (
        <div className="admin-aura-modal-overlay">
          <div
            className="admin-aura-delete-modal"
            style={{
              border:
                confirmAction.type === "restore"
                  ? "2px solid #36C667"
                  : "2px solid #e74c3c",
            }}
          >
            <h3 className="modal-title">
              {confirmAction.type === "restore"
                ? "Confirm Restore"
                : "Confirm Deactivation"}
            </h3>
            <p className="modal-text">
              {confirmAction.type === "restore"
                ? "This account will be restored to active status."
                : confirmAction.type === "delete"
                  ? "This account will be permanently deleted. This action cannot be undone."
                  : "This account will be deactivated. You can restore it later if needed."}
            </p>

            <div className="modal-actions">
              <button
                className="btn-cancel"
                onClick={() => setConfirmAction(null)}
              >
                Cancel
              </button>
              <button
                className="btn-confirm"
                onClick={handleConfirmAction}
                style={{
                  backgroundColor:
                    confirmAction.type === "restore" ? "#36C667" : "#e74c3c",
                  borderColor:
                    confirmAction.type === "restore" ? "#36C667" : "#e74c3c",
                }}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      {/* =======================
         DEACTIVATION MODALS
      ====================== */}
      
      {/* Step 1: Deactivation Confirmation */}
      {deactivationStep === 1 && deactivationAccount && (
        <div className="admin-aura-modal-overlay">
          <div className="account-details-card" style={{ border: "2px solid #e74c3c" }}>
            <div className="section-header-row">
              <h3 className="section-title">Confirm Deactivation</h3>
              <span className="account-status-tag inactive">
                <span className="status-dot" />
                Deactivating
              </span>
            </div>
            
            <div className="details-section">
              <p style={{ marginBottom: 20 }}>
                Are you sure you want to deactivate this account? This action will prevent the user from accessing the system.
              </p>
              
              <div className="details-row">
                <label>Account ID</label>
                <input value={deactivationAccount.id} disabled />
              </div>
              <div className="details-row">
                <label>Email</label>
                <input value={deactivationAccount.email} disabled />
              </div>
              <div className="details-row">
                <label>Name</label>
                <input value={deactivationAccount.fullName} disabled />
              </div>
            </div>

            <div className="modal-actions">
              <button className="btn-cancel" onClick={cancelDeactivation}>
                Cancel
              </button>
              <button className="btn-confirm" onClick={handleDeactivationConfirm} style={{ backgroundColor: "#e74c3c", borderColor: "#e74c3c" }}>
                Confirm Deactivation
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Step 2: Deactivation Reason */}
      {deactivationStep === 2 && deactivationAccount && (
        <div className="admin-aura-modal-overlay">
          <div className="account-details-card" style={{ border: "2px solid #e74c3c" }}>
            <div className="section-header-row">
              <h3 className="section-title">Deactivation Reason</h3>
              <span className="account-status-tag inactive">
                <span className="status-dot" />
                Deactivating
              </span>
            </div>
            
            <div className="details-section">
              <p style={{ marginBottom: 20 }}>
                Please select a reason for deactivating this account:
              </p>
              
              <div className="details-row">
                <label>Account ID</label>
                <input value={deactivationAccount.id} disabled />
              </div>
              
              <div className="details-row">
                <label>Deactivation Reason *</label>
                <select 
                  value={deactivationReason} 
                  onChange={(e) => setDeactivationReason(e.target.value)}
                  className="cs-aura-input"
                  style={{ width: "100%", padding: "8px 12px" }}
                >
                  <option value="">Select a reason...</option>
                  <option value="suspended">Suspended</option>
                  <option value="temporarily_unavailable">Temporarily Unavailable</option>
                  <option value="offboarding">Offboarding</option>
                </select>
              </div>
              
              {deactivationError && (
                <p style={{ color: "#e74c3c", fontSize: "12px", marginTop: "5px" }}>
                  {deactivationError}
                </p>
              )}
            </div>

            <div className="modal-actions">
              <button className="btn-cancel" onClick={cancelDeactivation}>
                Cancel
              </button>
              <button 
                className="btn-confirm" 
                onClick={() => handleDeactivationReason(deactivationReason)}
                disabled={!deactivationReason}
                style={{ 
                  backgroundColor: deactivationReason ? "#e74c3c" : "#ccc", 
                  borderColor: deactivationReason ? "#e74c3c" : "#ccc" 
                }}
              >
                Next
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Step 3: Admin Password Verification */}
      {deactivationStep === 3 && deactivationAccount && (
        <div className="admin-aura-modal-overlay">
          <div className="account-details-card" style={{ border: "2px solid #e74c3c" }}>
            <div className="section-header-row">
              <h3 className="section-title">Admin Verification Required</h3>
              <span className="account-status-tag inactive">
                <span className="status-dot" />
                Final Step
              </span>
            </div>
            
            <div className="details-section">
              <p style={{ marginBottom: 20 }}>
                For security purposes, please enter your admin password to complete the deactivation:
              </p>
              
              <div className="details-row">
                <label>Account to Deactivate</label>
                <input value={deactivationAccount.email} disabled />
              </div>
              
              <div className="details-row">
                <label>Deactivation Reason</label>
                <input 
                  value={deactivationReason.charAt(0).toUpperCase() + deactivationReason.slice(1).replace(/_/g, ' ')} 
                  disabled 
                />
              </div>
              
              <div className="details-row">
                <label>Admin Password *</label>
                <input 
                  type="password" 
                  value={adminPassword}
                  onChange={(e) => setAdminPassword(e.target.value)}
                  placeholder="Enter your admin password"
                  className="cs-aura-input"
                  style={{ width: "100%", padding: "8px 12px" }}
                  disabled={isDeactivating}
                />
              </div>
              
              {deactivationError && (
                <p style={{ color: "#e74c3c", fontSize: "12px", marginTop: "5px" }}>
                  {deactivationError}
                </p>
              )}
            </div>

            <div className="modal-actions">
              <button className="btn-cancel" onClick={cancelDeactivation} disabled={isDeactivating}>
                Cancel
              </button>
              <button 
                className="btn-confirm" 
                onClick={handleAdminPasswordVerification}
                disabled={isDeactivating || !adminPassword.trim()}
                style={{ 
                  backgroundColor: (isDeactivating || !adminPassword.trim()) ? "#ccc" : "#e74c3c", 
                  borderColor: (isDeactivating || !adminPassword.trim()) ? "#ccc" : "#e74c3c" 
                }}
              >
                {isDeactivating ? "Deactivating..." : "Deactivate Account"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* =======================
         REACTIVATION MODALS
      ====================== */}
      
      {/* Step 1: Reactivation Confirmation */}
      {reactivationStep === 1 && reactivationAccount && (
        <div className="admin-aura-modal-overlay">
          <div className="account-details-card" style={{ border: "2px solid #36C667" }}>
            <div className="section-header-row">
              <h3 className="section-title">Confirm Reactivation</h3>
              <span className="account-status-tag active">
                <span className="status-dot" />
                Reactivating
              </span>
            </div>
            
            <div className="details-section">
              <p style={{ marginBottom: 20 }}>
                Are you sure you want to reactivate this account? This action will restore the user's access to the system.
              </p>
              
              <div className="details-row">
                <label>Account ID</label>
                <input value={reactivationAccount.id} disabled />
              </div>
              <div className="details-row">
                <label>Email</label>
                <input value={reactivationAccount.email} disabled />
              </div>
              <div className="details-row">
                <label>Name</label>
                <input value={reactivationAccount.fullName} disabled />
              </div>
            </div>

            <div className="modal-actions">
              <button className="btn-cancel" onClick={cancelReactivation}>
                Cancel
              </button>
              <button className="btn-confirm" onClick={handleReactivationConfirm} style={{ backgroundColor: "#36C667", borderColor: "#36C667" }}>
                Confirm Reactivation
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Step 2: Reactivation Reason */}
      {reactivationStep === 2 && reactivationAccount && (
        <div className="admin-aura-modal-overlay">
          <div className="account-details-card" style={{ border: "2px solid #36C667" }}>
            <div className="section-header-row">
              <h3 className="section-title">Reactivation Reason</h3>
              <span className="account-status-tag active">
                <span className="status-dot" />
                Reactivating
              </span>
            </div>
            
            <div className="details-section">
              <p style={{ marginBottom: 20 }}>
                Please select a reason for reactivating this account:
              </p>
              
              <div className="details-row">
                <label>Account ID</label>
                <input value={reactivationAccount.id} disabled />
              </div>
              
              <div className="details-row">
                <label>Reactivation Reason *</label>
                <select 
                  value={reactivationReason} 
                  onChange={(e) => setReactivationReason(e.target.value)}
                  className="cs-aura-input"
                  style={{ width: "100%", padding: "8px 12px" }}
                >
                  <option value="">Select a reason...</option>
                  <option value="offboarding_reversal">Offboarding Reversal</option>
                  <option value="suspension_lifted">Suspension Lifted</option>
                  <option value="temporary_access_restored">Temporary Access Restored</option>
                  <option value="account_reinstated">Account Reinstated</option>
                  <option value="compliance_verification_passed">Compliance / Verification Passed</option>
                </select>
              </div>
              
              {reactivationError && (
                <p style={{ color: "#e74c3c", fontSize: "12px", marginTop: "5px" }}>
                  {reactivationError}
                </p>
              )}
            </div>

            <div className="modal-actions">
              <button className="btn-cancel" onClick={cancelReactivation}>
                Cancel
              </button>
              <button 
                className="btn-confirm" 
                onClick={() => handleReactivationReason(reactivationReason)}
                disabled={!reactivationReason}
                style={{ 
                  backgroundColor: reactivationReason ? "#36C667" : "#ccc", 
                  borderColor: reactivationReason ? "#36C667" : "#ccc" 
                }}
              >
                Next
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Step 3: Admin Password Verification */}
      {reactivationStep === 3 && reactivationAccount && (
        <div className="admin-aura-modal-overlay">
          <div className="account-details-card" style={{ border: "2px solid #36C667" }}>
            <div className="section-header-row">
              <h3 className="section-title">Admin Verification Required</h3>
              <span className="account-status-tag active">
                <span className="status-dot" />
                Final Step
              </span>
            </div>
            
            <div className="details-section">
              <p style={{ marginBottom: 20 }}>
                For security purposes, please enter your admin password to complete the reactivation:
              </p>
              
              <div className="details-row">
                <label>Account to Reactivate</label>
                <input value={reactivationAccount.email} disabled />
              </div>
              
              <div className="details-row">
                <label>Reactivation Reason</label>
                <input 
                  value={reactivationReason.charAt(0).toUpperCase() + reactivationReason.slice(1).replace(/_/g, ' ')} 
                  disabled 
                />
              </div>
              
              <div className="details-row">
                <label>Admin Password *</label>
                <input 
                  type="password" 
                  value={reactivationPassword}
                  onChange={(e) => setReactivationPassword(e.target.value)}
                  placeholder="Enter your admin password"
                  className="cs-aura-input"
                  style={{ width: "100%", padding: "8px 12px" }}
                  disabled={isReactivating}
                />
              </div>
              
              {reactivationError && (
                <p style={{ color: "#e74c3c", fontSize: "12px", marginTop: "5px" }}>
                  {reactivationError}
                </p>
              )}
            </div>

            <div className="modal-actions">
              <button className="btn-cancel" onClick={cancelReactivation} disabled={isReactivating}>
                Cancel
              </button>
              <button 
                className="btn-confirm" 
                onClick={handleReactivationPasswordVerification}
                disabled={isReactivating || !reactivationPassword.trim()}
                style={{ 
                  backgroundColor: (isReactivating || !reactivationPassword.trim()) ? "#ccc" : "#36C667", 
                  borderColor: (isReactivating || !reactivationPassword.trim()) ? "#ccc" : "#36C667" 
                }}
              >
                {isReactivating ? "Reactivating..." : "Reactivate Account"}
              </button>
            </div>
          </div>
        </div>
      )}
        </>
      )}

    </div>
  );
}




/* ======================================================
   CREATE STAFF VIEW
====================================================== */
function CreateStaff({ onCancel, onSuccess }) {
  const [roles, setRoles] = useState([]);
  const [form, setForm] = useState({
    fullName: "",
    email: "",
    contact: "",
    licenseNumber: "",
  });

  const [errors, setErrors] = useState({});
  const [creating, setCreating] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  
  const STEPS = [
    "Basic Information",
    "Staff Details",
    "Review & Confirm"
  ];
  
  // Modal states
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const API_BASE =
    process.env.NODE_ENV === "development"
      ? "http://localhost:5030/auravit-bb843/us-central1"
      : "https://us-central1-auravit-bb843.cloudfunctions.net";

  const emailExists = async (email) => {
    const snap = await getDocs(query(collection(db, "users"), where("email", "==", email), limit(1)));
    return !snap.empty;
  };

  const contactExists = async (last10) => {
    const snap = await getDocs(query(collection(db, "users"), where("phoneLast10", "==", last10), limit(1)));
    return !snap.empty;
  };

  const licenseExists = async (licenseNumber) => {
    const snap = await getDocs(
      query(collection(db, "users"), where("medicalLicenseNumber", "==", licenseNumber), limit(1))
    );
    return !snap.empty;
  };

  const toggleRole = (role) => {
    setRoles((prev) => {
      const isAdding = !prev.includes(role);
      const next = isAdding ? [...prev, role] : prev.filter((r) => r !== role);
      if (role === "Medical Professional" && isAdding) {
        setForm((p) => ({ ...p, licenseNumber: "" }));
      }
      
      // Clear role error when at least one role is selected
      if (next.length > 0) {
        setErrors((prev) => ({
          ...prev,
          roles: ""
        }));
      }
      
      return next;
    });
  };

  const isMedicalProfessional = roles.includes("Medical Professional");

  // Regex validations
  const validateFullName = (name) => /^[A-Za-z\s\-']{1,50}$/.test(name);
  const validateFullNameHasTwoParts = (name) => {
    const parts = name.trim().split(/\s+/);
    return parts.length >= 2;
  };
  const validateEmail = (email) => {
    // Simplified email validation that allows common email formats
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email);
  };
  const validateContact = (contact) => /^9\d{9}$/.test(contact);
  const validateLicense = (license) =>
    /^\d{7}$/.test(license);

  // Handle input changes
  const handleChange = (field, value) => {
    // Restrict full name input - allow letters, spaces, hyphens, and apostrophes
    if (field === "fullName") {
      value = value.replace(/[^A-Za-z\s\-']/g, "").slice(0, 50);
    }

    // Restrict email input - disable spaces only, allow @ symbol and max 254 characters
    if (field === "email") {
      value = value.replace(/\s/g, "").slice(0, 254);
    }

    // Restrict contact input
    if (field === "contact") {
      value = value.replace(/[^0-9]/g, "").slice(0, 10);
    }

    // Restrict license input
    if (field === "licenseNumber") {
      value = value.replace(/[^0-9]/g, "").slice(0, 7);
    }

    setForm((prev) => ({ ...prev, [field]: value }));

    // Live validation - clear uniqueness errors when field is changed
    setErrors((prev) => {
      const newErrors = { ...prev };

      // Clear uniqueness errors when user changes the field
      if (field === "email" && newErrors.email === "Email already taken") {
        newErrors.email = "";
      }
      if (field === "contact" && newErrors.contact === "Contact number is already taken") {
        newErrors.contact = "";
      }
      if (field === "licenseNumber" && newErrors.licenseNumber === "License number is already taken") {
        newErrors.licenseNumber = "";
      }

      // Perform normal validation for the field
      newErrors[field] =
        (field === "fullName" && !value)
          ? "Full Name is required"
          : (field === "fullName" && !validateFullName(value))
            ? "Only letters, spaces, hyphens, and apostrophes allowed (max 50 characters)"
            : (field === "email" && !value)
              ? "Email is required"
              : (field === "email" && value && !validateEmail(value))
                ? "Invalid email format"
              : (field === "contact" && !value)
                ? "Contact Number is required"
                : (field === "contact" && !validateContact(value))
                  ? "Must start with 9 and be 10 digits"
                  : (field === "licenseNumber" && isMedicalProfessional && !value)
                    ? "License Number required"
                    : (field === "licenseNumber" && isMedicalProfessional && !validateLicense(value))
                      ? "7 digits only"
                      : "";

      return newErrors;
    });

    // Live validation for role selection
    if (roles.length === 0) {
      setErrors((prev) => ({
        ...prev,
        roles: "At least one role must be selected"
      }));
    } else {
      setErrors((prev) => ({
        ...prev,
        roles: ""
      }));
    }
  };

  const handleCreate = async () => {
    setCreating(true);
    // Trigger validation for all required fields
    const newErrors = {};

    // Validate full name
    if (!form.fullName) {
      newErrors.fullName = "Full Name is required";
    } else if (!validateFullName(form.fullName)) {
      newErrors.fullName = "Only letters, spaces, hyphens, and apostrophes allowed (max 50 characters)";
    } else if (!validateFullNameHasTwoParts(form.fullName)) {
      newErrors.fullName = "Full Name must contain at least two parts (first name and last name)";
    }

    // Validate email
    if (!form.email) {
      newErrors.email = "Email is required";
    } else if (!validateEmail(form.email)) {
      newErrors.email = "Invalid email format";
    }

    // Validate contact
    if (!form.contact) {
      newErrors.contact = "Contact Number is required";
    } else if (!validateContact(form.contact)) {
      newErrors.contact = "Must start with 9 and be 10 digits";
    }

    // Validate license if medical professional
    if (isMedicalProfessional) {
      if (!form.licenseNumber) {
        newErrors.licenseNumber = "License Number required";
      } else if (!validateLicense(form.licenseNumber)) {
        newErrors.licenseNumber = "Must be 7 digits";
      }
    }

    // Validate role selection
    if (roles.length === 0) {
      newErrors.roles = "Select at least one role";
    }

    // Set all errors at once
    setErrors(newErrors);

    // Only proceed if no errors
    if (Object.keys(newErrors).length === 0) {
      setShowPasswordModal(true);
      setCreating(false);
    } else {
      setCreating(false);
    }
  };

  const handlePasswordConfirm = async (adminPassword) => {
    const adminEmail = auth.currentUser?.email || JSON.parse(localStorage.getItem("user") || "{}")?.email;
    if (!adminEmail || !adminPassword) return;
    setCreating(true);
    try {
      const user = auth.currentUser;
      if (!user?.email) throw new Error("Not logged in");

      const cred = EmailAuthProvider.credential(user.email, adminPassword);
      await reauthenticateWithCredential(user, cred);

      // Authentication successful, close modal and proceed with creation
      setShowPasswordModal(false);

      const generateTempPassword = () => {
        const uppercase = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
        const lowercase = "abcdefghijklmnopqrstuvwxyz";
        const numbers = "0123456789";

        // Ensure at least one character from each category
        let password = "";
        password += uppercase[Math.floor(Math.random() * uppercase.length)];
        password += lowercase[Math.floor(Math.random() * lowercase.length)];
        password += numbers[Math.floor(Math.random() * numbers.length)];

        // Fill remaining 9 characters with random mix of letters and numbers
        const allChars = uppercase + lowercase + numbers;
        for (let i = 0; i < 9; i++) {
          password += allChars[Math.floor(Math.random() * allChars.length)];
        }

        // Shuffle the password to randomize character positions
        return password.split('').sort(() => Math.random() - 0.5).join('');
      };

      const fullName = String(form.fullName || "").trim();
const staffEmail = String(form.email || "").trim().toLowerCase();
      const phoneLast10 = String(form.contact || "").replace(/\D/g, "").slice(-10);
      const phone = phoneLast10 ? `+63${phoneLast10}` : "";
      const adminSystemId = JSON.parse(localStorage.getItem("user") || "{}")?.systemId || user?.uid || "Admin";
      const tempPassword = generateTempPassword();
      const staffId = await generateStaffId(); // Generate staff ID on frontend
      const idToken = await user.getIdToken(true);

      const response = await fetch(`${API_BASE}/createStaffAccount`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify({
          email: staffEmail,
          phone,
          phoneLast10,
          fullName,
          roles,
          licenseNumber: roles.includes("Medical Professional") ? String(form.licenseNumber || "").trim() : "",
          adminSystemId,
          tempPassword,
          staffId, // Pass the generated staff ID to backend
        }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload?.error || "Staff creation failed");
      }

      const staffUid = payload?.uid;
      if (!staffUid || !staffId) {
        throw new Error("Failed to save staff data. Please try again.");
      }

      // Explicitly save/ensure data is in Firestore from frontend as a safety measure
      try {
        await setDoc(
          doc(db, "users", staffUid),
          {
            staffId,
            uid: staffUid,
            accountType: "Staff",
            email: staffEmail,
            phone,
            phoneLast10,
            fullName,
            roles,
            role: roles[0] || "",
            medicalLicenseNumber: roles.includes("Medical Professional")
              ? String(form.licenseNumber || "").trim()
              : "",
            accountCreatorSystemId: adminSystemId,
            status: "active",
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
          },
          { merge: true }
        );
      } catch {
        throw new Error("Failed to save staff data. Please try again.");
      }

      // Just refresh the accounts list.
      window.dispatchEvent(new Event("admin-accounts-refresh"));
        setForm({ fullName: "", email: "", contact: "", licenseNumber: "" });
        setRoles([]);
      setCreating(false);
      setShowSuccessModal(true);
    } catch (err) {
      // Authentication failed, keep modal open and set creating to false
      setCreating(false);
      throw err; // Re-throw to be caught by the modal's error handling
    }
  };

  const handleNextStep = async () => {
    if (currentStep === 0) {
      // Validate Basic Information
      const newErrors = {};
      if (!form.fullName) newErrors.fullName = "Full Name is required";
      else if (!validateFullName(form.fullName)) newErrors.fullName = "Only letters, spaces, hyphens, and apostrophes allowed (max 50 characters)";
      else if (!validateFullNameHasTwoParts(form.fullName)) newErrors.fullName = "Full Name must contain at least two parts (first name and last name)";

      if (!form.email) newErrors.email = "Email is required";
      else if (!validateEmail(form.email)) newErrors.email = "Invalid email format";

      if (!form.contact) newErrors.contact = "Contact Number is required";
      else if (!validateContact(form.contact)) newErrors.contact = "Must start with 9 and be 10 digits";

      setErrors(newErrors);

      // Only proceed with uniqueness checks if basic validation passes
      if (Object.keys(newErrors).length === 0) {
        try {
          const staffEmail = String(form.email || "").trim().toLowerCase();
          const phoneLast10 = String(form.contact || "").replace(/\D/g, "").slice(-10);

          const uniquenessErrors = {};

          // Check email existence
          if (await emailExists(staffEmail)) uniquenessErrors.email = "Email already taken";

          // Check contact existence (checks all accounts - staff and user)
          if (phoneLast10 && await contactExists(phoneLast10)) uniquenessErrors.contact = "Contact number is already taken";

          if (Object.keys(uniquenessErrors).length > 0) {
            setErrors((prev) => ({ ...prev, ...uniquenessErrors }));
            return; // Don't proceed to step 2 if uniqueness checks fail
          }

          setCurrentStep(1);
          // Clear uniqueness errors when moving to step 2
          setErrors((prev) => ({
            ...prev,
            email: "",
            contact: ""
          }));
        } catch (err) {
          // If uniqueness check fails due to connectivity, still allow proceeding
          console.error("Uniqueness check failed:", err);
          setCurrentStep(1);
          // Clear uniqueness errors when moving to step 2
          setErrors((prev) => ({
            ...prev,
            email: "",
            contact: ""
          }));
        }
      }
    } else if (currentStep === 1) {
      // Validate Staff Details
      const newErrors = {};
      if (roles.length === 0) newErrors.roles = "Select at least one role";
      if (isMedicalProfessional && !form.licenseNumber) newErrors.licenseNumber = "License Number required";
      else if (isMedicalProfessional && !validateLicense(form.licenseNumber)) newErrors.licenseNumber = "Must be 7 digits";

      setErrors(newErrors);

      // Only proceed with license uniqueness check if basic validation passes
      if (Object.keys(newErrors).length === 0) {
        try {
          const uniquenessErrors = {};

          // Check license number existence if medical professional
          if (isMedicalProfessional) {
            const lic = String(form.licenseNumber || "").trim();
            if (await licenseExists(lic)) uniquenessErrors.licenseNumber = "License number is already taken";
          }

          if (Object.keys(uniquenessErrors).length > 0) {
            setErrors((prev) => ({ ...prev, ...uniquenessErrors }));
            return; // Don't proceed to step 2 if uniqueness checks fail
          }

          setCurrentStep(2);
          // Clear uniqueness errors when moving to step 3
          setErrors((prev) => ({
            ...prev,
            email: "",
            contact: "",
            licenseNumber: ""
          }));
        } catch (err) {
          // If uniqueness check fails due to connectivity, still allow proceeding
          console.error("License uniqueness check failed:", err);
          setCurrentStep(2);
          // Clear uniqueness errors when moving to step 3
          setErrors((prev) => ({
            ...prev,
            email: "",
            contact: "",
            licenseNumber: ""
          }));
        }
      }
    }
  };

  const handlePrevStep = () => {
    if (currentStep > 0) setCurrentStep(currentStep - 1);
  };

  return (
    <React.Fragment>
      <Portal>
      <div className="modal-backdrop" onClick={onCancel}>
        <div 
          style={{ 
            maxWidth: '680px', 
            width: '94%', 
            maxHeight: '85vh', 
            borderRadius: '24px', 
            overflow: 'hidden', 
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.2)', 
            position: 'relative',
            background: '#ffffff'
          }}
          onClick={e => e.stopPropagation()}
        >
          <button 
            type="button" 
            onClick={onCancel} 
            style={{ 
              position: 'absolute',
              top: '14px',
              right: '14px',
              zIndex: 20,
              width: '38px', 
              height: '38px',
              borderRadius: '50%',
              background: 'rgba(255,255,255,0.18)',
              border: 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              color: 'white',
              fontSize: '22px',
              lineHeight: 1,
              transition: 'all 0.2s ease',
              backdropFilter: 'blur(6px)'
            }}
            onMouseEnter={(e) => e.target.style.background = 'rgba(255,255,255,0.32)'}
            onMouseLeave={(e) => e.target.style.background = 'rgba(255,255,255,0.18)'}
          >
            <FiX />
          </button>

          <div style={{ 
            background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 50%, #1e40af 100%)',
            padding: '24px 24px 18px 24px',
            color: 'white',
            width: '100%',
            boxSizing: 'border-box'
          }}>
            <div style={{ marginBottom: '16px' }}>
              <h2 style={{ margin: 0, fontSize: '21px', fontWeight: 700, letterSpacing: '-0.3px' }}>Create Staff Account</h2>
              <p style={{ margin: '6px 0 0 0', fontSize: '14px', opacity: 0.92, fontWeight: 400 }}>Add a new staff member to the system</p>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div style={{ 
                background: 'rgba(255,255,255,0.2)', 
                padding: '6px 14px', 
                borderRadius: '9999px',
                fontSize: '12.5px',
                fontWeight: 600,
                letterSpacing: '0.02em'
              }}>
                Step {currentStep + 1} of {STEPS.length}
              </div>
              <div style={{ fontSize: '14px', fontWeight: 600, opacity: 0.98 }}>
                {STEPS[currentStep]}
              </div>
            </div>
          </div>

          <div style={{ padding: '16px 24px 0 24px' }}>
            <div style={{ display: 'flex', gap: '5px', height: '5px' }}>
              {STEPS.map((step, idx) => (
                <div 
                  key={idx} 
                  style={{
                    flex: 1,
                    height: '100%',
                    borderRadius: '9999px',
                    backgroundColor: idx <= currentStep ? '#3b82f6' : '#e2e8f0',
                    transition: 'background-color 0.3s ease'
                  }}
                />
              ))}
            </div>
          </div>

          <div style={{ 
            padding: '18px 24px 20px 24px', 
            overflowY: 'auto', 
            maxHeight: '52vh'
          }}>
            {/* Step 1: Basic Information */}
            {currentStep === 0 && (
              <div>
                <h3 style={{ fontSize: '18px', fontWeight: 700, color: '#0f172a', margin: '0 0 18px 0' }}>Basic Information</h3>
                
                <div style={{ marginBottom: '18px' }}>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, color: '#334155', marginBottom: '10px' }}>Full Name</label>
                  <input
                    style={{
                      width: '100%',
                      padding: '14px 16px',
                      borderRadius: '14px',
                      border: errors.fullName ? '2px solid #dc2626' : '2px solid #e2e8f0',
                      fontSize: '14px',
                      outline: 'none',
                      transition: 'all 0.2s ease',
                      boxSizing: 'border-box'
                    }}
                    placeholder="Enter full name"
                    value={form.fullName}
                    onChange={(e) => handleChange("fullName", e.target.value)}
                    onFocus={(e) => {
                      e.target.style.borderColor = errors.fullName ? '#dc2626' : '#3b82f6';
                      e.target.style.boxShadow = errors.fullName ? '0 0 0 4px rgba(220, 38, 38, 0.1)' : '0 0 0 4px rgba(59, 130, 246, 0.1)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = errors.fullName ? '#dc2626' : '#e2e8f0';
                      e.target.style.boxShadow = 'none';
                    }}
                  />
                  {errors.fullName && (
                    <p style={{ 
                      fontSize: '13px', 
                      color: '#dc2626', 
                      marginTop: '6px', 
                      fontWeight: '500',
                      marginBottom: '0'
                    }}>
                      {errors.fullName}
                    </p>
                  )}
                </div>

                <div style={{ marginBottom: '18px' }}>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, color: '#334155', marginBottom: '10px' }}>Email Address</label>
                  <input
                    type="email"
                    name="email"
                    value={form.email}
                    onChange={(e) => handleChange("email", e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === ' ') {
                        e.preventDefault();
                      }
                    }}
                    placeholder="Enter email address"
                    style={{
                      width: '100%',
                      padding: '14px 16px',
                      borderRadius: '14px',
                      border: errors.email ? '2px solid #dc2626' : '2px solid #e2e8f0',
                      fontSize: '14px',
                      outline: 'none',
                      transition: 'all 0.2s ease',
                      boxSizing: 'border-box'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = errors.email ? '#dc2626' : '#3b82f6';
                      e.target.style.boxShadow = errors.email ? '0 0 0 4px rgba(220, 38, 38, 0.1)' : '0 0 0 4px rgba(59, 130, 246, 0.1)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = errors.email ? '#dc2626' : '#e2e8f0';
                      e.target.style.boxShadow = 'none';
                    }}
                  />
                  {errors.email && (
                    <p style={{ 
                      fontSize: '13px', 
                      color: '#dc2626', 
                      marginTop: '6px', 
                      fontWeight: '500',
                      marginBottom: '0'
                    }}>
                      {errors.email}
                    </p>
                  )}
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, color: '#334155', marginBottom: '10px' }}>Contact Number</label>
                  <div style={{ 
                    display: 'flex', 
                    gap: '8px',
                    width: '100%'
                  }}>
                    <span style={{ 
                      background: '#f1f5f9', 
                      padding: '14px 16px', 
                      borderRadius: '14px', 
                      border: '2px solid #e2e8f0',
                      fontWeight: '600',
                      color: '#475569'
                    }}>+63</span>
                    <input
                      type="text"
                      name="contact"
                      value={form.contact}
                      onChange={(e) => handleChange("contact", e.target.value)}
                      placeholder="9123456789"
                      style={{
                        flex: '1',
                        padding: '14px 16px',
                        borderRadius: '14px',
                        border: errors.contact ? '2px solid #dc2626' : '2px solid #e2e8f0',
                        fontSize: '14px',
                        outline: 'none',
                        transition: 'all 0.2s ease',
                        boxSizing: 'border-box'
                      }}
                      onFocus={(e) => {
                        e.target.style.borderColor = errors.contact ? '#dc2626' : '#3b82f6';
                        e.target.style.boxShadow = errors.contact ? '0 0 0 4px rgba(220, 38, 38, 0.1)' : '0 0 0 4px rgba(59, 130, 246, 0.1)';
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = errors.contact ? '#dc2626' : '#e2e8f0';
                        e.target.style.boxShadow = 'none';
                      }}
                    />
                  </div>
                  {errors.contact && (
                    <p style={{ 
                      fontSize: '13px', 
                      color: '#dc2626', 
                      marginTop: '6px', 
                      fontWeight: '500',
                      marginBottom: '0'
                    }}>
                      {errors.contact}
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Step 2: Staff Details */}
            {currentStep === 1 && (
              <div>
                <h3 style={{ fontSize: '18px', fontWeight: 700, color: '#0f172a', margin: '0 0 18px 0' }}>Staff Details</h3>
                
                <div style={{ marginBottom: '20px' }}>
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center', 
                    marginBottom: '10px' 
                  }}>
                    <span style={{ fontSize: '14px', fontWeight: '700', color: '#334155' }}>
                      Role
                    </span>
                    <small style={{ fontSize: '12px', color: '#94a3b8' }}>Select one or more</small>
                  </div>

                  <div style={{ 
                    display: 'flex', 
                    flexWrap: 'wrap', 
                    gap: '10px',
                    marginBottom: '10px'
                  }}>
                    {["Content Curator", "Peer Educator", "Medical Professional"].map(
                      (role) => (
                        <label
                          key={role}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px',
                            padding: '12px 16px',
                            borderRadius: '14px',
                            border: roles.includes(role) ? '2px solid #3b82f6' : '2px solid #e2e8f0',
                            background: roles.includes(role) ? 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)' : '#ffffff',
                            cursor: 'pointer',
                            transition: 'all 0.15s ease',
                            fontSize: '14px',
                            fontWeight: '600',
                            color: roles.includes(role) ? '#1e40af' : '#334155'
                          }}
                          onMouseEnter={(e) => {
                            if (!roles.includes(role)) {
                              e.currentTarget.style.background = '#f8fafc';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (!roles.includes(role)) {
                              e.currentTarget.style.background = '#ffffff';
                            }
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={roles.includes(role)}
                            onChange={() => toggleRole(role)}
                            style={{ 
                              width: '18px', 
                              height: '18px',
                              accentColor: '#3b82f6',
                              margin: 0
                            }}
                          />
                          {role}
                        </label>
                      )
                    )}
                  </div>
                  {errors.roles && (
                    <p style={{ 
                      fontSize: '13px', 
                      color: '#dc2626', 
                      marginTop: '6px', 
                      fontWeight: '500',
                      marginBottom: '0'
                    }}>
                      {errors.roles}
                    </p>
                  )}
                </div>

                {isMedicalProfessional && (
                  <div>
                    <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, color: '#334155', marginBottom: '10px' }}>License Number</label>
                    <input
                      placeholder="Enter 7-digit license number"
                      value={form.licenseNumber}
                      onChange={(e) => handleChange("licenseNumber", e.target.value)}
                      style={{
                        width: '100%',
                        padding: '14px 16px',
                        borderRadius: '14px',
                        border: errors.licenseNumber ? '2px solid #dc2626' : '2px solid #e2e8f0',
                        fontSize: '14px',
                        outline: 'none',
                        transition: 'all 0.2s ease',
                        boxSizing: 'border-box'
                      }}
                      onFocus={(e) => {
                        e.target.style.borderColor = errors.licenseNumber ? '#dc2626' : '#3b82f6';
                        e.target.style.boxShadow = errors.licenseNumber ? '0 0 0 4px rgba(220, 38, 38, 0.1)' : '0 0 0 4px rgba(59, 130, 246, 0.1)';
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = errors.licenseNumber ? '#dc2626' : '#e2e8f0';
                        e.target.style.boxShadow = 'none';
                      }}
                    />
                    {errors.licenseNumber && (
                      <p style={{ 
                        fontSize: '13px', 
                        color: '#dc2626', 
                        marginTop: '6px', 
                        fontWeight: '500',
                        marginBottom: '0'
                      }}>
                        {errors.licenseNumber}
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Step 3: Review & Confirm */}
            {currentStep === 2 && (
              <div>
                <h3 style={{ fontSize: '18px', fontWeight: 700, color: '#0f172a', margin: '0 0 18px 0' }}>Review & Confirm</h3>
                
                <div style={{ marginBottom: '16px' }}>
                  <h4 style={{ fontSize: '14px', fontWeight: '700', color: '#475569', margin: '0 0 10px 0' }}>Basic Information</h4>
                  <div style={{ 
                    background: '#f8fafc', 
                    borderRadius: '12px', 
                    padding: '16px',
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: '12px'
                  }}>
                    <div>
                      <div style={{ fontSize: '12px', fontWeight: '600', color: '#94a3b8', marginBottom: '4px' }}>Full Name</div>
                      <div style={{ fontSize: '14px', fontWeight: '600', color: '#1e293b' }}>{form.fullName}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: '12px', fontWeight: '600', color: '#94a3b8', marginBottom: '4px' }}>Email</div>
                      <div style={{ fontSize: '14px', fontWeight: '600', color: '#1e293b' }}>{form.email}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: '12px', fontWeight: '600', color: '#94a3b8', marginBottom: '4px' }}>Contact Number</div>
                      <div style={{ fontSize: '14px', fontWeight: '600', color: '#1e293b' }}>+63 {form.contact}</div>
                    </div>
                  </div>
                </div>

                <div style={{ marginBottom: '16px' }}>
                  <h4 style={{ fontSize: '14px', fontWeight: '700', color: '#475569', margin: '0 0 10px 0' }}>Staff Details</h4>
                  <div style={{ 
                    background: '#f8fafc', 
                    borderRadius: '12px', 
                    padding: '16px'
                  }}>
                    <div style={{ marginBottom: isMedicalProfessional ? '12px' : '0' }}>
                      <div style={{ fontSize: '12px', fontWeight: '600', color: '#94a3b8', marginBottom: '4px' }}>Roles</div>
                      <div style={{ fontSize: '14px', fontWeight: '600', color: '#1e293b' }}>{roles.join(', ')}</div>
                    </div>
                    {isMedicalProfessional && (
                      <div>
                        <div style={{ fontSize: '12px', fontWeight: '600', color: '#94a3b8', marginBottom: '4px' }}>License Number</div>
                        <div style={{ fontSize: '14px', fontWeight: '600', color: '#1e293b' }}>{form.licenseNumber}</div>
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <h4 style={{ fontSize: '14px', fontWeight: '700', color: '#475569', margin: '0 0 10px 0' }}>System</h4>
                  <div style={{ 
                    background: '#f8fafc', 
                    borderRadius: '12px', 
                    padding: '16px'
                  }}>
                    <div style={{ fontSize: '12px', fontWeight: '600', color: '#94a3b8', marginBottom: '4px' }}>Account Creator</div>
                    <div style={{ fontSize: '14px', fontWeight: '600', color: '#1e293b' }}>Admin-001</div>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div style={{ padding: '16px 24px 24px 24px', display: 'flex', gap: '12px', justifyContent: currentStep === 0 ? 'flex-end' : 'space-between' }}>
            {currentStep > 0 && (
              <button 
                onClick={handlePrevStep}
                style={{
                  padding: '14px 32px',
                  borderRadius: '16px',
                  border: 'none',
                  background: '#f1f5f9',
                  color: '#475569',
                  fontSize: '15px',
                  fontWeight: '700',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = '#e2e8f0';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = '#f1f5f9';
                }}
              >
                Back
              </button>
            )}
            
            <div style={{ display: 'flex', gap: '12px', marginLeft: currentStep === 0 ? 'auto' : '0' }}>
              {currentStep < 2 ? (
                <button 
                  onClick={handleNextStep}
                  disabled={
                    currentStep === 0 
                      ? !!(errors.fullName || errors.email || errors.contact || !form.fullName || !form.email || !form.contact)
                      : !!(errors.roles || (isMedicalProfessional && (errors.licenseNumber || !form.licenseNumber)))
                  }
                  style={{
                    padding: '14px 32px',
                    borderRadius: '16px',
                    border: 'none',
                    background:
                      currentStep === 0
                        ? (errors.fullName || errors.email || errors.contact || !form.fullName || !form.email || !form.contact)
                          ? '#94a3b8'
                          : '#f1f5f9'
                        : (errors.roles || (isMedicalProfessional && (errors.licenseNumber || !form.licenseNumber)))
                          ? '#94a3b8'
                          : '#f1f5f9',
                    color:
                      currentStep === 0
                        ? (errors.fullName || errors.email || errors.contact || !form.fullName || !form.email || !form.contact)
                          ? '#ffffff'
                          : '#475569'
                        : (errors.roles || (isMedicalProfessional && (errors.licenseNumber || !form.licenseNumber)))
                          ? '#ffffff'
                          : '#475569',
                    fontSize: '15px',
                    fontWeight: '700',
                    cursor:
                      currentStep === 0
                        ? (errors.fullName || errors.email || errors.contact || !form.fullName || !form.email || !form.contact)
                          ? 'not-allowed'
                          : 'pointer'
                        : (errors.roles || (isMedicalProfessional && (errors.licenseNumber || !form.licenseNumber)))
                          ? 'not-allowed'
                          : 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    const isDisabled = currentStep === 0
                      ? !!(errors.fullName || errors.email || errors.contact || !form.fullName || !form.email || !form.contact)
                      : !!(errors.roles || (isMedicalProfessional && (errors.licenseNumber || !form.licenseNumber)));
                    if (!isDisabled) {
                      e.target.style.background = 'linear-gradient(135deg, #3b82f6 0%, #1e40af 100%)';
                      e.target.style.color = '#ffffff';
                    }
                  }}
                  onMouseLeave={(e) => {
                    const isDisabled = currentStep === 0
                      ? !!(errors.fullName || errors.email || errors.contact || !form.fullName || !form.email || !form.contact)
                      : !!(errors.roles || (isMedicalProfessional && (errors.licenseNumber || !form.licenseNumber)));
                    if (!isDisabled) {
                      e.target.style.background = '#f1f5f9';
                      e.target.style.color = '#475569';
                    }
                  }}
                >
                  Next
                </button>
              ) : (
                <button
                  disabled={creating}
                  onClick={handleCreate}
                  style={{
                    padding: '14px 32px',
                    borderRadius: '16px',
                    border: 'none',
                    background: creating
                      ? '#94a3b8'
                      : '#f1f5f9',
                    color: creating ? '#ffffff' : '#475569',
                    fontSize: '15px',
                    fontWeight: '700',
                    cursor: creating ? 'not-allowed' : 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    if (!creating) {
                      e.target.style.background = 'linear-gradient(135deg, #3b82f6 0%, #1e40af 100%)';
                      e.target.style.color = '#ffffff';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!creating) {
                      e.target.style.background = '#f1f5f9';
                      e.target.style.color = '#475569';
                    }
                  }}
                >
                  {creating ? (
                    <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <FiRefreshCw className="spinning" />
                      Creating...
                    </span>
                  ) : (
                    "Create Account"
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
      </Portal>

      {/* Modals */}
      <ConfirmPasswordModal
        isOpen={showPasswordModal}
        onClose={() => setShowPasswordModal(false)}
        onConfirm={handlePasswordConfirm}
        title="Confirm Admin Password"
      />
      <StaffCreatedSuccessModal
        isOpen={showSuccessModal}
        onViewAccounts={() => {
          setShowSuccessModal(false);
          if (onSuccess) onSuccess();
        }}
      />
    </React.Fragment>
  );
}